import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { 
  Send, User, Sparkles, Loader2, RefreshCw, ArrowLeft,
  Menu, X, Trash2, Upload, FileText, Settings, Lock,
  Youtube, Link as LinkIcon, MessageCircle, Globe, Activity, ThumbsUp, ThumbsDown, CreditCard, CheckCircle, Copy, Check, Mic, HardDrive, Users, Wifi, LogOut, ChevronUp, ChevronDown, Database, UserCircle, Heart, Target, Star, Download
} from "lucide-react";
import "./App.css";

// Use same-origin API calls to avoid cross-domain issues on iOS Safari (ITP blocks third-party cookies)
// This ensures API calls work on custom domains (ask-dr-nawwaf.com) and Emergent hosts alike
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${window.location.origin}/api`;
const DR_NAWWAF_IMG = "/dr-nawwaf-new.png";

// --- Memory fallback for localStorage (for private browsing/restricted environments) ---
const memoryStore = {};
const safeStorage = {
    getItem: (key) => {
        try { return localStorage.getItem(key); } catch(e) { return memoryStore[key] || null; }
    },
    setItem: (key, value) => {
        try { localStorage.setItem(key, value); } catch(e) { memoryStore[key] = value; }
    },
    removeItem: (key) => {
        try { localStorage.removeItem(key); } catch(e) { delete memoryStore[key]; }
    }
};

// --- In-app browser detection ---
const detectBrowserEnvironment = () => {
    const ua = navigator.userAgent || '';
    const uaLower = ua.toLowerCase();
    
    const isInAppBrowser = /fban|fbav|instagram|line\/|twitter|snapchat|wv\)|webview|fb_iab|musical_ly|bytedance/i.test(ua);
    const isMobile = /mobile|iphone|android|ipod|phone|bb10|blackberry|opera mini|opera mobi|iemobile|windows phone/i.test(ua) && !/ipad|tablet/i.test(ua);
    const isTablet = /ipad|tablet|kindle|silk|playbook|nexus 7|nexus 10|sm-t|gt-p/i.test(ua);
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    const isAndroid = /android/i.test(ua);
    const isSafari = /safari/i.test(ua) && !/chrome|crios|fxios|edgios/i.test(ua);
    const isPrivateBrowsing = (() => {
        try { localStorage.setItem('__test__', '1'); localStorage.removeItem('__test__'); return false; } catch(e) { return true; }
    })();
    
    let browserName = 'Unknown';
    if (isInAppBrowser) {
        if (/fban|fbav|fb_iab/i.test(ua)) browserName = 'Facebook In-App';
        else if (/instagram/i.test(ua)) browserName = 'Instagram In-App';
        else if (/line\//i.test(ua)) browserName = 'LINE In-App';
        else if (/twitter/i.test(ua)) browserName = 'Twitter In-App';
        else browserName = 'In-App Browser';
    } else if (/crios/i.test(ua)) browserName = 'Chrome iOS';
    else if (/samsungbrowser/i.test(ua)) browserName = 'Samsung Browser';
    else if (isSafari) browserName = 'Safari';
    else if (/chrome/i.test(ua)) browserName = 'Chrome';
    else if (/firefox|fxios/i.test(ua)) browserName = 'Firefox';
    else if (/edg/i.test(ua)) browserName = 'Edge';
    
    return { isInAppBrowser, isMobile, isTablet, isIOS, isAndroid, isSafari, isPrivateBrowsing, browserName, userAgent: ua };
};

// Configure axios to include credentials (cookies)
axios.defaults.withCredentials = true;

// Axios request interceptor: automatically attach Bearer token to ALL requests
axios.interceptors.request.use((config) => {
    const token = safeStorage.getItem('auth_session_token');
    if (token) {
        config.headers = config.headers || {};
        if (!config.headers['Authorization']) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
    }
    return config;
});

// Axios response interceptor: handle expired sessions globally
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        // If any API call gets 401 and it's an auth endpoint, clear stale token
        if (error.response?.status === 401 && error.config?.url?.includes('/auth/google/me')) {
            safeStorage.removeItem('auth_session_token');
        }
        return Promise.reject(error);
    }
);

// --- Translations ---
const translations = {
    ar: {
        welcomeTitle: "اسأل الدكتور نواف الشبلي",
        welcomeDesc: "مساحتك الآمنة للحوار الفكري والروحاني.",
        startChat: "ابدأ المحادثة الآن",
        signInToChat: "سجّل دخولك للبدء",
        signInWithGoogle: "تسجيل الدخول بحساب Google",
        signOut: "تسجيل الخروج",
        aiLabel: "ذكاء اصطناعي",
        available247: "متاح 24/7",
        privacy: "خصوصية تامة",
        chatInputPlaceholder: "اكتب رسالتك هنا...",
        newChat: "محادثة جديدة",
        prevChats: "المحادثات السابقة",
        noChats: "لا توجد محادثات سابقة",
        typing: "يكتب الآن...",
        error: "عذراً يا بني، حدث خطأ تقني بسيط. هل يمكننا المحاولة مرة أخرى؟",
        adminLogin: "لوحة الإدارة",
        adminDesc: "أدخل رمز الدخول للمتابعة",
        uploadFile: "رفع مصدر جديد",
        importYoutube: "استيراد من يوتيوب",
        fileTab: "ملف (PDF/Word/Excel)",
        youtubeTab: "يوتيوب",
        you: "أنت",
        drNawwaf: "د. نواف",
        adminBtn: "إدارة",
        subtitles: "د. نواف الشبلي",
        role: "مرشدك الفكري والروحاني",
        feedbackPrompt: "ما رأيك في هذه الإجابة؟",
        feedbackComment: "أضف تعليقاً (اختياري)",
        submit: "إرسال",
        thanks: "شكراً لك!",
        pricing: "الاشتراكات",
        free: "مجاني",
        basic: "أساسي",
        pro: "احترافي",
        month: "شهر",
        questionsUsed: "الأسئلة المستخدمة",
        questionsRemaining: "الأسئلة المتبقية",
        unlimited: "غير محدود",
        limitReached: "لقد استنفدت أسئلتك المجانية",
        upgradeToContinue: "يرجى الترقية للمتابعة",
        subscribe: "اشترك الآن",
        currentPlan: "الخطة الحالية",
        features: {
            basic_support: "دعم أساسي",
            free_limit: "الأسئلة لا تنتهي",
            basic_limit: "١٠٠ سؤال شهرياً",
            full_access: "وصول كامل للمحتوى",
            priority_support: "دعم ذو أولوية",
            unlimited_queries: "أسئلة غير محدودة",
            early_access: "وصول مبكر للمحتوى الجديد"
        },
        paymentSuccess: "تم الدفع بنجاح!",
        paymentProcessing: "جاري معالجة الدفع...",
        paymentFailed: "فشل الدفع",
        copy: "نسخ",
        copied: "تم النسخ",
        // Legal & Footer translations
        termsOfService: "شروط الخدمة",
        privacyPolicy: "سياسة الخصوصية",
        disclaimer: "إخلاء المسؤولية",
        aboutUs: "من نحن",
        contactUs: "تواصل معنا",
        secureCheckout: "دفع آمن ومشفر عبر Stripe",
        allRightsReserved: "جميع الحقوق محفوظة",
        backToHome: "العودة للرئيسية"
    },
    en: {
        welcomeTitle: "Ask Dr. Nawwaf Al-Shibli",
        welcomeDesc: "Your safe space for intellectual and spiritual dialogue. Ask your questions and benefit from Dr. Nawwaf's wisdom.",
        startChat: "Start Chatting Now",
        signInToChat: "Sign in to start",
        signInWithGoogle: "Sign in with Google",
        signOut: "Sign Out",
        aiLabel: "AI Powered",
        available247: "Available 24/7",
        privacy: "Total Privacy",
        chatInputPlaceholder: "Type your message here...",
        newChat: "New Chat",
        prevChats: "Previous Chats",
        noChats: "No previous chats",
        typing: "Typing...",
        error: "My apologies, a small technical error occurred. Shall we try again?",
        adminLogin: "Admin Panel",
        adminDesc: "Enter access code to continue",
        uploadFile: "Upload New Source",
        importYoutube: "Import from YouTube",
        fileTab: "File (PDF/Word/Excel)",
        youtubeTab: "YouTube",
        you: "You",
        drNawwaf: "Dr. Nawwaf",
        adminBtn: "Admin",
        subtitles: "Dr. Nawwaf Al-Shibli",
        role: "Your Intellectual & Spiritual Mentor",
        feedbackPrompt: "How was this response?",
        feedbackComment: "Add a comment (optional)",
        submit: "Submit",
        thanks: "Thank you!",
        pricing: "Pricing",
        free: "Free",
        basic: "Basic",
        pro: "Pro",
        month: "month",
        subscribe: "Subscribe",
        currentPlan: "Current Plan",
        questionsUsed: "Questions Used",
        questionsRemaining: "Questions Remaining",
        unlimited: "Unlimited",
        limitReached: "You've used all your free questions",
        upgradeToContinue: "Please upgrade to continue",
        features: {
            basic_support: "Basic Support",
            free_limit: "5 Questions",
            basic_limit: "25 Questions",
            pro_limit: "50 Questions",
            full_access: "Full Content Access",
            priority_support: "Priority Support",
            unlimited_queries: "Unlimited Questions",
            early_access: "Early Access to New Content",
            vip_support: "VIP Support"
        },
        paymentSuccess: "Payment Successful!",
        paymentProcessing: "Processing Payment...",
        paymentFailed: "Payment Failed",
        copy: "Copy",
        copied: "Copied",
        // Legal & Footer translations
        termsOfService: "Terms of Service",
        privacyPolicy: "Privacy Policy",
        disclaimer: "Disclaimer",
        aboutUs: "About Us",
        contactUs: "Contact Us",
        secureCheckout: "Secure & Encrypted Payment via Stripe",
        allRightsReserved: "All Rights Reserved",
        backToHome: "Back to Home"
    },
    fr: {
        welcomeTitle: "Demandez au Dr. Nawwaf Al-Shibli",
        welcomeDesc: "Votre espace sûr pour le dialogue intellectuel et spirituel. Posez vos questions et profitez de la sagesse du Dr. Nawwaf.",
        startChat: "Commencer la discussion",
        aiLabel: "IA Alimentée",
        available247: "Disponible 24/7",
        privacy: "Confidentialité totale",
        chatInputPlaceholder: "Tapez votre message ici...",
        newChat: "Nouvelle discussion",
        prevChats: "Discussions précédentes",
        noChats: "Aucune discussion précédente",
        typing: "En train d'écrire...",
        error: "Toutes mes excuses, une petite erreur technique est survenue. On réessaie ?",
        adminLogin: "Panneau d'administration",
        adminDesc: "Entrez le code d'accès pour continuer",
        uploadFile: "Télécharger une source",
        importYoutube: "Importer depuis YouTube",
        fileTab: "Fichier (PDF/Word/Excel)",
        youtubeTab: "YouTube",
        you: "Vous",
        drNawwaf: "Dr. Nawwaf",
        adminBtn: "Admin",
        subtitles: "Dr. Nawwaf Al-Shibli",
        role: "Votre Mentor Intellectuel et Spirituel",
        feedbackPrompt: "Comment était cette réponse ?",
        feedbackComment: "Ajouter un commentaire (facultatif)",
        submit: "Envoyer",
        thanks: "Merci !",
        pricing: "Tarification",
        free: "Gratuit",
        basic: "Basique",
        pro: "Pro",
        month: "mois",
        subscribe: "S'abonner",
        currentPlan: "Plan Actuel",
        features: {
            basic_support: "Support de base",
            free_limit: "50 requêtes/mois seulement",
            basic_limit: "100 requêtes/mois",
            full_access: "Accès complet",
            priority_support: "Support prioritaire",
            unlimited_queries: "Requêtes illimitées",
            early_access: "Accès anticipé"
        },
        paymentSuccess: "Paiement réussi !",
        paymentProcessing: "Traitement du paiement...",
        paymentFailed: "Échec du paiement",
        copy: "Copier",
        copied: "Copié",
        // Legal & Footer translations
        termsOfService: "Conditions d'utilisation",
        privacyPolicy: "Politique de confidentialité",
        disclaimer: "Avertissement",
        aboutUs: "À propos de nous",
        contactUs: "Contactez-nous",
        secureCheckout: "Paiement sécurisé et crypté via Stripe",
        allRightsReserved: "Tous droits réservés",
        backToHome: "Retour à l'accueil"
    }
};

// --- Context ---
const LanguageContext = createContext();

const LanguageProvider = ({ children }) => {
    const [lang, setLang] = useState(safeStorage.getItem('app_lang') || 'ar');
    
    const changeLang = (newLang) => {
        setLang(newLang);
        safeStorage.setItem('app_lang', newLang);
    };

    const t = (key) => translations[lang][key] || translations['ar'][key];
    const dir = lang === 'ar' ? 'rtl' : 'ltr';

    return (
        <LanguageContext.Provider value={{ lang, changeLang, t, dir, translations }}>
            <div dir={dir} className={lang === 'ar' ? 'font-arabic' : 'font-sans'}>
                {children}
            </div>
        </LanguageContext.Provider>
    );
};

const useLanguage = () => useContext(LanguageContext);


// --- Profile Modal Component ---
const ProfileModal = ({ isOpen, onClose, user, onSave, isOnboarding = false }) => {
    const { lang, t } = useLanguage();
    const isRTL = lang === 'ar';
    const [form, setForm] = useState({
        display_name: '',
        gender: '',
        about_me: '',
        goals: '',
        dr_nawwaf_meaning: ''
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen && user?.profile) {
            setForm({
                display_name: user.profile.display_name || user.name || '',
                gender: user.profile.gender || '',
                about_me: user.profile.about_me || '',
                goals: user.profile.goals || '',
                dr_nawwaf_meaning: user.profile.dr_nawwaf_meaning || ''
            });
        } else if (isOpen && user) {
            setForm(f => ({ ...f, display_name: f.display_name || user.name || '' }));
        }
    }, [isOpen, user]);

    const handleSave = async () => {
        if (!form.display_name.trim() || !form.gender) return;
        setSaving(true);
        try {
            await axios.put(`${API_URL}/user/profile`, form, { withCredentials: true });
            if (onSave) onSave(form);
            onClose();
        } catch (e) {
            console.error("Profile save error:", e);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    const labels = {
        title: isOnboarding 
            ? (lang === 'ar' ? 'مرحباً بك! دعنا نتعرف عليك' : 'Welcome! Let us get to know you')
            : (lang === 'ar' ? 'ملفك الشخصي' : 'Your Profile'),
        subtitle: isOnboarding
            ? (lang === 'ar' ? 'حتى يتمكن د. نواف من مناداتك باسمك وتذكّر رحلتك' : 'So Dr. Nawwaf can call you by name and remember your journey')
            : (lang === 'ar' ? 'عدّل معلوماتك الشخصية' : 'Edit your personal information'),
        name: lang === 'ar' ? 'ما الاسم الذي تحب أن أناديك به؟' : 'What name would you like me to call you?',
        namePlaceholder: lang === 'ar' ? 'اسمك المفضل...' : 'Your preferred name...',
        about: lang === 'ar' ? 'حدثني عن نفسك' : 'Tell me about yourself',
        aboutPlaceholder: lang === 'ar' ? 'شاركني ما تحب أن أعرفه عنك... خلفيتك، اهتماماتك، ما يشغل بالك...' : 'Share what you would like me to know about you... your background, interests, what\'s on your mind...',
        goals: lang === 'ar' ? 'ما هدفك من استخدام التطبيق؟' : 'What is your goal with this app?',
        goalsPlaceholder: lang === 'ar' ? 'ما الذي تبحث عنه في هذه الرحلة الروحانية؟...' : 'What are you seeking in this spiritual journey?...',
        meaning: lang === 'ar' ? 'ماذا يعني لك د. نواف؟' : 'What does Dr. Nawwaf mean to you?',
        meaningPlaceholder: lang === 'ar' ? 'كيف أثّر د. نواف في حياتك أو وعيك؟...' : 'How has Dr. Nawwaf impacted your life or consciousness?...',
        save: lang === 'ar' ? 'حفظ' : 'Save',
        skip: lang === 'ar' ? 'لاحقاً' : 'Later',
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="profile-modal">
            <div className={`bg-[#1a2332] border border-[#d4a574]/30 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                {/* Header */}
                <div className="p-5 border-b border-[#d4a574]/20">
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#d4a574] to-[#b8956a] flex items-center justify-center">
                            <Heart size={20} className="text-[#0f172a]" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-[#d4a574]">{labels.title}</h2>
                            <p className="text-[#94a3b8] text-xs">{labels.subtitle}</p>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <div className="p-5 space-y-4">
                    {/* Display Name */}
                    <div>
                        <label className="block text-[#d4a574] text-sm font-medium mb-1.5">{labels.name}</label>
                        <input
                            data-testid="profile-display-name"
                            type="text"
                            value={form.display_name}
                            onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                            placeholder={labels.namePlaceholder}
                            className="w-full px-3 py-2.5 bg-[#0f172a] border border-[#334155] rounded-xl text-white placeholder-[#64748b] focus:border-[#d4a574] focus:outline-none text-sm"
                            maxLength={100}
                        />
                    </div>

                    {/* Gender */}
                    <div>
                        <label className="block text-[#d4a574] text-sm font-medium mb-2">
                            {lang === 'ar' ? 'الجنس' : 'Gender'} <span className="text-red-400">*</span>
                        </label>
                        <div className="flex gap-3">
                            <label 
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl cursor-pointer border transition-all ${form.gender === 'male' ? 'bg-[#d4a574]/20 border-[#d4a574] text-[#d4a574]' : 'bg-[#0f172a] border-[#334155] text-[#94a3b8] hover:border-[#64748b]'}`}
                                data-testid="profile-gender-male"
                            >
                                <input
                                    type="radio"
                                    name="gender"
                                    value="male"
                                    checked={form.gender === 'male'}
                                    onChange={() => setForm(f => ({ ...f, gender: 'male' }))}
                                    className="sr-only"
                                />
                                <span className="text-sm font-medium">{lang === 'ar' ? 'ذكر' : 'Male'}</span>
                            </label>
                            <label 
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl cursor-pointer border transition-all ${form.gender === 'female' ? 'bg-[#d4a574]/20 border-[#d4a574] text-[#d4a574]' : 'bg-[#0f172a] border-[#334155] text-[#94a3b8] hover:border-[#64748b]'}`}
                                data-testid="profile-gender-female"
                            >
                                <input
                                    type="radio"
                                    name="gender"
                                    value="female"
                                    checked={form.gender === 'female'}
                                    onChange={() => setForm(f => ({ ...f, gender: 'female' }))}
                                    className="sr-only"
                                />
                                <span className="text-sm font-medium">{lang === 'ar' ? 'أنثى' : 'Female'}</span>
                            </label>
                        </div>
                        {!form.gender && saving === false && (
                            <p className="text-red-400 text-xs mt-1">{lang === 'ar' ? 'يرجى اختيار الجنس' : 'Please select your gender'}</p>
                        )}
                    </div>

                    {/* About Me */}
                    <div>
                        <label className="block text-[#d4a574] text-sm font-medium mb-1.5">{labels.about}</label>
                        <textarea
                            data-testid="profile-about-me"
                            value={form.about_me}
                            onChange={e => setForm(f => ({ ...f, about_me: e.target.value }))}
                            placeholder={labels.aboutPlaceholder}
                            className="w-full px-3 py-2.5 bg-[#0f172a] border border-[#334155] rounded-xl text-white placeholder-[#64748b] focus:border-[#d4a574] focus:outline-none text-sm resize-none"
                            rows={3}
                            maxLength={1000}
                        />
                    </div>

                    {/* Goals */}
                    <div>
                        <label className="block text-[#d4a574] text-sm font-medium mb-1.5">{labels.goals}</label>
                        <textarea
                            data-testid="profile-goals"
                            value={form.goals}
                            onChange={e => setForm(f => ({ ...f, goals: e.target.value }))}
                            placeholder={labels.goalsPlaceholder}
                            className="w-full px-3 py-2.5 bg-[#0f172a] border border-[#334155] rounded-xl text-white placeholder-[#64748b] focus:border-[#d4a574] focus:outline-none text-sm resize-none"
                            rows={2}
                            maxLength={500}
                        />
                    </div>

                    {/* Dr. Nawwaf Meaning */}
                    <div>
                        <label className="block text-[#d4a574] text-sm font-medium mb-1.5">{labels.meaning}</label>
                        <textarea
                            data-testid="profile-dr-nawwaf-meaning"
                            value={form.dr_nawwaf_meaning}
                            onChange={e => setForm(f => ({ ...f, dr_nawwaf_meaning: e.target.value }))}
                            placeholder={labels.meaningPlaceholder}
                            className="w-full px-3 py-2.5 bg-[#0f172a] border border-[#334155] rounded-xl text-white placeholder-[#64748b] focus:border-[#d4a574] focus:outline-none text-sm resize-none"
                            rows={2}
                            maxLength={500}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="p-5 border-t border-[#d4a574]/20 flex gap-3 justify-end">
                    {isOnboarding && (
                        <button onClick={onClose} className="px-5 py-2 text-[#94a3b8] hover:text-white text-sm transition-colors" data-testid="profile-skip-btn">
                            {labels.skip}
                        </button>
                    )}
                    {!isOnboarding && (
                        <button onClick={onClose} className="px-5 py-2 text-[#94a3b8] hover:text-white text-sm transition-colors" data-testid="profile-cancel-btn">
                            {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                        </button>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={saving || !form.display_name.trim() || !form.gender}
                        className="px-6 py-2 bg-gradient-to-r from-[#d4a574] to-[#b8956a] text-[#0f172a] rounded-xl font-bold text-sm hover:from-[#e2b687] hover:to-[#c9a67b] transition-all disabled:opacity-50"
                        data-testid="profile-save-btn"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : labels.save}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Auth Context ---
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sessionExpired, setSessionExpired] = useState(false);

    const checkAuth = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/auth/google/me`, {
                withCredentials: true
            });
            setUser(response.data);
            setSessionExpired(false);
        } catch (error) {
            setUser(null);
            // Only clear token on explicit 401 (invalid/expired session)
            if (error.response && error.response.status === 401) {
                const hadToken = !!safeStorage.getItem('auth_session_token');
                safeStorage.removeItem('auth_session_token');
                // If user HAD a token but it expired, flag it so we show a message
                if (hadToken) {
                    setSessionExpired(true);
                }
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Auto-refresh user state every 2 minutes to catch admin changes, gifts, payment updates
    useEffect(() => {
        if (!user) return;
        const interval = setInterval(async () => {
            try {
                const response = await axios.get(`${API_URL}/auth/google/me`, {
                    withCredentials: true,
                    timeout: 5000
                });
                setUser(response.data);
            } catch (error) {
                // Only clear on 401 — ignore transient errors
                if (error.response && error.response.status === 401) {
                    safeStorage.removeItem('auth_session_token');
                    setUser(null);
                }
            }
        }, 120000); // 2 minutes
        return () => clearInterval(interval);
    }, [user]);

    // Refresh user state when the tab becomes visible again (user comes back to the tab)
    useEffect(() => {
        const handleVisibility = async () => {
            if (document.visibilityState === 'visible' && safeStorage.getItem('auth_session_token')) {
                try {
                    const response = await axios.get(`${API_URL}/auth/google/me`, {
                        withCredentials: true,
                        timeout: 5000
                    });
                    setUser(response.data);
                } catch (error) {
                    if (error.response && error.response.status === 401) {
                        safeStorage.removeItem('auth_session_token');
                        setUser(null);
                    }
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, []);

    const signInWithGoogle = () => {
        const env = detectBrowserEnvironment();
        
        // MOBILE FIX: Warn users in in-app browsers BEFORE redirecting to OAuth
        if (env.isInAppBrowser) {
            const currentUrl = window.location.origin;
            const shouldContinue = window.confirm(
                'يبدو أنك تستخدم متصفح داخلي (' + env.browserName + '). ' +
                'لتسجيل الدخول بنجاح، يُفضل فتح الرابط في Safari أو Chrome.\n\n' +
                'هل تريد المتابعة على أي حال؟\n\n' +
                'It looks like you\'re using an in-app browser. For best results, open the link in Safari or Chrome.\n\nContinue anyway?'
            );
            if (!shouldContinue) {
                // Copy link to clipboard
                navigator.clipboard?.writeText(currentUrl).then(() => {
                    alert('تم نسخ الرابط! الصقه في Safari أو Chrome\nLink copied! Paste it in Safari or Chrome');
                }).catch(() => {
                    alert('افتح هذا الرابط في Safari أو Chrome:\n' + currentUrl);
                });
                return;
            }
        }
        
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const signOut = async () => {
        try {
            await axios.post(`${API_URL}/auth/google/logout`, {}, { 
                withCredentials: true
            });
        } catch (error) {
            console.error("Logout error:", error);
        }
        // Clear localStorage token
        safeStorage.removeItem('auth_session_token');
        setUser(null);
    };

    const refreshUser = async () => {
        // Small delay to ensure cookie is properly set
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Try to refresh with retry
        for (let i = 0; i < 3; i++) {
            try {
                const response = await axios.get(`${API_URL}/auth/google/me`, {
                    withCredentials: true,
                    timeout: 10000
                });
                setUser(response.data);
                return;
            } catch (error) {
                if (i < 2) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
        }
        // If all retries fail, call checkAuth as fallback
        await checkAuth();
    };

    return (
        <AuthContext.Provider value={{ user, loading, signInWithGoogle, signOut, refreshUser, sessionExpired, setSessionExpired }}>
            {children}
        </AuthContext.Provider>
    );
};

const useAuth = () => useContext(AuthContext);

// --- Auth Callback Component (Mobile-Optimized) ---
const AuthCallback = () => {
    const navigate = useNavigate();
    const hasProcessed = useRef(false);
    const { refreshUser } = useAuth();
    const [error, setError] = useState(null);
    const [status, setStatus] = useState('processing');
    const [browserEnv] = useState(() => detectBrowserEnvironment());

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const processAuth = async () => {
            const hash = window.location.hash;
            const search = window.location.search;
            const fullUrl = window.location.href;
            
            // Check for error in URL params
            const urlParams = new URLSearchParams(search);
            const errorParam = urlParams.get('error');
            const errorDesc = urlParams.get('error_description');
            
            if (errorParam) {
                console.error("OAuth error:", errorParam, errorDesc);
                setError(errorDesc || errorParam);
                setStatus('error');
                setTimeout(() => navigate('/', { replace: true }), 5000);
                return;
            }
            
            // MOBILE FIX: Extract session_id from multiple possible locations
            // Some mobile browsers put it in hash, others in query params, others lose the hash entirely
            let sessionId = null;
            
            // Method 1: Check hash fragment (#session_id=XXX)
            const hashMatch = hash.match(/session_id=([^&]+)/);
            if (hashMatch) sessionId = hashMatch[1];
            
            // Method 2: Check query parameters (?session_id=XXX)
            if (!sessionId) sessionId = urlParams.get('session_id');
            
            // Method 3: Check full URL for session_id anywhere (some mobile browsers mangle the URL)
            if (!sessionId) {
                const fullMatch = fullUrl.match(/session_id=([^&#\s]+)/);
                if (fullMatch) sessionId = fullMatch[1];
            }
            
            // Method 4: Check if session_id was saved before redirect (for in-app browsers)
            if (!sessionId) {
                try {
                    sessionId = sessionStorage.getItem('pending_session_id');
                    sessionStorage.removeItem('pending_session_id');
                } catch(e) {}
            }
            
            // Method 5: Check safeStorage (memory fallback)
            if (!sessionId) {
                sessionId = safeStorage.getItem('pending_session_id');
                safeStorage.removeItem('pending_session_id');
            }
            
            if (sessionId) {
                setStatus('authenticating');
                
                // More retries for mobile (slower connections, flaky networks)
                const maxRetries = browserEnv.isMobile || browserEnv.isTablet ? 5 : 4;
                let retries = maxRetries;
                let success = false;
                
                while (retries > 0 && !success) {
                    try {
                        const response = await axios.post(`${API_URL}/auth/google/session`, 
                            { session_id: sessionId },
                            { 
                                withCredentials: true,
                                timeout: 25000  // Longer timeout for mobile
                            }
                        );
                        
                        // Store session token (primary auth method, especially critical for mobile)
                        if (response.data && response.data.session_token) {
                            safeStorage.setItem('auth_session_token', response.data.session_token);
                        }
                        
                        // Check if this is a new user (show welcome popup)
                        if (response.data && response.data.is_new_user) {
                            safeStorage.setItem('show_welcome_popup', 'true');
                        }
                        
                        // Wait for cookie to be set (longer on mobile)
                        const cookieDelay = browserEnv.isMobile || browserEnv.isTablet ? 1200 : 800;
                        await new Promise(resolve => setTimeout(resolve, cookieDelay));
                        
                        await refreshUser();
                        success = true;
                        
                        // Route directly based on user's plan
                        const plan = response.data?.subscription_plan;
                        if (plan && plan !== 'none') {
                            navigate('/chat', { replace: true });
                        } else {
                            navigate('/pricing', { replace: true });
                        }
                    } catch (error) {
                        console.error(`Auth attempt failed (${retries} retries left):`, error);
                        retries--;
                        
                        if (retries > 0) {
                            // Longer delay between retries on mobile
                            const retryDelay = browserEnv.isMobile ? 2000 : 1500;
                            await new Promise(resolve => setTimeout(resolve, retryDelay));
                        }
                    }
                }
                
                if (!success) {
                    try {
                        await refreshUser();
                        // Check if refreshUser actually got the user (cookie might have worked)
                        navigate('/', { replace: true });
                    } catch (e) {
                        setError("auth_failed");
                        setStatus('error');
                    }
                }
            } else {
                // No session_id found — show helpful error
                console.error("No session_id found in URL:", fullUrl);
                setError("session_lost");
                setStatus('error');
            }
        };

        processAuth();
    }, [navigate, refreshUser, browserEnv]);

    // Error screen with mobile-specific guidance
    if (error) {
        const isInApp = browserEnv.isInAppBrowser;
        const isMobileDevice = browserEnv.isMobile || browserEnv.isTablet;
        const currentUrl = window.location.origin;
        
        return (
            <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center gap-4 p-4" dir="auto">
                <div className="bg-[#1e293b] border border-[#d4a574]/30 rounded-2xl p-6 max-w-md w-full text-center shadow-2xl">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <X className="text-red-400" size={32} />
                    </div>
                    
                    <h2 className="text-white text-xl font-bold mb-3">
                        {error === 'session_lost' ? 'فشل تسجيل الدخول' : 'خطأ في المصادقة'}
                    </h2>
                    <h3 className="text-[#94a3b8] text-sm mb-4">
                        {error === 'session_lost' ? 'Login Failed' : 'Authentication Error'}
                    </h3>
                    
                    {/* In-app browser warning */}
                    {isInApp && (
                        <div className="bg-amber-500/20 border border-amber-500/40 rounded-xl p-4 mb-4 text-right" data-testid="in-app-browser-warning">
                            <p className="text-amber-300 text-sm font-bold mb-2">
                                يبدو أنك تستخدم متصفح داخلي ({browserEnv.browserName})
                            </p>
                            <p className="text-amber-200/80 text-xs mb-3">
                                المتصفحات الداخلية (واتساب، إنستغرام، فيسبوك) لا تدعم تسجيل الدخول بشكل كامل.
                            </p>
                            <p className="text-white text-sm font-bold">
                                الحل: افتح الرابط في Safari أو Chrome
                            </p>
                            <div className="mt-3 flex flex-col gap-2">
                                <button
                                    onClick={() => {
                                        // Try to open in external browser
                                        const url = currentUrl;
                                        // For iOS, try Safari
                                        if (browserEnv.isIOS) {
                                            window.location.href = `x-safari-${url}`;
                                            setTimeout(() => window.open(url, '_system'), 500);
                                        } else {
                                            // For Android, try intent
                                            window.open(url, '_system');
                                        }
                                    }}
                                    className="w-full py-2.5 bg-gradient-to-r from-[#d4a574] to-[#b8956a] text-[#0f172a] rounded-xl font-bold text-sm"
                                    data-testid="open-in-browser-btn"
                                >
                                    فتح في المتصفح الخارجي
                                </button>
                                <button
                                    onClick={() => {
                                        navigator.clipboard?.writeText(currentUrl).then(() => {
                                            alert('تم نسخ الرابط! الصقه في Safari أو Chrome');
                                        }).catch(() => {});
                                    }}
                                    className="w-full py-2.5 bg-[#334155] text-white rounded-xl text-sm"
                                    data-testid="copy-link-btn"
                                >
                                    نسخ الرابط
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {/* General mobile guidance */}
                    {!isInApp && isMobileDevice && (
                        <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-4 mb-4 text-right">
                            <p className="text-blue-300 text-sm mb-2">نصائح لحل المشكلة:</p>
                            <ul className="text-blue-200/80 text-xs space-y-1.5 list-disc list-inside">
                                <li>تأكد أنك تستخدم Safari (iOS) أو Chrome (Android)</li>
                                <li>أغلق وضع التصفح الخفي (Private Browsing)</li>
                                <li>حاول مسح الكاش والكوكيز ثم أعد المحاولة</li>
                                <li>تأكد من تفعيل الكوكيز في إعدادات المتصفح</li>
                            </ul>
                        </div>
                    )}
                    
                    {/* Desktop error */}
                    {!isMobileDevice && error !== 'session_lost' && (
                        <p className="text-red-300 text-sm mb-4">{error}</p>
                    )}
                    
                    {/* Debug info for admin */}
                    <details className="text-left mt-3 mb-4">
                        <summary className="text-[#64748b] text-xs cursor-pointer hover:text-[#94a3b8]">
                            Debug Info (for admin)
                        </summary>
                        <div className="bg-[#0f172a] rounded-lg p-3 mt-2 text-xs text-[#64748b] space-y-1 break-all" data-testid="auth-debug-info">
                            <p>Browser: {browserEnv.browserName}</p>
                            <p>Device: {browserEnv.isMobile ? 'Mobile' : browserEnv.isTablet ? 'Tablet' : 'Desktop'}</p>
                            <p>OS: {browserEnv.isIOS ? 'iOS' : browserEnv.isAndroid ? 'Android' : 'Other'}</p>
                            <p>In-App: {browserEnv.isInAppBrowser ? 'Yes' : 'No'}</p>
                            <p>Private: {browserEnv.isPrivateBrowsing ? 'Yes' : 'No'}</p>
                            <p>URL Hash: {window.location.hash ? 'Present' : 'Missing'}</p>
                            <p>URL: {window.location.href.substring(0, 100)}</p>
                        </div>
                    </details>
                    
                    <div className="flex flex-col gap-2">
                        <button
                            onClick={() => navigate('/', { replace: true })}
                            className="w-full py-3 bg-gradient-to-r from-[#d4a574] to-[#b8956a] text-[#0f172a] rounded-xl font-bold text-sm hover:from-[#e2b687] hover:to-[#c9a67b] transition-all"
                            data-testid="retry-login-btn"
                        >
                            حاول مرة أخرى / Try Again
                        </button>
                        <a
                            href="https://wa.me/963988242654?text=مشكلة+في+تسجيل+الدخول+على+الموبايل"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full py-2.5 bg-[#25D366]/20 text-[#25D366] rounded-xl text-sm text-center border border-[#25D366]/30"
                        >
                            تواصل مع الدعم عبر واتساب
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center gap-3 p-4">
            <Loader2 className="animate-spin text-[#d4a574]" size={48} />
            <p className="text-[#94a3b8] text-sm">
                {status === 'authenticating' ? 'جاري تسجيل الدخول...' : 'جاري المعالجة...'}
            </p>
            {(browserEnv.isMobile || browserEnv.isTablet) && (
                <p className="text-[#64748b] text-xs">يرجى عدم إغلاق هذه الصفحة</p>
            )}
        </div>
    );
};

// --- Components ---

const PaymentModal = ({ isOpen, onClose, plan, onStripe, user }) => {
    const [loading, setLoading] = useState(false);
    const { lang } = useLanguage();

    if (!isOpen || !plan) return null;

    const handleStripe = async () => {
        setLoading(true);
        try {
            const currentSessionId = safeStorage.getItem("chat_session_id");
            const res = await axios.post(`${API_URL}/payments/create-session`, {
                plan_id: plan.id,
                origin_url: window.location.origin,
                chat_session_id: currentSessionId
            }, { withCredentials: true });
            if (res.data.url) {
                window.location.href = res.data.url;
            }
        } catch (e) {
            setLoading(false);
            if (e.response?.status === 401) {
                alert(lang === 'ar' ? 'انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.' : 'Session expired. Please sign in again.');
            } else {
                alert(lang === 'ar' ? 'حدث خطأ. يرجى المحاولة مرة أخرى.' : 'An error occurred. Please try again.');
            }
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-gradient-to-b from-[#1e293b] to-[#0f172a] rounded-2xl w-full max-w-sm p-6 shadow-xl relative border border-[#334155]">
                <button onClick={onClose} className="absolute right-4 top-4 text-[#94a3b8] hover:text-white"><X size={20} /></button>
                
                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#d4a574] to-[#b8956a] rounded-full flex items-center justify-center mx-auto mb-4">
                        <CreditCard className="text-[#0f172a]" size={32} />
                    </div>
                    <h3 className="font-bold text-xl text-white mb-1">
                        {lang === 'ar' ? 'اشترك في' : 'Subscribe to'} {plan.name}
                    </h3>
                    <p className="text-3xl font-bold text-[#d4a574]">${plan.price}</p>
                    <p className="text-sm text-[#94a3b8] mt-1">
                        {lang === 'ar' ? `${plan.questions} سؤال` : `${plan.questions} questions`}
                    </p>
                </div>
                
                <div className="space-y-3">
                    <button 
                        onClick={handleStripe}
                        disabled={loading}
                        className="w-full py-4 bg-gradient-to-r from-[#635bff] to-[#7a73ff] text-white rounded-xl font-bold hover:from-[#7a73ff] hover:to-[#8f89ff] transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 hover:scale-[1.02] active:scale-[0.98]"
                        data-testid="stripe-pay-btn"
                    >
                        {loading ? (
                            <Loader2 className="animate-spin" size={20} />
                        ) : (
                            <>
                                <CreditCard size={20} />
                                {lang === 'ar' ? 'الدفع الآن' : 'Pay Now'}
                            </>
                        )}
                    </button>
                    
                    <div className="flex items-center justify-center gap-2 text-[#94a3b8] text-xs mt-4">
                        <Lock size={14} className="text-[#22c55e]" />
                        <span>{lang === 'ar' ? 'دفع آمن ومشفر' : 'Secure & encrypted payment'}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const PricingPage = () => {
    const { t, lang } = useLanguage();
    const { user, signInWithGoogle, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState("idle"); // idle, processing, success, failed
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);

    useEffect(() => {
        // Stripe return
        const sessionId = searchParams.get("session_id");
        if (sessionId && searchParams.get("success") === "true") {
            setStatus("processing");
            pollPaymentStatus(sessionId);
        }
        // PayPal return — capture the order
        const paypalSuccess = searchParams.get("paypal_success");
        const paypalToken = searchParams.get("token"); // PayPal passes token= (order ID)
        const paypalPlanId = searchParams.get("plan_id");
        if (paypalSuccess === "true" && paypalToken && paypalPlanId) {
            setStatus("processing");
            capturePayPalOrder(paypalToken, paypalPlanId);
        }
    }, [searchParams]);

    const capturePayPalOrder = async (orderId, planId) => {
        try {
            const res = await axios.post(`${API_URL}/payments/paypal/capture-order`, {
                order_id: orderId,
                plan_id: planId
            }, { withCredentials: true });
            if (res.data.status === 'success' || res.data.status === 'already_credited') {
                setStatus("success");
                await refreshUser();
            } else {
                setStatus("failed");
            }
        } catch (e) {
            console.error("PayPal capture failed", e);
            setStatus("failed");
        }
    };

    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        if (attempts > 5) {
            setStatus("success");
            await refreshUser();
            return;
        }
        try {
            const res = await axios.get(`${API_URL}/payments/status/${sessionId}`, { withCredentials: true });
            if (res.data.status === 'paid' || res.data.status === 'complete') {
                setStatus("success");
                await refreshUser();
            } else {
                setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
            }
        } catch (e) {
            console.error("Poll failed", e);
            setStatus("success");
            await refreshUser();
        }
    };

    const handleSubscribe = (plan) => {
        if (!user) {
            signInWithGoogle();
            return;
        }
        setSelectedPlan(plan);
        setPaymentModalOpen(true);
    };

    const plans = [
        { id: "basic", name: lang === 'ar' ? 'أساسي' : 'Basic', price: "10", questions: 10, features: [
            lang === 'ar' ? 'وصول كامل للمحتوى' : 'Full Content Access',
            lang === 'ar' ? '10 أسئلة' : '10 Questions'
        ]},
        { id: "pro", name: lang === 'ar' ? 'احترافي' : 'Pro', price: "20", questions: 20, features: [
            lang === 'ar' ? 'وصول كامل للمحتوى' : 'Full Content Access',
            lang === 'ar' ? 'دعم أولوية' : 'Priority Support',
            lang === 'ar' ? '20 سؤال' : '20 Questions'
        ]},
        { id: "ultimate", name: lang === 'ar' ? 'متقدم' : 'Ultimate', price: "50", questions: 50, features: [
            lang === 'ar' ? '50 سؤال' : '50 Questions',
            lang === 'ar' ? 'وصول مبكر للميزات' : 'Early Access',
            lang === 'ar' ? 'دعم أولوية' : 'Priority Support',
            lang === 'ar' ? 'دعم VIP' : 'VIP Support'
        ]},
        { id: "premium", name: lang === 'ar' ? 'بريميوم' : 'Premium', price: "100", questions: 100, features: [
            lang === 'ar' ? '100 سؤال' : '100 Questions',
            lang === 'ar' ? 'أفضل قيمة' : 'Best Value',
            lang === 'ar' ? 'دعم VIP حصري' : 'Exclusive VIP Support',
            lang === 'ar' ? 'أولوية قصوى' : 'Top Priority'
        ]}
    ];

    if (status === "success") {
        // Auto-redirect to chat after 3 seconds
        setTimeout(() => navigate('/chat'), 3000);
        return (
            <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6 text-center">
                <CheckCircle size={64} className="text-emerald-500 mb-4" />
                <h2 className="text-3xl font-bold text-white mb-2">{t("paymentSuccess")}</h2>
                <p className="text-[#94a3b8] mb-8">{t("welcomeDesc")}</p>
                <button data-testid="start-chat-after-payment" onClick={() => navigate('/chat')} className="px-8 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700">
                    {t("startChat")}
                </button>
                <p className="text-[#64748b] text-sm mt-4">{lang === 'ar' ? 'سيتم توجيهك تلقائياً...' : 'Redirecting automatically...'}</p>
            </div>
        );
    }

    if (status === "processing") {
        return (
            <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6 text-center">
                <Loader2 size={64} className="text-emerald-500 animate-spin mb-4" />
                <h2 className="text-2xl font-bold text-white">{t("paymentProcessing")}</h2>
                <p className="text-[#94a3b8] mt-4">{lang === 'ar' ? 'يرجى الانتظار...' : 'Please wait...'}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#0f172a] to-[#1e293b] flex flex-col">
            <header className="flex justify-between items-center p-6 md:px-12">
                <div className="text-2xl font-bold text-[#d4a574] cursor-pointer" onClick={() => navigate('/')}>
                    {lang === 'ar' ? 'د. نواف' : 'Dr. Nawwaf'}
                </div>
                <div className="flex items-center gap-4">
                    {user && (
                        <div className="flex items-center gap-2 bg-[#1e293b] rounded-full px-3 py-1 border border-[#334155]">
                            {user.picture && <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />}
                            <span className="text-xs text-[#94a3b8]">{user.name}</span>
                            <span className="text-xs text-[#d4a574] font-bold uppercase">{user.subscription_plan}</span>
                        </div>
                    )}
                    <button onClick={() => navigate('/')} className="p-2 text-[#94a3b8] hover:text-white"><X size={24} /></button>
                </div>
            </header>

            <main className="flex-1 max-w-6xl mx-auto w-full p-6 pb-20">
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-white mb-4">
                        {lang === 'ar' ? 'خطط الاشتراك' : 'Subscription Plans'}
                    </h1>
                    <p className="text-[#94a3b8]">
                        {lang === 'ar' ? 'اختر الخطة التي تناسب رحلتك' : 'Choose the plan that fits your journey'}
                    </p>
                    {user && user.subscription_plan && user.subscription_plan !== 'none' && (
                        <div className="mt-4 text-sm text-[#64748b]">
                            {lang === 'ar' ? 'خطتك الحالية:' : 'Your plan:'} 
                            <span className="text-[#d4a574] font-bold ml-1">
                                {user.subscription_plan === 'gift' ? '🎁 Gift' : user.subscription_plan}
                            </span>
                        </div>
                    )}
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {plans.map((plan) => {
                        const isCurrentPlan = user?.subscription_plan === plan.id;
                        const isPopular = plan.id === 'pro';
                        const isUltimate = plan.id === 'ultimate';
                        const isPremium = plan.id === 'premium';
                        return (
                            <div key={plan.id} className={`rounded-2xl p-6 shadow-lg border-2 ${
                                isPremium
                                    ? 'bg-gradient-to-b from-[#b45309] to-[#78350f] border-[#fbbf24] relative overflow-hidden'
                                    : isUltimate
                                        ? 'bg-gradient-to-b from-[#7c3aed] to-[#4c1d95] border-[#d4a574] relative overflow-hidden'
                                        : isPopular 
                                            ? 'bg-gradient-to-b from-[#1e40af] to-[#1e3a5f] border-[#d4a574] relative overflow-hidden' 
                                            : 'bg-[#1e293b] border-[#334155]'
                            }`}>
                                {isPopular && (
                                    <div className="absolute top-0 right-0 bg-[#d4a574] text-[#0f172a] text-xs font-bold px-3 py-1 rounded-bl-xl">
                                        {lang === 'ar' ? 'الأكثر شعبية' : 'POPULAR'}
                                    </div>
                                )}
                                {isUltimate && (
                                    <div className="absolute top-0 right-0 bg-[#fbbf24] text-[#0f172a] text-xs font-bold px-3 py-1 rounded-bl-xl">
                                        {lang === 'ar' ? 'الأفضل قيمة' : 'BEST VALUE'}
                                    </div>
                                )}
                                {isPremium && (
                                    <div className="absolute top-0 right-0 bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-[#0f172a] text-xs font-bold px-3 py-1 rounded-bl-xl">
                                        {lang === 'ar' ? '⭐ VIP' : '⭐ VIP'}
                                    </div>
                                )}
                                <h3 className="text-lg font-bold text-white mb-2">{plan.name}</h3>
                                <div className="flex items-baseline gap-1 mb-2">
                                    <span className={`text-3xl font-bold ${isPremium ? 'text-[#fbbf24]' : 'text-[#d4a574]'}`}>${plan.price}</span>
                                </div>
                                <p className="text-sm text-[#94a3b8] mb-4">
                                    {plan.questions === -1 
                                        ? (lang === 'ar' ? 'أسئلة غير محدودة' : 'Unlimited questions')
                                        : (lang === 'ar' ? `${plan.questions} سؤال` : `${plan.questions} questions`)
                                    }
                                </p>
                                
                                <ul className="space-y-2 mb-6">
                                    {plan.features.map((f, idx) => (
                                        <li key={idx} className="flex items-center gap-2 text-xs text-[#e2e8f0]">
                                            <CheckCircle size={14} className={`${isPremium ? 'text-[#fbbf24]' : 'text-[#d4a574]'} shrink-0`} />
                                            {f}
                                        </li>
                                    ))}
                                </ul>

                                <button 
                                    onClick={() => handleSubscribe(plan)}
                                    disabled={isCurrentPlan}
                                    className={`w-full py-2.5 rounded-xl font-bold text-sm transition-colors ${
                                        isCurrentPlan 
                                            ? 'bg-[#334155] text-[#64748b] cursor-default' 
                                            : isPremium
                                                ? 'bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-[#0f172a] hover:from-[#fcd34d] hover:to-[#fbbf24]'
                                                : isUltimate
                                                    ? 'bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-[#0f172a] hover:from-[#fcd34d] hover:to-[#fbbf24]'
                                                    : 'bg-gradient-to-r from-[#d4a574] to-[#b8956a] text-[#0f172a] hover:from-[#e2b687] hover:to-[#c9a67b]'
                                    }`}
                                >
                                    {isCurrentPlan 
                                        ? (lang === 'ar' ? 'خطتك الحالية' : 'Current')
                                        : (lang === 'ar' ? 'اشترك' : 'Subscribe')
                                    }
                                </button>
                            </div>
                        );
                    })}
                </div>
                
                {/* Mission Message */}
                <div className="mt-10 max-w-2xl mx-auto" dir="rtl">
                    <div className="bg-gradient-to-br from-[#1e3a5f]/60 to-[#0f172a] rounded-2xl p-6 border border-[#d4a574]/30">
                        <div className="text-center">
                            <p className="text-[#d4a574] text-xl font-bold leading-relaxed">
                                اشتراكك مساهمة في نشر مناهج تعليم القيم الإنسانية
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Secure Checkout & Legal Info */}
                <div className="mt-12 text-center space-y-4">
                    {/* Currency Note */}
                    <p className="text-[#94a3b8] text-sm">
                        {lang === 'ar' 
                            ? 'جميع الأسعار معروضة بالدولار الأمريكي (USD)' 
                            : 'All prices are displayed in US Dollars (USD)'}
                    </p>
                    
                    {/* Secure Payment Badge */}
                    <div className="flex justify-center items-center gap-2">
                        <Lock size={16} className="text-[#22c55e]" />
                        <span className="text-[#64748b] text-sm">
                            {lang === 'ar' ? 'دفع آمن ومشفر عبر Stripe' : 'Secure & Encrypted Payment via Stripe'}
                        </span>
                    </div>
                    
                    {/* No Refund Policy Notice */}
                    <div className="bg-[#1e293b]/50 border border-[#334155] rounded-xl p-4 max-w-md mx-auto">
                        <p className="text-[#94a3b8] text-xs">
                            {lang === 'ar' 
                                ? 'ملاحظة: جميع المدفوعات نهائية وغير قابلة للاسترداد نظراً لطبيعة المنتجات الرقمية.' 
                                : 'Note: All payments are final and non-refundable due to the digital nature of our products.'}
                        </p>
                        <button 
                            onClick={() => navigate('/terms')}
                            className="text-[#d4a574] text-xs hover:underline mt-2"
                        >
                            {lang === 'ar' ? 'قراءة شروط الخدمة' : 'Read Terms of Service'}
                        </button>
                    </div>
                    
                    {/* Legal Links */}
                    <div className="flex flex-wrap justify-center gap-4 text-xs">
                        <button onClick={() => navigate('/terms')} className="text-[#64748b] hover:text-[#d4a574]">
                            {lang === 'ar' ? 'شروط الخدمة' : 'Terms'}
                        </button>
                        <span className="text-[#334155]">•</span>
                        <button onClick={() => navigate('/privacy')} className="text-[#64748b] hover:text-[#d4a574]">
                            {lang === 'ar' ? 'الخصوصية' : 'Privacy'}
                        </button>
                        <span className="text-[#334155]">•</span>
                        <button onClick={() => navigate('/disclaimer')} className="text-[#64748b] hover:text-[#d4a574]">
                            {lang === 'ar' ? 'إخلاء المسؤولية' : 'Disclaimer'}
                        </button>
                    </div>
                </div>
            </main>

            <PaymentModal 
                isOpen={paymentModalOpen}
                onClose={() => setPaymentModalOpen(false)}
                plan={selectedPlan}
                user={user}
            />
        </div>
    );
};

const FeedbackModal = ({ isOpen, onClose, onSubmit }) => {
    const { t } = useLanguage();
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await onSubmit(comment);
            setComment("");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-bold text-lg mb-4 text-stone-800">{t('feedbackPrompt')}</h3>
                <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t('feedbackComment')}
                    className="w-full p-3 border border-stone-200 rounded-xl mb-4 h-24 resize-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-stone-800 bg-white placeholder-stone-400"
                />
                <div className="flex gap-2">
                    <button 
                        onClick={onClose}
                        className="flex-1 py-3 text-stone-500 hover:bg-stone-100 rounded-lg transition-colors"
                        type="button"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="flex-1 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-bold disabled:opacity-50 active:bg-emerald-800"
                        type="button"
                        data-testid="feedback-submit-btn"
                    >
                        {submitting ? '...' : t('submit')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const LimitModal = ({ isOpen, onClose, message }) => {
    const navigate = useNavigate();
    const { t } = useLanguage();

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl text-center transform scale-100 transition-transform">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock size={32} className="text-red-600" />
                </div>
                <h3 className="font-bold text-2xl mb-2 text-stone-800">Limit Reached</h3>
                <p className="text-stone-600 mb-8 text-lg leading-relaxed">{message}</p>
                <div className="flex flex-col gap-3">
                    <button 
                        onClick={() => { onClose(); navigate('/pricing'); }}
                        className="w-full py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-200"
                    >
                        {t('pricing')} (Upgrade Now)
                    </button>
                    <button 
                        onClick={onClose}
                        className="w-full py-3 text-stone-400 hover:text-stone-600 font-medium"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const ChatMessage = ({ message, onRate, userQuery }) => {
  const isUser = message.role === "user";
  const { t } = useLanguage();
  const [rated, setRated] = useState(null); // 'up' or 'down'
  const [copied, setCopied] = useState(false);

  const handleRate = (type) => {
      setRated(type);
      onRate(type, message.content, userQuery);
  };

  const handleCopy = () => {
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      const text = `${userQuery ? `السؤال: ${userQuery}\n\nجواب تطبيق اسأل د. نواف: ` : ''}${message.content}\n\n🗓️ ${dateStr}, ${timeStr}\n\nكافة الحقوق محفوظة⛔\nالموقع الإلكتروني للتطبيق\n✅www.ask-dr-nawwaf.com`;
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className={`flex w-full mb-4 ${isUser ? "justify-start" : "justify-end"}`}>
      <div 
        className={`flex flex-col max-w-[85%] md:max-w-[75%] rounded-2xl p-4 shadow-lg ${
          isUser 
            ? "bg-[#1e40af] text-white rounded-br-none" 
            : "bg-white text-[#1e293b] rounded-bl-none border border-[#e2e8f0]"
        }`}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 mb-1 opacity-80 text-xs">
            {isUser ? (
              <User size={14} className="text-white/70" />
            ) : (
              <img 
                src={DR_NAWWAF_IMG} 
                alt="Dr. Nawwaf" 
                className="w-4 h-4 rounded-full object-cover"
              />
            )}
            <span className={`font-semibold ${isUser ? "text-white/80" : "text-[#d4a574]"}`}>
              {isUser ? t('you') : t('drNawwaf')}
            </span>
          </div>
          <p className={`text-base leading-relaxed whitespace-pre-wrap font-arabic ${isUser ? "text-white" : "text-[#334155]"}`} dir="auto">
            {message.content}
          </p>
        </div>

        {!isUser && (
            <div className="mt-3 flex items-center gap-2 border-t border-[#e2e8f0] pt-2">
                <button 
                    onClick={() => handleRate('up')}
                    disabled={rated !== null}
                    className={`p-1.5 rounded-full transition-colors ${rated === 'up' ? 'bg-[#1e40af]/10 text-[#1e40af]' : 'hover:bg-[#f1f5f9] text-[#94a3b8] opacity-60 hover:opacity-100'}`}
                    title="Helpful"
                >
                    <ThumbsUp size={14} />
                </button>
                <button 
                    onClick={() => handleRate('down')}
                    disabled={rated !== null}
                    className={`p-1.5 rounded-full transition-colors ${rated === 'down' ? 'bg-red-100 text-red-600' : 'hover:bg-[#f1f5f9] text-[#94a3b8] opacity-60 hover:opacity-100'}`}
                    title="Unhelpful"
                >
                    <ThumbsDown size={14} />
                </button>
                <div className="w-px h-4 bg-[#e2e8f0] mx-1"></div>
                <button 
                    onClick={handleCopy}
                    className="p-1.5 rounded-full hover:bg-[#f1f5f9] text-[#94a3b8] opacity-60 hover:opacity-100 transition-colors flex items-center gap-1"
                    title={t('copy')}
                >
                    {copied ? <Check size={14} className="text-[#1e40af]" /> : <Copy size={14} />}
                    {copied && <span className="text-[10px] font-bold text-[#1e40af]">{t('copied')}</span>}
                </button>
                
                {rated && <span className="text-[10px] text-[#d4a574] font-bold px-1 ml-auto">{t('thanks')}</span>}
            </div>
        )}
      </div>
    </div>
  );
};

const Sidebar = ({ isOpen, onClose, sessions, currentSessionId, onSelectSession, onExportChats, exportingChats }) => {
  const { t, dir } = useLanguage();
  return (
    <div className={`fixed inset-y-0 z-40 w-72 bg-[#0f172a] shadow-xl transform transition-transform duration-300 ease-in-out md:static md:w-80 md:shadow-none border-r border-[#334155] flex flex-col
        ${dir === 'rtl' ? 'right-0' : 'left-0'}
        ${isOpen ? "translate-x-0" : (dir === 'rtl' ? "translate-x-full" : "-translate-x-full")} md:translate-x-0`}>
      <div className="p-4 border-b border-[#334155] flex justify-between items-center">
        <h2 className="font-bold text-[#e2e8f0]">{t('prevChats')}</h2>
        <button onClick={onClose} className="md:hidden p-1 text-[#94a3b8]">
          <X size={20} />
        </button>
      </div>

      {/* Export Chats Button */}
      <div className="px-3 pt-3">
          <button
              onClick={onExportChats}
              disabled={exportingChats || sessions.length === 0}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 border border-[#d4a574]/40 text-[#d4a574] hover:bg-[#d4a574]/10 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="export-chats-btn"
          >
              {exportingChats ? (
                  <>
                      <RefreshCw size={15} className="animate-spin" />
                      <span>جاري التصدير...</span>
                  </>
              ) : (
                  <>
                      <Download size={15} />
                      <span>حفظ المحادثات على جهازك</span>
                  </>
              )}
          </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {sessions.length === 0 && (
          <div className="text-center text-[#64748b] py-8 text-sm">
            {t('noChats')}
          </div>
        )}

        {sessions.map((session, index) => (
          <div 
            key={session.id}
            className={`group relative flex items-center p-3 rounded-lg cursor-pointer transition-colors ${currentSessionId === session.id ? "bg-[#1e293b] border border-[#d4a574]/40" : "hover:bg-[#1e293b]/50 border border-transparent"}`}
            onClick={() => { onSelectSession(session.id); if(window.innerWidth < 768) onClose(); }}
          >
            <span className={`text-[10px] font-bold min-w-[24px] text-center rounded-full py-0.5 ${currentSessionId === session.id ? "bg-[#d4a574]/20 text-[#d4a574]" : "bg-[#1e293b] text-[#64748b]"}`}>
              {sessions.length - index}
            </span>
            <div className="flex-1 min-w-0 text-start ms-2">
              <h3 className={`text-sm font-medium truncate ${currentSessionId === session.id ? "text-[#d4a574]" : "text-[#94a3b8]"}`}>
                {session.title || "Untitled"}
              </h3>
              <p className="text-[10px] text-[#64748b] mt-1">
                {new Date(session.updated_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Admin Component used as both Modal and Full Page
const AdminPanel = ({ isModal, onClose }) => {
  const { t, dir } = useLanguage();
  const [password, setPassword] = useState(safeStorage.getItem('adminPassword') || "");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sources, setSources] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("payments"); // "payments", "file", "audio", "youtube", "gdrive", "analytics", "questions"
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [gdriveUrl, setGdriveUrl] = useState("");
  const [playlistVideos, setPlaylistVideos] = useState([]);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [questions, setQuestions] = useState(null);
  const [questionsDays, setQuestionsDays] = useState(7);
  const [enhancedAnalytics, setEnhancedAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [transcriptionPreview, setTranscriptionPreview] = useState("");
  // Payment management states
  const [paymentStats, setPaymentStats] = useState(null);
  const [paymentUsers, setPaymentUsers] = useState([]);
  const [paymentTransactions, setPaymentTransactions] = useState([]);
  const [txFilter, setTxFilter] = useState('all');
  const [pendingStripe, setPendingStripe] = useState([]);
  const [pendingPaypal, setPendingPaypal] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({});
  const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradePlan, setUpgradePlan] = useState("basic");
  const [upgradeNote, setUpgradeNote] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [userSort, setUserSort] = useState("newest");
  const [userListSearch, setUserListSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);

  useEffect(() => {
    if (safeStorage.getItem('adminPassword')) {
       setIsAuthenticated(true); 
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
        fetchSources();
        fetchStats();
        fetchFeedbacks();
        fetchPaymentData();
    }
  }, [isAuthenticated]);

  const fetchAnalytics = async () => {
      try {
          const res = await axios.get(`${API_URL}/admin/analytics`, { 
              headers: getHeaders(),
              timeout: 10000 // 10 second timeout
          });
          setAnalytics(res.data);
      } catch (e) {
          console.error("Fetch analytics error", e);
          // Set empty data so UI doesn't stay stuck
          setAnalytics({ messages_today: 0, unique_devices_today: 0, unique_ips_today: 0 });
      }
  };

  const fetchQuestions = async (days = questionsDays) => {
      try {
          const res = await axios.get(`${API_URL}/admin/questions?days=${days}`, { headers: getHeaders() });
          setQuestions(res.data);
      } catch (e) {
          console.error("Fetch questions error", e);
      }
  };

  const fetchEnhancedAnalytics = async (days = analyticsDays) => {
      try {
          const res = await axios.get(`${API_URL}/admin/analytics/enhanced?days=${days}`, { 
              headers: getHeaders(),
              timeout: 10000 // 10 second timeout
          });
          setEnhancedAnalytics(res.data);
      } catch (e) {
          console.error("Fetch enhanced analytics error", e);
          // Set empty data so UI doesn't stay stuck on loading
          setEnhancedAnalytics({
              subscriptions: { total_users: 0, no_plan_users: 0, basic_users: 0, pro_users: 0, ultimate_users: 0, gift_users: 0 },
              revenue: { total_period: 0, by_plan: {} },
              engagement: { avg_questions_per_day: 0 },
              geographic: { top_countries: [] },
              peak_hours: []
          });
      }
  };

  // Country code to flag emoji converter
  const getCountryFlag = (countryCode) => {
      if (!countryCode || countryCode === "XX" || countryCode.length !== 2) return "🌍";
      const codePoints = countryCode
          .toUpperCase()
          .split('')
          .map(char => 127397 + char.charCodeAt(0));
      return String.fromCodePoint(...codePoints);
  };

  const getHeaders = () => ({
      'x-admin-secret': password
  });

  const fetchSources = async () => {
      try {
          const res = await axios.get(`${API_URL}/admin/sources`, { headers: getHeaders(), timeout: 15000 });
          setSources(res.data);
      } catch (e) {
          console.error("Failed to fetch sources", e);
          if (e.response && e.response.status === 401) {
              setIsAuthenticated(false);
              safeStorage.removeItem('adminPassword');
              alert("Auth failed");
          }
          setSources([]);
      }
  };

  const fetchStats = async () => {
      try {
          const res = await axios.get(`${API_URL}/admin/stats`, { headers: getHeaders(), timeout: 10000 });
          setStats(res.data);
      } catch (e) {
          console.error("Failed stats", e);
          setStats({ total: 0, youtube: 0, files: 0, audio: 0, gdrive: 0, recent: [], total_chats: 0 });
      }
  };

  const fetchFeedbacks = async () => {
      try {
          const res = await axios.get(`${API_URL}/admin/feedbacks`, { headers: getHeaders(), timeout: 10000 });
          setFeedbacks(res.data);
      } catch (e) {
          console.error("Failed feedbacks", e);
          setFeedbacks([]);
      }
  };

  // Payment management functions
  const fetchPaymentData = async () => {
      try {
          const [statsRes, usersRes, txRes, pendingStripeRes, pendingPaypalRes] = await Promise.all([
              axios.get(`${API_URL}/admin/payments/stats?days=3650`, { headers: getHeaders(), timeout: 15000 }),
              axios.get(`${API_URL}/admin/payments/users`, { headers: getHeaders(), timeout: 15000 }),
              axios.get(`${API_URL}/admin/payments/transactions?days=3650`, { headers: getHeaders(), timeout: 30000 }),
              axios.get(`${API_URL}/admin/payments/pending-stripe`, { headers: getHeaders(), timeout: 15000 }),
              axios.get(`${API_URL}/admin/payments/pending-paypal`, { headers: getHeaders(), timeout: 15000 })
          ]);
          setPaymentStats(statsRes.data);
          setPaymentUsers(usersRes.data);
          setPaymentTransactions(txRes.data);
          setPendingStripe(pendingStripeRes.data.pending_verifications || []);
          setPendingPaypal(pendingPaypalRes.data.pending_verifications || []);
      } catch (e) {
          console.error("Failed to fetch payment data", e);
          setPaymentStats({});
          setPaymentUsers([]);
          setPaymentTransactions([]);
          setPendingStripe([]);
          setPendingPaypal([]);
      }
  };

  const handleVerifyStripe = async (pending) => {
      // Confirmation dialog
      const confirmed = confirm(
          `⚠️ CONFIRM PAYMENT VERIFICATION\n\n` +
          `User: ${pending.user_name}\n` +
          `Email: ${pending.user_email}\n` +
          `Plan: ${pending.plan_name}\n` +
          `Amount: $${pending.amount} USD\n\n` +
          `Have you verified this payment was received in your Stripe dashboard?\n\n` +
          `Click OK only if payment is confirmed in Stripe.`
      );
      
      if (!confirmed) return;
      
      try {
          await axios.post(`${API_URL}/admin/payments/verify-stripe`, {
              user_id: pending.user_id,
              plan_id: pending.plan_id
          }, { headers: getHeaders() });
          alert(`✅ ${pending.user_name} has been upgraded to ${pending.plan_name}!`);
          fetchPaymentData();
      } catch (e) {
          alert("Verification failed: " + (e.response?.data?.detail || e.message));
      }
  };

  const handleRejectStripe = async (pending) => {
      if (!confirm(`Dismiss payment request from ${pending.user_name}?`)) return;
      try {
          await axios.post(`${API_URL}/admin/payments/reject-stripe`, {
              user_id: pending.user_id,
              plan_id: pending.plan_id
          }, { headers: getHeaders() });
          fetchPaymentData();
      } catch (e) {
          alert("Failed: " + (e.response?.data?.detail || e.message));
      }
  };

  const handleVerifyPaypal = async (pending) => {
      const confirmed = confirm(
          `CONFIRM PAYPAL PAYMENT VERIFICATION\n\n` +
          `User: ${pending.user_name}\n` +
          `Email: ${pending.user_email}\n` +
          `Plan: ${pending.plan_name}\n` +
          `Amount: $${pending.amount} USD\n\n` +
          `Have you verified this payment was received in your PayPal account?\n\n` +
          `Click OK only if payment is confirmed in PayPal.`
      );
      if (!confirmed) return;
      try {
          await axios.post(`${API_URL}/admin/payments/verify-paypal`, {
              user_id: pending.user_id,
              plan_id: pending.plan_id
          }, { headers: getHeaders() });
          alert(`${pending.user_name} has been upgraded to ${pending.plan_name}!`);
          fetchPaymentData();
      } catch (e) {
          alert("Verification failed: " + (e.response?.data?.detail || e.message));
      }
  };

  const handleRejectPaypal = async (pending) => {
      if (!confirm(`Dismiss PayPal payment request from ${pending.user_name}?`)) return;
      try {
          await axios.post(`${API_URL}/admin/payments/reject-paypal`, {
              user_id: pending.user_id,
              plan_id: pending.plan_id
          }, { headers: getHeaders() });
          fetchPaymentData();
      } catch (e) {
          alert("Failed: " + (e.response?.data?.detail || e.message));
      }
  };

  const searchUsers = async (query) => {
      if (query.length < 2) {
          setSearchResults([]);
          return;
      }
      try {
          const res = await axios.get(`${API_URL}/admin/payments/search-user?q=${encodeURIComponent(query)}`, { headers: getHeaders() });
          setSearchResults(res.data);
      } catch (e) {
          console.error("Search failed", e);
      }
  };

  const handleUpgradeUser = async () => {
      if (!selectedUser) return;
      try {
          await axios.post(`${API_URL}/admin/payments/upgrade-user`, {
              user_id: selectedUser.user_id,
              plan_id: upgradePlan,
              note: upgradeNote || "Manual upgrade by admin"
          }, { headers: getHeaders() });
          alert(`User ${selectedUser.name} upgraded to ${upgradePlan} successfully!`);
          setUpgradeModalOpen(false);
          setSelectedUser(null);
          setUpgradeNote("");
          fetchPaymentData();
      } catch (e) {
          alert("Upgrade failed: " + (e.response?.data?.detail || e.message));
      }
  };

  const handleDowngradeUser = async (user) => {
      if (!confirm(`Are you sure you want to remove ${user.name}'s subscription?`)) return;
      try {
          await axios.post(`${API_URL}/admin/payments/downgrade-user`, {
              user_id: user.user_id,
              plan_id: "none",
              note: "Manual downgrade by admin"
          }, { headers: getHeaders() });
          alert(`User ${user.name}'s subscription removed!`);
          fetchPaymentData();
      } catch (e) {
          alert("Downgrade failed: " + (e.response?.data?.detail || e.message));
      }
  };

  const handleResetQuestions = async (user) => {
      if (!confirm(`Reset questions count for ${user.name}?`)) return;
      try {
          await axios.post(`${API_URL}/admin/payments/reset-questions`, {
              user_id: user.user_id,
              note: "Questions reset by admin"
          }, { headers: getHeaders() });
          alert(`Questions reset for ${user.name}!`);
          fetchPaymentData();
      } catch (e) {
          alert("Reset failed: " + (e.response?.data?.detail || e.message));
      }
  };

  const handleDeleteUser = async (user) => {
      const confirmed = confirm(
          `⚠️ DELETE USER - THIS CANNOT BE UNDONE!\n\n` +
          `User: ${user.name}\n` +
          `Email: ${user.email}\n` +
          `Plan: ${user.subscription_plan || 'none'}\n\n` +
          `This will permanently delete:\n` +
          `• User account\n` +
          `• All payment history\n` +
          `• All chat logs\n\n` +
          `Are you absolutely sure?`
      );
      
      if (!confirmed) return;
      
      // Double confirmation for safety
      const doubleConfirm = confirm(`⛔ FINAL WARNING: Delete ${user.name} permanently?`);
      if (!doubleConfirm) return;
      
      try {
          await axios.delete(`${API_URL}/admin/payments/delete-user?user_id=${user.user_id}`, { headers: getHeaders() });
          alert(`🗑️ User ${user.name} has been deleted.`);
          fetchPaymentData();
      } catch (e) {
          alert("Delete failed: " + (e.response?.data?.detail || e.message));
      }
  };

  const handleRepairUser = async (user) => {
      const confirmed = confirm(
          `🔧 REPAIR USER ACCOUNT\n\n` +
          `User: ${user.name}\n` +
          `Email: ${user.email}\n\n` +
          `This will:\n` +
          `• Clear all old/stale sessions\n` +
          `• Fix missing account fields\n` +
          `• User will need to sign in again\n\n` +
          `Proceed?`
      );
      if (!confirmed) return;
      
      try {
          const res = await axios.post(`${API_URL}/admin/payments/repair-user`, 
              { user_id: user.user_id }, 
              { headers: getHeaders() }
          );
          alert(`✅ ${res.data.message}`);
          fetchPaymentData();
      } catch (e) {
          alert("Repair failed: " + (e.response?.data?.detail || e.message));
      }
  };

  const handleRepairAllUsers = async () => {
      const confirmed = confirm(
          `🔧 REPAIR ALL USERS\n\n` +
          `This will:\n` +
          `• Clean up all expired sessions\n` +
          `• Remove orphaned sessions\n` +
          `• Fix missing fields in all user accounts\n\n` +
          `Proceed?`
      );
      if (!confirmed) return;
      
      try {
          const res = await axios.post(`${API_URL}/admin/payments/repair-all-users`, {}, { headers: getHeaders() });
          const r = res.data.results;
          alert(`✅ Repair Complete!\n\nRepaired users: ${r.repaired_users}\nCleaned expired sessions: ${r.cleaned_sessions}\nCleaned orphaned sessions: ${r.cleaned_orphans}`);
          fetchPaymentData();
      } catch (e) {
          alert("Repair failed: " + (e.response?.data?.detail || e.message));
      }
  };

  // Get currently filtered users list
  const getFilteredUsers = () => {
      return paymentUsers.filter(user => {
          const plan = user.subscription_plan === 'free' ? 'none' : user.subscription_plan;
          if (userFilter === "all") return true;
          if (userFilter === "paid") return plan && plan !== 'none';
          if (userFilter === "none") return !plan || plan === 'none';
          return plan === userFilter;
      });
  };

  const getSortedFilteredUsers = () => {
      let users = getFilteredUsers();
      // Search filter
      if (userListSearch && userListSearch.trim()) {
          const q = userListSearch.trim().toLowerCase();
          users = users.filter(u => 
              (u.name || '').toLowerCase().includes(q) || 
              (u.email || '').toLowerCase().includes(q) ||
              (u.user_id || '').toLowerCase().includes(q)
          );
      }
      // Sort
      return [...users].sort((a, b) => {
          if (userSort === 'newest') return (b.created_at || '').localeCompare(a.created_at || '');
          if (userSort === 'oldest') return (a.created_at || '').localeCompare(b.created_at || '');
          if (userSort === 'most_paid') return (b.total_paid || 0) - (a.total_paid || 0);
          if (userSort === 'most_questions') return (b.questions_used || 0) - (a.questions_used || 0);
          if (userSort === 'name_az') return (a.name || '').localeCompare(b.name || '');
          return 0;
      });
  };

  const handleSelectAll = (checked) => {
      if (checked) {
          const filtered = getSortedFilteredUsers();
          setSelectedUserIds(new Set(filtered.map(u => u.user_id)));
      } else {
          setSelectedUserIds(new Set());
      }
  };

  const toggleUserSelection = (userId) => {
      setSelectedUserIds(prev => {
          const next = new Set(prev);
          if (next.has(userId)) next.delete(userId);
          else next.add(userId);
          return next;
      });
  };

  const handleBulkDelete = async () => {
      if (selectedUserIds.size === 0) return;
      
      const filterLabel = userFilter === 'none' ? 'Free/No Plan' : userFilter;
      const confirmed = confirm(
          `⚠️ BULK DELETE — ${selectedUserIds.size} USERS\n\n` +
          `Filter: ${filterLabel}\n` +
          `This will permanently delete ${selectedUserIds.size} user accounts and all their data.\n\n` +
          `THIS CANNOT BE UNDONE!\n\n` +
          `Are you sure?`
      );
      if (!confirmed) return;

      const doubleConfirm = confirm(`⛔ FINAL WARNING: Delete ${selectedUserIds.size} users permanently?`);
      if (!doubleConfirm) return;

      try {
          const res = await axios.post(`${API_URL}/admin/payments/bulk-delete-users`, 
              { user_ids: Array.from(selectedUserIds) },
              { headers: getHeaders() }
          );
          alert(`🗑️ Deleted ${res.data.deleted_count} users successfully.`);
          setSelectedUserIds(new Set());
          fetchPaymentData();
      } catch (e) {
          alert("Bulk delete failed: " + (e.response?.data?.detail || e.message));
      }
  };

  const [diagnoseEmail, setDiagnoseEmail] = useState('');
  const [diagnoseResult, setDiagnoseResult] = useState(null);
  
  // --- Export/Import Progress Tracking ---
  const [opProgress, setOpProgress] = useState({
      active: false,
      type: '',
      stage: '',
      progress: 0,
      loaded: 0,
      total: 0,
      speed: 0,
      startTime: null,
      message: '',
      result: null,
      error: null,
      fileName: ''
  });
  
  const formatBytes = (bytes) => {
      if (!bytes || bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };
  
  const formatSpeed = (bps) => {
      if (!bps) return '—';
      return formatBytes(bps) + '/s';
  };
  
  const formatETA = (seconds) => {
      if (!seconds || !isFinite(seconds)) return '—';
      if (seconds < 60) return `${Math.ceil(seconds)}s`;
      return `${Math.floor(seconds / 60)}m ${Math.ceil(seconds % 60)}s`;
  };

  const handleExport = async (type) => {
      const urlMap = {
          'full': `/admin/export-full-backup?key=${encodeURIComponent(password)}`,
          'csv': `/admin/users/export-csv?key=${encodeURIComponent(password)}`,
          'kb': `/admin/knowledge-base/export?key=${encodeURIComponent(password)}`
      };
      const nameMap = { 'full': 'Full Database Backup', 'csv': 'Users CSV', 'kb': 'Knowledge Base' };
      
      setOpProgress({ active: true, type: `export-${type}`, stage: 'preparing', progress: 0, loaded: 0, total: 0, speed: 0, startTime: Date.now(), message: `Preparing ${nameMap[type]} export...`, result: null, error: null, fileName: '' });
      
      try {
          // KB export is async (large dataset)
          if (type === 'kb') {
              const startRes = await axios.get(`${API_URL}/${urlMap[type]}`, { timeout: 30000 });
              const jobId = startRes.data.job_id;
              
              // Poll for progress
              let polling = true;
              while (polling) {
                  await new Promise(resolve => setTimeout(resolve, 2000));
                  try {
                      const statusRes = await axios.get(`${API_URL}/admin/import-status?job_id=${jobId}&key=${encodeURIComponent(password)}`);
                      const job = statusRes.data;
                      
                      if (job.status === 'complete') {
                          polling = false;
                          setOpProgress(p => ({ ...p, stage: 'downloading', progress: 95, message: 'Downloading file...' }));
                          
                          // Download the file
                          const dlRes = await axios.get(
                              `${API_URL}/admin/knowledge-base/download?job_id=${jobId}&key=${encodeURIComponent(password)}`,
                              { responseType: 'blob', timeout: 300000 }
                          );
                          
                          const disposition = dlRes.headers['content-disposition'] || '';
                          let fileName = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]?.replace(/['"]/g, '') || '';
                          if (!fileName) fileName = `knowledge_base_${new Date().toISOString().slice(0,19).replace(/[:.]/g,'-')}.json.gz`;
                          
                          const url = window.URL.createObjectURL(dlRes.data);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = fileName;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(url);
                          
                          setOpProgress(p => ({
                              ...p, stage: 'complete', progress: 100, message: 'Export complete!',
                              fileName, result: { size: dlRes.data.size, fileName, total_docs: job.results?.total_docs }
                          }));
                      } else if (job.status === 'error') {
                          polling = false;
                          setOpProgress(p => ({ ...p, stage: 'error', error: job.errors?.join(', ') || 'Export failed', message: 'Export failed' }));
                      } else {
                          setOpProgress(p => ({
                              ...p, stage: 'processing', progress: job.progress || 0,
                              message: job.message || `Processing... ${job.processed_docs || 0}/${job.total_docs || '?'} documents`
                          }));
                      }
                  } catch (pollErr) {
                      console.warn("Poll error:", pollErr);
                  }
              }
              return;
          }
          
          // Non-KB exports (small files) — direct download
          const response = await axios.get(`${API_URL}/${urlMap[type]}`, {
              responseType: 'blob',
              timeout: 600000,
              onDownloadProgress: (e) => {
                  const now = Date.now();
                  const elapsed = (now - (opProgress.startTime || now)) / 1000;
                  const speed = elapsed > 0 ? e.loaded / elapsed : 0;
                  const eta = e.total ? (e.total - e.loaded) / speed : 0;
                  const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
                  setOpProgress(p => ({
                      ...p,
                      stage: 'downloading',
                      progress: pct || Math.min(p.progress + 1, 95),
                      loaded: e.loaded,
                      total: e.total || 0,
                      speed: speed,
                      message: `Downloading... ${formatBytes(e.loaded)}${e.total ? ` / ${formatBytes(e.total)}` : ''} — ETA: ${formatETA(eta)}`
                  }));
              }
          });
          
          const disposition = response.headers['content-disposition'] || '';
          let fileName = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]?.replace(/['"]/g, '') || '';
          if (!fileName) {
              const ext = type === 'csv' ? '.csv' : '.json.gz';
              const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
              fileName = `${nameMap[type].replace(/\s/g, '_')}_${ts}${ext}`;
          }
          
          const url = window.URL.createObjectURL(response.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          
          setOpProgress(p => ({
              ...p, stage: 'complete', progress: 100, message: 'Export complete!',
              fileName, result: { size: response.data.size, fileName }
          }));
      } catch (err) {
          setOpProgress(p => ({
              ...p, stage: 'error',
              error: err.response?.data?.detail || err.message || 'Export failed',
              message: 'Export failed'
          }));
      }
  };

  const handleImport = async (type, file, mode) => {
      const nameMap = { 'full': 'Full Backup', 'kb': 'Knowledge Base' };
      const CHUNK_SIZE = 1 * 1024 * 1024; // 1MB chunks (proxy-safe)
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const uploadId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      
      setOpProgress({ active: true, type: `import-${type}`, stage: 'uploading', progress: 0, loaded: 0, total: file.size, speed: 0, startTime: Date.now(), message: `Uploading ${file.name} (${totalChunks} chunks)...`, result: null, error: null, fileName: file.name });
      
      try {
          const uploadStart = Date.now();
          
          // Upload file in chunks with retry
          for (let i = 0; i < totalChunks; i++) {
              const start = i * CHUNK_SIZE;
              const end = Math.min(start + CHUNK_SIZE, file.size);
              const chunk = file.slice(start, end);
              
              const formData = new FormData();
              formData.append('file', chunk, `chunk_${i}`);
              
              let chunkSuccess = false;
              let retries = 3;
              while (!chunkSuccess && retries > 0) {
                  try {
                      await axios.post(
                          `${API_URL}/admin/upload-chunk?key=${encodeURIComponent(password)}&upload_id=${uploadId}&chunk_index=${i}&total_chunks=${totalChunks}`,
                          formData,
                          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
                      );
                      chunkSuccess = true;
                  } catch (chunkErr) {
                      retries--;
                      if (retries <= 0) throw chunkErr;
                      await new Promise(r => setTimeout(r, 2000));
                  }
              }
              
              const loaded = end;
              const elapsed = (Date.now() - uploadStart) / 1000;
              const speed = elapsed > 0 ? loaded / elapsed : 0;
              const eta = speed > 0 ? (file.size - loaded) / speed : 0;
              const pct = Math.round((loaded / file.size) * 100);
              
              setOpProgress(p => ({
                  ...p,
                  progress: Math.min(pct, 99),
                  loaded: loaded,
                  speed: speed,
                  message: `Uploading chunk ${i + 1}/${totalChunks}... ${formatBytes(loaded)} / ${formatBytes(file.size)} — ETA: ${formatETA(eta)}`
              }));
          }
          
          // All chunks uploaded — trigger processing
          setOpProgress(p => ({ ...p, stage: 'processing', progress: 0, speed: 0, message: 'All chunks uploaded. Starting server processing...' }));
          
          const processRes = await axios.post(
              `${API_URL}/admin/process-import?key=${encodeURIComponent(password)}&upload_id=${uploadId}&import_type=${type}&mode=${mode || 'merge'}`,
              {},
              { timeout: 120000 }
          );
          
          const jobId = processRes.data.job_id;
          
          // Poll for job status
          let polling = true;
          while (polling) {
              await new Promise(resolve => setTimeout(resolve, 2000));
              try {
                  const statusRes = await axios.get(`${API_URL}/admin/import-status?job_id=${jobId}&key=${encodeURIComponent(password)}`);
                  const job = statusRes.data;
                  
                  if (job.status === 'complete') {
                      polling = false;
                      setOpProgress(p => ({
                          ...p,
                          stage: 'complete',
                          progress: 100,
                          message: 'Import complete!',
                          result: { imported: job.results, errors: job.errors, total_docs: job.total_docs, processed_docs: job.processed_docs }
                      }));
                  } else if (job.status === 'error') {
                      polling = false;
                      setOpProgress(p => ({
                          ...p,
                          stage: 'error',
                          error: job.errors?.join(', ') || 'Unknown error',
                          message: 'Import failed'
                      }));
                  } else {
                      setOpProgress(p => ({
                          ...p,
                          stage: 'processing',
                          progress: job.progress || 0,
                          message: job.message || `Processing... ${job.processed_docs || 0}/${job.total_docs || '?'} documents`
                      }));
                  }
              } catch (pollErr) {
                  console.warn("Poll error:", pollErr);
              }
          }
      } catch (err) {
          setOpProgress(p => ({
              ...p,
              stage: 'error',
              error: err.response?.data?.detail || err.message || 'Import failed',
              message: 'Import failed'
          }));
      }
  };
  
  const handleDiagnoseUser = async () => {
      if (!diagnoseEmail.trim()) return;
      try {
          const res = await axios.get(`${API_URL}/admin/diagnose-user?email=${encodeURIComponent(diagnoseEmail.trim())}`, { headers: getHeaders() });
          setDiagnoseResult(res.data);
      } catch (e) {
          setDiagnoseResult({ error: e.response?.data?.detail || e.message });
      }
  };

  const handleGrantGift = async (user) => {
      // Calculate current remaining
      const currentRemaining = Math.max(0, (user.questions_limit || 0) - (user.questions_used || 0));
      
      // First, prompt for number of questions
      const questionsInput = prompt(
          `🎁 ADD QUESTIONS\n\n` +
          `User: ${user.name}\n` +
          `Email: ${user.email}\n` +
          `Current remaining: ${currentRemaining} questions\n\n` +
          `Enter the number of questions to ADD:`,
          "100"
      );
      
      if (!questionsInput) return;
      
      const customQuestions = parseInt(questionsInput);
      if (isNaN(customQuestions) || customQuestions < 1) {
          alert("Please enter a valid number of questions (minimum 1)");
          return;
      }
      
      const confirmed = confirm(
          `Confirm adding ${customQuestions} questions to ${user.name}?\n\n` +
          `Current: ${currentRemaining} questions\n` +
          `Adding: +${customQuestions} questions\n` +
          `New total: ${currentRemaining + customQuestions} questions`
      );
      
      if (!confirmed) return;
      
      try {
          const res = await axios.post(`${API_URL}/admin/payments/grant-gift`, {
              user_id: user.user_id,
              note: "Gift access granted by admin",
              custom_questions: customQuestions
          }, { headers: getHeaders() });
          alert(`🎁 ${res.data.message}`);
          fetchPaymentData();
      } catch (e) {
          alert("Grant gift failed: " + (e.response?.data?.detail || e.message));
      }
  };

  // Migration function for deployment
  const handleMigrateQuestions = async () => {
      if (!confirm(
          "⚠️ MIGRATION TOOL\n\n" +
          "This will set the questions_limit field for all existing users.\n" +
          "This ensures no user loses their purchased questions.\n\n" +
          "Safe to run multiple times - won't overwrite existing data.\n\n" +
          "Run this ONCE after deploying to production."
      )) return;
      
      try {
          const res = await axios.post(`${API_URL}/admin/migrate-questions`, {}, { headers: getHeaders() });
          alert(
              `✅ Migration Complete!\n\n` +
              `Migrated: ${res.data.migrated_count} users\n` +
              `Skipped (already set): ${res.data.skipped_count} users\n` +
              `Total users: ${res.data.total_users}`
          );
          fetchPaymentData();
      } catch (e) {
          alert("Migration failed: " + (e.response?.data?.detail || e.message));
      }
  };

  const handleLogin = (e) => {
      e.preventDefault();
      if (!password) return;
      safeStorage.setItem('adminPassword', password);
      setIsAuthenticated(true);
  };

  const handleUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      try {
          await axios.post(`${API_URL}/admin/upload`, formData, {
              headers: { 
                  "Content-Type": "multipart/form-data",
                  ...getHeaders()
              }
          });
          await fetchSources();
          await fetchStats();
          alert("Success");
      } catch (err) {
          console.error(err);
          alert("Upload failed: " + (err.response?.data?.detail || err.message));
      } finally {
          setUploading(false);
          if(fileInputRef.current) fileInputRef.current.value = "";
      }
  };

  const handleAudioUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setUploading(true);
      setTranscriptionPreview("");
      const formData = new FormData();
      formData.append("file", file);

      try {
          const res = await axios.post(`${API_URL}/admin/upload-audio`, formData, {
              headers: { 
                  "Content-Type": "multipart/form-data",
                  ...getHeaders()
              },
              timeout: 300000 // 5 minutes timeout for large audio files
          });
          await fetchSources();
          await fetchStats();
          setTranscriptionPreview(res.data.transcription_preview || "");
          alert(`Success! Transcribed ${res.data.chunks} chunks from audio.`);
      } catch (err) {
          console.error(err);
          alert("Audio upload failed: " + (err.response?.data?.detail || err.message));
      } finally {
          setUploading(false);
          if(audioInputRef.current) audioInputRef.current.value = "";
      }
  };

  const handleYoutubeUpload = async (e) => {
      e.preventDefault();
      if (!youtubeUrl.trim()) return;
      
      setUploading(true);
      setPlaylistVideos([]);
      
      try {
          const res = await axios.post(`${API_URL}/admin/fetch-playlist`, { url: youtubeUrl }, { headers: getHeaders() });
          
          if (res.data.count > 1) {
              const newCount = res.data.new_count || res.data.count;
              const dupCount = res.data.duplicate_count || 0;
              
              setPlaylistVideos(res.data.videos);
              
              let confirmMsg = `Found ${res.data.count} videos total.\n`;
              if (dupCount > 0) {
                  confirmMsg += `✅ ${dupCount} already imported (will be skipped)\n`;
                  confirmMsg += `🆕 ${newCount} new videos to import\n\n`;
              }
              confirmMsg += `Import ${newCount} new videos?`;
              
              if (newCount > 0 && window.confirm(confirmMsg)) {
                  await processBatchImport(res.data.videos);
              } else if (newCount === 0) {
                  alert(`All ${res.data.count} videos are already imported!`);
                  setYoutubeUrl("");
              }
          } else {
              const result = await axios.post(`${API_URL}/admin/upload-youtube`, { url: youtubeUrl }, { headers: getHeaders() });
              if (result.data.status === "skipped") {
                  alert(`Video already imported: ${result.data.filename}`);
              } else {
                  await fetchSources();
                  alert("Success");
              }
              setYoutubeUrl("");
          }
      } catch (err) {
          console.error(err);
          alert("Import failed: " + (err.response?.data?.detail || err.message));
      } finally {
          setUploading(false);
      }
  };

  const processBatchImport = async (videos) => {
      setImportProgress({ current: 0, total: videos.length });
      let successCount = 0;
      let skippedCount = 0;
      
      for (let i = 0; i < videos.length; i++) {
          try {
              setImportProgress({ current: i + 1, total: videos.length });
              const result = await axios.post(`${API_URL}/admin/upload-youtube`, { url: videos[i].url }, { headers: getHeaders() });
              if (result.data.status === "skipped") {
                  skippedCount++;
              } else {
                  successCount++;
              }
          } catch (e) {
              console.error(`Failed to import ${videos[i].title}`, e);
          }
      }
      
      await fetchSources();
      await fetchStats();
      setImportProgress({ current: 0, total: 0 });
      setPlaylistVideos([]);
      
      let resultMsg = `Batch import complete!\n`;
      resultMsg += `✅ ${successCount} new videos imported\n`;
      if (skippedCount > 0) {
          resultMsg += `⏭️ ${skippedCount} duplicates skipped`;
      }
      alert(resultMsg);
      setYoutubeUrl("");
  };

  const handleGdriveImport = async (e) => {
      e.preventDefault();
      if (!gdriveUrl.trim()) return;
      
      setUploading(true);
      
      try {
          const res = await axios.post(`${API_URL}/admin/import-gdrive`, { url: gdriveUrl }, { 
              headers: getHeaders(),
              timeout: 300000 // 5 min timeout for large files
          });
          await fetchSources();
          await fetchStats();
          alert(`Success! Imported "${res.data.filename}" (${res.data.chunks} chunks)`);
          setGdriveUrl("");
      } catch (err) {
          console.error(err);
          alert("Google Drive import failed: " + (err.response?.data?.detail || err.message));
      } finally {
          setUploading(false);
      }
  };

  const handleDelete = async (id) => {
      if(!window.confirm("Delete?")) return;
      try {
          await axios.delete(`${API_URL}/admin/sources/${id}`, { headers: getHeaders() });
          setSources(prev => prev.filter(s => s.id !== id));
      } catch (e) {
          alert("Delete failed");
      }
  };

  const Content = (
    <div className={`${isModal ? 'max-w-lg' : 'max-w-5xl'} w-full mx-auto`}>
      {/* Glass morphism container with gradient border */}
      <div className="relative rounded-3xl overflow-hidden">
        {/* Animated gradient border */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 opacity-10 blur-xl"></div>
        
        <div className={`relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl ${isModal ? 'shadow-2xl max-h-[90vh]' : 'shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]'} overflow-hidden flex flex-col border border-slate-700/50`}>
          
          {/* Header with glow effect */}
          <div className="p-5 border-b border-slate-700/50 flex justify-between items-center bg-gradient-to-r from-slate-800/80 to-slate-900/80 backdrop-blur-xl">
            <h2 className="font-bold text-white flex items-center gap-3 text-lg">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg shadow-purple-500/30">
                <Lock size={20} className="text-white" />
              </div>
              <span className="bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                {t('adminLogin')}
              </span>
            </h2>
            {isModal && (
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-xl transition-all duration-300">
                <X size={20} />
              </button>
            )}
          </div>
          
          <div className="p-6 overflow-y-auto bg-gradient-to-b from-slate-900/50 to-slate-800/50">
              {!isAuthenticated ? (
                  <form onSubmit={handleLogin} className="flex flex-col gap-4">
                      <div className="text-center mb-6">
                          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/40 hover:shadow-purple-500/60 transition-all duration-500 hover:scale-105">
                              <Lock size={36} className="text-white" />
                          </div>
                          <p className="text-slate-400">{t('adminDesc')}</p>
                      </div>
                      <input 
                          type="password" 
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="******"
                          className="w-full p-4 bg-slate-800/50 border border-slate-600/50 rounded-2xl text-center font-bold text-lg text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 outline-none transition-all duration-300 placeholder-slate-500"
                          autoFocus
                      />
                      <button type="submit" className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl font-bold hover:from-purple-500 hover:to-pink-500 transition-all duration-300 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-[1.02] active:scale-[0.98]">
                          Enter
                      </button>
                  </form>
              ) : (
                  <div className="space-y-6">
                      {/* Modern Glowing Tabs */}
                      <div className="flex bg-slate-800/50 p-2 rounded-2xl flex-wrap gap-1 border border-slate-700/50 backdrop-blur-sm">
                          <button
                              onClick={() => { setActiveTab("payments"); fetchPaymentData(); }}
                              className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "payments" 
                                ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg shadow-green-500/40 scale-[1.02]" 
                                : "text-slate-400 hover:text-white hover:bg-slate-700/50"}`}
                              data-testid="payments-tab-btn"
                          >
                              <CreditCard size={16} />
                              Payments
                          </button>
                          <button
                              onClick={() => { setActiveTab("questions"); fetchQuestions(); }}
                              className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "questions" 
                                ? "bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/40 scale-[1.02]" 
                                : "text-slate-400 hover:text-white hover:bg-slate-700/50"}`}
                              data-testid="questions-tab-btn"
                          >
                              <Globe size={16} />
                              Questions
                          </button>
                      <button
                          onClick={() => { setActiveTab("feedback"); fetchFeedbacks(); }}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "feedback" 
                            ? "bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-lg shadow-violet-500/40 scale-[1.02]" 
                            : "text-slate-400 hover:text-white hover:bg-slate-700/50"}`}
                      >
                          <MessageCircle size={16} />
                          Feedback
                      </button>
                      <button
                          onClick={() => { setActiveTab("analytics"); fetchAnalytics(); fetchEnhancedAnalytics(); fetchPaymentData(); }}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "analytics" 
                            ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/40 scale-[1.02]" 
                            : "text-slate-400 hover:text-white hover:bg-slate-700/50"}`}
                      >
                          <Users size={16} />
                          Users
                      </button>
                      <button
                          onClick={() => { setActiveTab("status"); fetchStats(); }}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "status" 
                            ? "bg-gradient-to-r from-sky-500 to-blue-500 text-white shadow-lg shadow-sky-500/40 scale-[1.02]" 
                            : "text-slate-400 hover:text-white hover:bg-slate-700/50"}`}
                      >
                          <Activity size={16} />
                          Status
                      </button>
                      <button
                          onClick={() => setActiveTab("youtube")}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "youtube" 
                            ? "bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg shadow-red-500/40 scale-[1.02]" 
                            : "text-slate-400 hover:text-white hover:bg-slate-700/50"}`}
                      >
                          <Youtube size={16} />
                          {t('youtubeTab')}
                      </button>
                      <button
                          onClick={() => setActiveTab("gdrive")}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "gdrive" 
                            ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/40 scale-[1.02]" 
                            : "text-slate-400 hover:text-white hover:bg-slate-700/50"}`}
                      >
                          <HardDrive size={16} />
                          Drive
                      </button>
                      <button
                          onClick={() => setActiveTab("audio")}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "audio" 
                            ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/40 scale-[1.02]" 
                            : "text-slate-400 hover:text-white hover:bg-slate-700/50"}`}
                      >
                          <Mic size={16} />
                          Audio
                      </button>
                          <button
                              onClick={() => setActiveTab("file")}
                              className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "file" 
                                ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/40 scale-[1.02]" 
                                : "text-slate-400 hover:text-white hover:bg-slate-700/50"}`}
                          >
                              <FileText size={16} />
                              {t('fileTab')}
                          </button>
                      <button
                          onClick={() => { setActiveTab("backup"); }}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "backup" 
                            ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/40 scale-[1.02]" 
                            : "text-slate-400 hover:text-white hover:bg-slate-700/50"}`}
                          data-testid="backup-tab-btn"
                      >
                          <Database size={16} />
                          Backup
                      </button>
                  </div>

                  {activeTab === "analytics" && (
                      <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700/50 space-y-4 backdrop-blur-sm">
                          <div className="flex justify-between items-center">
                              <h3 className="font-bold text-white flex items-center gap-2">
                                  <Activity size={18} className="text-indigo-400" /> Enhanced Analytics
                              </h3>
                              <select 
                                  value={analyticsDays} 
                                  onChange={(e) => { setAnalyticsDays(Number(e.target.value)); fetchEnhancedAnalytics(Number(e.target.value)); }}
                                  className="px-4 py-2 bg-slate-700 border border-indigo-500/50 rounded-xl text-sm text-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 cursor-pointer"
                              >
                                  <option value={1}>Last 24h</option>
                                  <option value={7}>Last 7 days</option>
                                  <option value={30}>Last 30 days</option>
                              </select>
                          </div>
                          
                          {!enhancedAnalytics && !analytics ? (
                              <div className="text-center py-8">
                                  <Loader2 className="animate-spin mx-auto text-indigo-400" size={32} />
                                  <p className="text-sm text-indigo-400/70 mt-2">Loading analytics...</p>
                              </div>
                          ) : (
                              <>
                                  {/* Subscription Stats */}
                                  {enhancedAnalytics && (
                                      <div className="bg-gradient-to-r from-purple-500/20 to-indigo-600/20 p-5 rounded-2xl border-2 border-purple-500/30 shadow-lg shadow-purple-500/20">
                                          <p className="text-xs text-purple-400 mb-3 font-bold">📊 Subscription Overview</p>
                                          <div className="grid grid-cols-5 gap-3">
                                              <div className="text-center bg-slate-800/50 p-3 rounded-xl border border-slate-600/50">
                                                  <p className="text-2xl font-bold text-white">{enhancedAnalytics.subscriptions?.total_users || 0}</p>
                                                  <p className="text-[10px] text-slate-400">Total Users</p>
                                              </div>
                                              <div className="text-center bg-slate-800/50 p-3 rounded-xl border border-gray-500/30">
                                                  <p className="text-2xl font-bold text-gray-400">{enhancedAnalytics.subscriptions?.no_plan_users || 0}</p>
                                                  <p className="text-[10px] text-slate-400">No Plan</p>
                                              </div>
                                              <div className="text-center bg-slate-800/50 p-3 rounded-xl border border-green-500/30">
                                                  <p className="text-2xl font-bold text-green-400">{enhancedAnalytics.subscriptions?.basic_users || 0}</p>
                                                  <p className="text-[10px] text-slate-400">Basic ($10)</p>
                                              </div>
                                              <div className="text-center bg-slate-800/50 p-3 rounded-xl border border-pink-500/30">
                                                  <p className="text-2xl font-bold text-pink-400">{enhancedAnalytics.subscriptions?.pro_users || 0}</p>
                                                  <p className="text-[10px] text-slate-400">Pro ($20)</p>
                                              </div>
                                              <div className="text-center bg-slate-800/50 p-3 rounded-xl border border-purple-500/30">
                                                  <p className="text-2xl font-bold text-purple-400">{enhancedAnalytics.subscriptions?.gift_users || 0}</p>
                                                  <p className="text-[10px] text-slate-400">🎁 Gift</p>
                                              </div>
                                          </div>
                                      </div>
                                  )}

                                  {/* Revenue Card */}
                                  {enhancedAnalytics && (
                                      <div className="bg-gradient-to-r from-green-500/20 to-emerald-600/20 p-5 rounded-2xl border-2 border-green-500/30 shadow-lg shadow-green-500/20">
                                          <p className="text-xs text-green-400 mb-3 font-bold">💰 Revenue ({analyticsDays} days)</p>
                                          <div className="flex justify-between items-center">
                                              <div>
                                                  <p className="text-4xl font-bold text-green-400">${enhancedAnalytics.revenue?.total?.toFixed(2) || '0.00'}</p>
                                                  <p className="text-xs text-green-400/70">{enhancedAnalytics.revenue?.transaction_count || 0} transactions</p>
                                              </div>
                                              <div className="p-4 bg-green-500/20 rounded-2xl">
                                                  <CreditCard size={40} className="text-green-400" />
                                              </div>
                                          </div>
                                      </div>
                                  )}

                                  {/* Basic Stats Grid */}
                                  {analytics && (
                                      <div className="grid grid-cols-2 gap-3">
                                          <div className="bg-slate-700/50 p-4 rounded-2xl border-l-4 border-indigo-500 hover:border-l-indigo-400 transition-all duration-300">
                                              <p className="text-xs text-slate-400">Total Devices</p>
                                              <p className="text-2xl font-bold text-indigo-400">{analytics.total_devices}</p>
                                          </div>
                                          <div className="bg-slate-700/50 p-4 rounded-2xl border-l-4 border-green-500 hover:border-l-green-400 transition-all duration-300">
                                              <p className="text-xs text-slate-400">Total Sessions</p>
                                              <p className="text-2xl font-bold text-green-400">{analytics.total_sessions}</p>
                                          </div>
                                          <div className="bg-slate-700/50 p-4 rounded-2xl border-l-4 border-blue-500 hover:border-l-blue-400 transition-all duration-300">
                                              <p className="text-xs text-slate-400">Total Messages</p>
                                              <p className="text-2xl font-bold text-blue-400">{analytics.total_messages}</p>
                                          </div>
                                          <div className="bg-slate-700/50 p-4 rounded-2xl border-l-4 border-orange-500 hover:border-l-orange-400 transition-all duration-300">
                                              <p className="text-xs text-slate-400">Avg/Day</p>
                                              <p className="text-2xl font-bold text-orange-600">{enhancedAnalytics?.engagement?.avg_questions_per_day || 0}</p>
                                          </div>
                                      </div>
                                  )}

                                  {/* Geographic Distribution */}
                                  {enhancedAnalytics?.geographic?.top_countries && (
                                      <div className="bg-slate-700/50 p-5 rounded-2xl border border-slate-600/50">
                                          <p className="text-xs font-bold text-indigo-400 mb-4 flex items-center gap-2">
                                              <Globe size={14} /> Geographic Distribution
                                          </p>
                                          <div className="space-y-3">
                                              {enhancedAnalytics.geographic.top_countries.slice(0, 8).map((c, idx) => (
                                                  <div key={idx} className="flex items-center justify-between hover:bg-slate-600/30 p-2 rounded-xl transition-all duration-300">
                                                      <div className="flex items-center gap-2">
                                                          <span className="text-lg">{getCountryFlag(c._id)}</span>
                                                          <span className="text-sm text-slate-300">{c.country}</span>
                                                      </div>
                                                      <div className="flex items-center gap-2">
                                                          <div className="w-24 bg-slate-600 rounded-full h-2">
                                                              <div 
                                                                  className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full" 
                                                                  style={{width: `${Math.min((c.count / (enhancedAnalytics.geographic.top_countries[0]?.count || 1)) * 100, 100)}%`}}
                                                              ></div>
                                                          </div>
                                                          <span className="text-xs font-bold text-indigo-400 w-8 text-right">{c.count}</span>
                                                      </div>
                                                  </div>
                                              ))}
                                          </div>
                                      </div>
                                  )}

                                  {/* Peak Hours */}
                                  {enhancedAnalytics?.peak_hours && enhancedAnalytics.peak_hours.length > 0 && (
                                      <div className="bg-slate-700/50 p-5 rounded-2xl border border-slate-600/50">
                                          <p className="text-xs font-bold text-indigo-400 mb-4">⏰ Activity by Hour (UTC)</p>
                                          <div className="flex items-end gap-1 h-20">
                                              {Array.from({length: 24}, (_, i) => {
                                                  const hourStr = i.toString().padStart(2, '0');
                                                  const hourData = enhancedAnalytics.peak_hours.find(h => h._id === hourStr);
                                                  const count = hourData?.count || 0;
                                                  const maxCount = Math.max(...enhancedAnalytics.peak_hours.map(h => h.count));
                                                  const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                                                  return (
                                                      <div key={i} className="flex-1 flex flex-col items-center" title={`${hourStr}:00 - ${count} questions`}>
                                                          <div 
                                                              className="w-full bg-gradient-to-t from-indigo-600 to-purple-500 rounded-t transition-all hover:from-indigo-500 hover:to-purple-400"
                                                              style={{height: `${Math.max(height, 4)}%`}}
                                                          ></div>
                                                          {i % 4 === 0 && <span className="text-[8px] text-slate-500 mt-1">{hourStr}</span>}
                                                      </div>
                                                  );
                                              })}
                                          </div>
                                      </div>
                                  )}

                                  {/* Today's Stats */}
                                  {analytics && (
                                      <div className="bg-slate-700/50 p-5 rounded-2xl border border-slate-600/50">
                                          <p className="text-xs font-bold text-indigo-400 mb-4">📅 Today's Activity</p>
                                          <div className="grid grid-cols-4 gap-3 text-center">
                                              <div className="bg-slate-800/50 p-3 rounded-xl border border-indigo-500/30">
                                                  <p className="text-lg font-bold text-indigo-400">{analytics.unique_devices_today}</p>
                                                  <p className="text-[10px] text-slate-500">New Devices</p>
                                              </div>
                                              <div className="bg-slate-800/50 p-3 rounded-xl border border-green-500/30">
                                                  <p className="text-lg font-bold text-green-400">{analytics.unique_ips_today}</p>
                                                  <p className="text-[10px] text-slate-500">New IPs</p>
                                              </div>
                                              <div className="bg-slate-800/50 p-3 rounded-xl border border-blue-500/30">
                                                  <p className="text-lg font-bold text-blue-400">{analytics.sessions_today}</p>
                                                  <p className="text-[10px] text-slate-500">Sessions</p>
                                              </div>
                                              <div className="bg-slate-800/50 p-3 rounded-xl border border-orange-500/30">
                                                  <p className="text-lg font-bold text-orange-400">{analytics.messages_today}</p>
                                                  <p className="text-[10px] text-slate-500">Messages</p>
                                              </div>
                                          </div>
                                      </div>
                                  )}

                                  <button onClick={() => { fetchAnalytics(); fetchEnhancedAnalytics(); }} className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-sm font-bold hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 shadow-lg shadow-indigo-500/30">
                                      Refresh Analytics
                                  </button>

                                  {/* SEARCH & MANAGE USERS - Collapsible */}
                                  <div className="rounded-2xl border border-slate-600/50 overflow-hidden">
                                      <button onClick={() => toggleSection('searchUsers')} className="w-full flex items-center justify-between p-4 bg-slate-700/50 hover:bg-slate-700/70 transition-all duration-200" data-testid="toggle-search-users">
                                          <h4 className="font-bold text-white flex items-center gap-2 text-base">Search & Manage Users</h4>
                                          {collapsedSections.searchUsers ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronUp size={18} className="text-slate-400" />}
                                      </button>
                                      {!collapsedSections.searchUsers && (
                                          <div className="p-4">
                                              <div className="flex gap-2 mb-4">
                                                  <input
                                                      type="text"
                                                      value={userSearchQuery}
                                                      onChange={(e) => { setUserSearchQuery(e.target.value); searchUsers(e.target.value); }}
                                                      placeholder="Search by email or name..."
                                                      className="flex-1 px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all duration-300"
                                                      data-testid="user-search-input"
                                                  />
                                              </div>
                                              {searchResults.length > 0 && (
                                                  <div className="border border-slate-600/50 rounded-xl divide-y divide-slate-700/50 max-h-48 overflow-y-auto mb-4">
                                                      {searchResults.map((user, idx) => (
                                                          <div key={idx} className="p-3 hover:bg-slate-600/30 flex justify-between items-center transition-all duration-300">
                                                              <div>
                                                                  <p className="font-medium text-white">{user.name}</p>
                                                                  <p className="text-xs text-slate-400">{user.email}</p>
                                                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                                      user.subscription_plan === 'ultimate' ? 'bg-amber-500/20 text-amber-400' :
                                                                      user.subscription_plan === 'pro' ? 'bg-purple-500/20 text-purple-400' :
                                                                      user.subscription_plan === 'basic' ? 'bg-blue-500/20 text-blue-400' :
                                                                      'bg-slate-600 text-slate-400'
                                                                  }`}>
                                                                      {user.subscription_plan || 'none'}
                                                                  </span>
                                                              </div>
                                                              <div className="flex gap-2">
                                                                  <button onClick={() => { setSelectedUser(user); setUpgradeModalOpen(true); }} className="px-3 py-1 bg-green-500 text-white text-xs rounded-lg hover:bg-green-600" data-testid="upgrade-user-btn">Upgrade</button>
                                                                  <button onClick={() => handleGrantGift(user)} className="px-3 py-1 bg-purple-500 text-white text-xs rounded-lg hover:bg-purple-600" title="Grant Gift Questions">Gift</button>
                                                                  <button onClick={() => handleResetQuestions(user)} className="px-3 py-1 bg-blue-500 text-white text-xs rounded-lg hover:bg-blue-600">Reset Q</button>
                                                                  {user.subscription_plan && user.subscription_plan !== 'none' && (
                                                                      <button onClick={() => handleDowngradeUser(user)} className="px-3 py-1 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600">Remove Plan</button>
                                                                  )}
                                                                  <button data-testid={`repair-user-${user.user_id}`} onClick={() => handleRepairUser(user)} className="px-3 py-1 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-700" title="Repair User">Repair</button>
                                                                  <button onClick={() => handleDeleteUser(user)} className="px-3 py-1 bg-red-700 text-white text-xs rounded-lg hover:bg-red-800" title="Delete User Permanently">Delete</button>
                                                              </div>
                                                          </div>
                                                      ))}
                                                  </div>
                                              )}
                                          </div>
                                      )}
                                  </div>

                                  {/* USER DIAGNOSIS - Collapsible */}
                                  <div className="rounded-2xl border border-slate-600/50 overflow-hidden">
                                      <button onClick={() => toggleSection('diagnose')} className="w-full flex items-center justify-between p-4 bg-slate-700/50 hover:bg-slate-700/70 transition-all duration-200" data-testid="toggle-diagnose">
                                          <h4 className="font-bold text-white flex items-center gap-2 text-base">Comprehensive User Diagnosis</h4>
                                          {collapsedSections.diagnose ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronUp size={18} className="text-slate-400" />}
                                      </button>
                                      {!collapsedSections.diagnose && (
                                          <div className="p-4" data-testid="diagnose-tool">
                                              <div className="flex gap-2">
                                                  <input type="email" value={diagnoseEmail} onChange={e => setDiagnoseEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleDiagnoseUser()} placeholder="user@email.com" className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-[#d4a574]" data-testid="diagnose-email-input" />
                                                  <button onClick={handleDiagnoseUser} className="px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 font-medium" data-testid="diagnose-btn">Diagnose</button>
                                              </div>
                                              {diagnoseResult && (
                                                  <div className="mt-3 p-3 bg-slate-800 rounded-xl text-xs">
                                                      {diagnoseResult.error ? (
                                                          <p className="text-red-400">{diagnoseResult.error}</p>
                                                      ) : !diagnoseResult.found ? (
                                                          <p className="text-yellow-400">User not found: {diagnoseResult.email}</p>
                                                      ) : (
                                                          <div className="space-y-3">
                                                              <div className="border-b border-slate-700 pb-2">
                                                                  <p className="text-white font-bold text-sm">{diagnoseResult.name} ({diagnoseResult.email})</p>
                                                                  <p className="text-slate-300">Plan: <span className="text-[#d4a574] font-bold">{diagnoseResult.subscription_plan}</span> | Questions: {diagnoseResult.questions_used}/{diagnoseResult.questions_limit}</p>
                                                                  <p className="text-slate-300">Sessions: <span className={diagnoseResult.active_sessions > 0 ? 'text-green-400' : 'text-red-400'}>{diagnoseResult.active_sessions} active</span> / {diagnoseResult.sessions_count} total</p>
                                                              </div>
                                                              <div>
                                                                  <p className="text-slate-400 font-bold mb-1">General Issues:</p>
                                                                  {diagnoseResult.issues?.map((issue, i) => (
                                                                      <p key={i} className={issue.includes('No issues') ? 'text-green-400' : 'text-red-400'}>{issue.includes('No issues') ? '✅' : '⚠️'} {issue}</p>
                                                                  ))}
                                                              </div>
                                                              {diagnoseResult.geo_issues && diagnoseResult.geo_issues.length > 0 && (
                                                                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2" data-testid="geo-issues">
                                                                      <p className="text-cyan-400 font-bold mb-1">Geo-Location / Region Issues:</p>
                                                                      {diagnoseResult.geo_issues.map((issue, i) => (
                                                                          <p key={i} className={
                                                                              issue.includes('no geo-specific issues') || issue.includes('All ') && !issue.includes('ALERT') && !issue.includes('MULTI') && !issue.includes('FAILED') ? 'text-green-400' 
                                                                              : issue.includes('REGION ALERT') || issue.includes('VPN/PROXY') || issue.includes('HOSTING') ? 'text-red-400' 
                                                                              : issue.includes('MULTI-COUNTRY') || issue.includes('NO IP DATA') || issue.includes('GEO LOOKUP') ? 'text-yellow-400' : 'text-cyan-300'
                                                                          }>{issue.includes('no geo-specific') ? '✅' : issue.includes('REGION ALERT') || issue.includes('VPN') || issue.includes('HOSTING') ? '🚨' : issue.includes('MULTI') ? '🔀' : 'i'} {issue}</p>
                                                                      ))}
                                                                  </div>
                                                              )}
                                                              {diagnoseResult.country_breakdown && Object.keys(diagnoseResult.country_breakdown).length > 0 && (
                                                                  <div data-testid="country-breakdown">
                                                                      <p className="text-slate-400 font-bold text-[10px] mb-1">Countries:</p>
                                                                      <div className="flex flex-wrap gap-1.5">
                                                                          {Object.entries(diagnoseResult.country_breakdown).sort((a, b) => b[1] - a[1]).map(([country, count]) => (
                                                                              <span key={country} className="px-2 py-1 bg-cyan-500/15 border border-cyan-500/30 rounded-md text-cyan-300 text-[10px]">{country}: <span className="font-bold text-white">{count}</span></span>
                                                                          ))}
                                                                      </div>
                                                                  </div>
                                                              )}
                                                              {((diagnoseResult.city_breakdown && Object.keys(diagnoseResult.city_breakdown).length > 0) || (diagnoseResult.isp_breakdown && Object.keys(diagnoseResult.isp_breakdown).length > 0)) && (
                                                                  <div className="grid grid-cols-2 gap-2">
                                                                      {diagnoseResult.city_breakdown && Object.keys(diagnoseResult.city_breakdown).length > 0 && (
                                                                          <div>
                                                                              <p className="text-slate-400 font-bold text-[10px] mb-1">Cities:</p>
                                                                              {Object.entries(diagnoseResult.city_breakdown).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([city, count]) => (
                                                                                  <p key={city} className="text-slate-300 text-[10px]">{city}: <span className="text-cyan-400">{count}</span></p>
                                                                              ))}
                                                                          </div>
                                                                      )}
                                                                      {diagnoseResult.isp_breakdown && Object.keys(diagnoseResult.isp_breakdown).length > 0 && (
                                                                          <div>
                                                                              <p className="text-slate-400 font-bold text-[10px] mb-1">ISPs:</p>
                                                                              {Object.entries(diagnoseResult.isp_breakdown).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([isp, count]) => (
                                                                                  <p key={isp} className="text-slate-300 text-[10px] truncate" title={isp}>{isp}: <span className="text-cyan-400">{count}</span></p>
                                                                              ))}
                                                                          </div>
                                                                      )}
                                                                  </div>
                                                              )}
                                                              {(diagnoseResult.proxy_sessions > 0 || diagnoseResult.hosting_sessions > 0) && (
                                                                  <div className="flex gap-2">
                                                                      {diagnoseResult.proxy_sessions > 0 && (<span className="px-2 py-1 bg-red-500/20 border border-red-500/30 rounded-md text-red-400 text-[10px]">VPN/Proxy: {diagnoseResult.proxy_sessions} session(s)</span>)}
                                                                      {diagnoseResult.hosting_sessions > 0 && (<span className="px-2 py-1 bg-yellow-500/20 border border-yellow-500/30 rounded-md text-yellow-400 text-[10px]">Hosting/DC: {diagnoseResult.hosting_sessions} session(s)</span>)}
                                                                  </div>
                                                              )}
                                                              {diagnoseResult.mobile_issues && (
                                                                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-2">
                                                                      <p className="text-orange-400 font-bold mb-1">Mobile/Tablet Issues:</p>
                                                                      {diagnoseResult.mobile_issues.map((issue, i) => (
                                                                          <p key={i} className={issue.includes('No mobile-specific issues') || issue.includes('sessions:') ? 'text-green-400' : issue.includes('NO MOBILE') || issue.includes('DESKTOP ONLY') || issue.includes('IN-APP') || issue.includes('ALL EXPIRED') ? 'text-red-400' : 'text-orange-300'}>
                                                                              {issue.includes('No mobile') ? '✅' : issue.includes('sessions:') ? '📊' : '⚠️'} {issue}
                                                                          </p>
                                                                      ))}
                                                                  </div>
                                                              )}
                                                              {diagnoseResult.device_breakdown && (
                                                                  <div className="grid grid-cols-4 gap-2">
                                                                      <div className="bg-blue-500/10 p-2 rounded-lg text-center border border-blue-500/20"><p className="text-blue-400 font-bold text-lg">{diagnoseResult.device_breakdown.desktop || 0}</p><p className="text-slate-500 text-[10px]">Desktop</p></div>
                                                                      <div className="bg-green-500/10 p-2 rounded-lg text-center border border-green-500/20"><p className="text-green-400 font-bold text-lg">{diagnoseResult.device_breakdown.mobile || 0}</p><p className="text-slate-500 text-[10px]">Mobile</p></div>
                                                                      <div className="bg-purple-500/10 p-2 rounded-lg text-center border border-purple-500/20"><p className="text-purple-400 font-bold text-lg">{diagnoseResult.device_breakdown.tablet || 0}</p><p className="text-slate-500 text-[10px]">Tablet</p></div>
                                                                      <div className="bg-red-500/10 p-2 rounded-lg text-center border border-red-500/20"><p className="text-red-400 font-bold text-lg">{diagnoseResult.in_app_sessions || 0}</p><p className="text-slate-500 text-[10px]">In-App</p></div>
                                                                  </div>
                                                              )}
                                                              {diagnoseResult.browser_breakdown && Object.keys(diagnoseResult.browser_breakdown).length > 0 && (
                                                                  <div className="grid grid-cols-2 gap-2">
                                                                      <div><p className="text-slate-400 font-bold text-[10px] mb-1">Browsers:</p>{Object.entries(diagnoseResult.browser_breakdown).map(([k, v]) => (<p key={k} className="text-slate-300">{k}: <span className="text-[#d4a574]">{v}</span></p>))}</div>
                                                                      <div><p className="text-slate-400 font-bold text-[10px] mb-1">OS:</p>{Object.entries(diagnoseResult.os_breakdown || {}).map(([k, v]) => (<p key={k} className="text-slate-300">{k}: <span className="text-[#d4a574]">{v}</span></p>))}</div>
                                                                  </div>
                                                              )}
                                                              {diagnoseResult.sessions && diagnoseResult.sessions.length > 0 && (
                                                                  <div>
                                                                      <p className="text-slate-400 font-bold text-[10px] mb-1">Recent Sessions (latest first):</p>
                                                                      <div className="max-h-48 overflow-y-auto space-y-1">
                                                                          {diagnoseResult.sessions.slice(0, 8).map((s, i) => (
                                                                              <div key={i} className={`p-2 rounded-lg text-[10px] ${s.is_expired ? 'bg-red-500/10 border border-red-500/20' : 'bg-green-500/10 border border-green-500/20'}`}>
                                                                                  <div className="flex justify-between items-center">
                                                                                      <span className={s.is_expired ? 'text-red-400' : 'text-green-400'}>{s.is_expired ? 'Expired' : 'Active'}</span>
                                                                                      <span className="text-slate-500">{s.created_at ? new Date(s.created_at).toLocaleString() : 'N/A'}</span>
                                                                                  </div>
                                                                                  <p className="text-slate-300">{s.device_type || 'unknown'} | {s.browser || 'unknown'} | {s.os || 'unknown'}{s.is_in_app_browser && <span className="text-red-400 ml-1">[IN-APP]</span>}{s.is_proxy && <span className="text-red-400 ml-1">[VPN/PROXY]</span>}{s.is_hosting && <span className="text-yellow-400 ml-1">[HOSTING]</span>}</p>
                                                                                  <p className="text-slate-400">{s.country && s.country !== 'Unknown' ? `${s.city ? s.city + ', ' : ''}${s.country}` : ''}{s.isp ? ` — ${s.isp}` : ''}</p>
                                                                                  {s.login_ip && s.login_ip !== 'unknown' && (<p className="text-slate-500">IP: {s.login_ip}</p>)}
                                                                              </div>
                                                                          ))}
                                                                      </div>
                                                                  </div>
                                                              )}
                                                          </div>
                                                      )}
                                                  </div>
                                              )}
                                          </div>
                                      )}
                                  </div>

                                  {/* ALL REGISTERED USERS - Collapsible */}
                                  <div className="rounded-2xl border border-slate-600/50 overflow-hidden">
                                      <button onClick={() => { toggleSection('allUsers'); if (!paymentUsers.length) fetchPaymentData(); }} className="w-full flex items-center justify-between p-4 bg-slate-700/50 hover:bg-slate-700/70 transition-all duration-200" data-testid="toggle-all-users">
                                          <h4 className="font-bold text-white flex items-center gap-2 text-base">All Registered Users ({paymentUsers.length})</h4>
                                          {collapsedSections.allUsers ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronUp size={18} className="text-slate-400" />}
                                      </button>
                                      {!collapsedSections.allUsers && (
                                          <div className="p-4 space-y-3">
                                              {/* Controls Bar */}
                                              <div className="flex items-center gap-2 flex-wrap">
                                                  <input
                                                      type="text"
                                                      value={userListSearch || ''}
                                                      onChange={(e) => setUserListSearch(e.target.value)}
                                                      placeholder="Search users..."
                                                      className="flex-1 min-w-[150px] px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                                      data-testid="user-list-search"
                                                  />
                                                  <select value={userFilter} onChange={(e) => { setUserFilter(e.target.value); setSelectedUserIds(new Set()); }} className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-xs text-white focus:outline-none" data-testid="user-filter-select">
                                                      <option value="all">All</option>
                                                      <option value="paid">Paid</option>
                                                      <option value="none">No Plan</option>
                                                      <option value="gift">Gift</option>
                                                      <option value="basic">Basic</option>
                                                      <option value="pro">Pro</option>
                                                      <option value="ultimate">Ultimate</option>
                                                      <option value="premium">Premium</option>
                                                  </select>
                                                  <select value={userSort || 'newest'} onChange={(e) => setUserSort(e.target.value)} className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-xs text-white focus:outline-none" data-testid="user-sort-select">
                                                      <option value="newest">Newest First</option>
                                                      <option value="oldest">Oldest First</option>
                                                      <option value="most_paid">Most Paid</option>
                                                      <option value="most_questions">Most Questions Used</option>
                                                      <option value="name_az">Name A-Z</option>
                                                  </select>
                                                  <button data-testid="repair-all-users-btn" onClick={handleRepairAllUsers} className="px-3 py-2 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-700 font-medium">Repair All</button>
                                              </div>

                                              {/* Select All + Bulk Actions */}
                                              <div className="flex items-center justify-between bg-slate-800/50 rounded-xl px-3 py-2 border border-slate-600/30">
                                                  <label className="flex items-center gap-2 cursor-pointer">
                                                      <input type="checkbox" checked={getSortedFilteredUsers().length > 0 && getSortedFilteredUsers().every(u => selectedUserIds.has(u.user_id))} onChange={(e) => handleSelectAll(e.target.checked)} className="w-4 h-4 rounded accent-[#d4a574]" data-testid="select-all-users-checkbox" />
                                                      <span className="text-xs text-slate-300">Select All ({getSortedFilteredUsers().length} shown)</span>
                                                  </label>
                                                  {selectedUserIds.size > 0 && (
                                                      <button onClick={handleBulkDelete} className="px-3 py-1.5 bg-red-700 text-white text-xs rounded-lg hover:bg-red-800 font-medium" data-testid="bulk-delete-btn">Delete Selected ({selectedUserIds.size})</button>
                                                  )}
                                              </div>

                                              {/* User Table */}
                                              <div className="overflow-x-auto">
                                                  <table className="w-full text-sm">
                                                      <thead>
                                                          <tr className="border-b border-slate-600/50">
                                                              <th className="py-2 px-2 w-8"></th>
                                                              <th className="text-right text-slate-400 font-medium py-2 px-2 text-xs">User</th>
                                                              <th className="text-right text-slate-400 font-medium py-2 px-2 text-xs">Plan</th>
                                                              <th className="text-right text-slate-400 font-medium py-2 px-2 text-xs">Questions</th>
                                                              <th className="text-right text-slate-400 font-medium py-2 px-2 text-xs">Paid</th>
                                                              <th className="text-right text-slate-400 font-medium py-2 px-2 text-xs">Registered</th>
                                                              <th className="text-right text-slate-400 font-medium py-2 px-2 text-xs">Actions</th>
                                                          </tr>
                                                      </thead>
                                                      <tbody>
                                                          {getSortedFilteredUsers().map((user, idx) => {
                                                              const limit = user.questions_limit || 0;
                                                              const used = user.questions_used || 0;
                                                              const remaining = Math.max(0, limit - used);
                                                              const regDate = user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
                                                              return (
                                                                  <tr key={idx} className="border-b border-slate-700/30 hover:bg-slate-700/30 transition-colors">
                                                                      <td className="py-2 px-2">
                                                                          <input type="checkbox" checked={selectedUserIds.has(user.user_id)} onChange={() => toggleUserSelection(user.user_id)} className="w-4 h-4 rounded accent-[#d4a574]" />
                                                                      </td>
                                                                      <td className="py-2 px-2 text-right">
                                                                          <p className="font-medium text-white text-sm">{user.name || 'Unknown'}</p>
                                                                          <p className="text-xs text-slate-500">{user.email}</p>
                                                                      </td>
                                                                      <td className="py-2 px-2 text-right">
                                                                          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                                                                              user.subscription_plan === 'gift' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                                                                              user.subscription_plan === 'premium' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                                                                              user.subscription_plan === 'ultimate' ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30' :
                                                                              user.subscription_plan === 'pro' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                                                                              user.subscription_plan === 'basic' ? 'bg-slate-600/30 text-slate-300 border border-slate-500/30' :
                                                                              'bg-red-500/10 text-red-400 border border-red-500/20'
                                                                          }`}>{user.subscription_plan === 'gift' ? 'Gift' : user.subscription_plan || 'none'}</span>
                                                                      </td>
                                                                      <td className="py-2 px-2 text-right">
                                                                          <p className="text-xs text-white font-medium">{used} / {limit}</p>
                                                                          <p className={`text-xs ${remaining > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{remaining} left</p>
                                                                      </td>
                                                                      <td className="py-2 px-2 text-right">
                                                                          {user.total_paid > 0 
                                                                              ? <span className="text-emerald-400 font-bold text-xs">${user.total_paid}</span>
                                                                              : <span className="text-slate-500 text-xs">$0</span>
                                                                          }
                                                                      </td>
                                                                      <td className="py-2 px-2 text-right">
                                                                          <span className="text-xs text-slate-400">{regDate}</span>
                                                                      </td>
                                                                      <td className="py-2 px-2 text-right">
                                                                          <div className="flex gap-1 justify-end">
                                                                              <button onClick={() => { setSelectedUser(user); setUpgradeModalOpen(true); }} className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors" title="Upgrade"><ChevronUp size={12} /></button>
                                                                              <button onClick={() => handleGrantGift(user)} className="p-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors" title="Gift"><Star size={12} /></button>
                                                                              <button onClick={() => handleResetQuestions(user)} className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors" title="Reset Q"><RefreshCw size={12} /></button>
                                                                              <button onClick={() => handleRepairUser(user)} className="p-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-500 transition-colors" title="Repair">Fix</button>
                                                                              <button onClick={() => handleDeleteUser(user)} className="p-1.5 bg-red-700 text-white rounded-lg hover:bg-red-600 transition-colors" title="Delete"><Trash2 size={12} /></button>
                                                                          </div>
                                                                      </td>
                                                                  </tr>
                                                              );
                                                          })}
                                                      </tbody>
                                                  </table>
                                              </div>
                                          </div>
                                      )}
                                  </div>
                              </>
                          )}
                      </div>
                  )}

                  {activeTab === "feedback" && (
                      <div className="space-y-4">
                          <h3 className="font-bold text-violet-400 flex items-center gap-2">
                              <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl shadow-lg shadow-violet-500/30">
                                  <MessageCircle size={18} className="text-white" />
                              </div>
                              User Interactions
                          </h3>
                          <div className="space-y-3">
                              {feedbacks.length === 0 && <p className="text-center text-slate-500 py-8">No feedback yet</p>}
                              {feedbacks.map((fb, idx) => (
                                  <div key={idx} className="bg-slate-800/50 p-4 rounded-2xl border border-slate-700/50 hover:border-violet-500/50 transition-all duration-300">
                                      <div className="flex justify-between items-start mb-3">
                                          <div className={`px-3 py-1.5 rounded-xl text-xs font-bold ${fb.rating === 1 ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                                              {fb.rating === 1 ? '👍 Helpful' : '👎 Unhelpful'}
                                          </div>
                                          <span className="text-[10px] text-slate-500">{new Date(fb.created_at).toLocaleString()}</span>
                                      </div>
                                      
                                      <div className="space-y-3 text-sm">
                                          <div className="bg-slate-700/50 p-3 rounded-xl text-slate-300 border border-slate-600/50">
                                              <span className="font-bold text-slate-500 text-xs block mb-1">USER ASKED:</span>
                                              {fb.user_message}
                                          </div>
                                          {fb.comment && (
                                              <div className="bg-violet-500/10 p-3 rounded-xl text-violet-300 border border-violet-500/30">
                                                  <span className="font-bold text-violet-400 text-xs block mb-1">COMMENT:</span>
                                                  "{fb.comment}"
                                              </div>
                                          )}
                                      </div>
                                  </div>
                              ))}
                          </div>
                          <button onClick={fetchFeedbacks} className="w-full py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl text-sm font-bold hover:from-violet-500 hover:to-purple-500 transition-all duration-300 shadow-lg shadow-violet-500/30">
                              Refresh
                          </button>
                      </div>
                  )}

                  {activeTab === "questions" && (
                      <div className="space-y-4" data-testid="questions-panel">
                          <div className="flex justify-between items-center">
                              <h3 className="font-bold text-amber-400 flex items-center gap-2">
                                  <div className="p-2 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl shadow-lg shadow-amber-500/30">
                                      <Globe size={18} className="text-white" />
                                  </div>
                                  User Questions by Day
                              </h3>
                              <select 
                                  value={questionsDays} 
                                  onChange={(e) => { setQuestionsDays(Number(e.target.value)); fetchQuestions(Number(e.target.value)); }}
                                  className="px-4 py-2 bg-slate-700 border border-amber-500/50 rounded-xl text-sm text-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 cursor-pointer"
                                  data-testid="questions-days-select"
                              >
                                  <option value={1}>Last 24h</option>
                                  <option value={7}>Last 7 days</option>
                                  <option value={30}>Last 30 days</option>
                              </select>
                          </div>
                          
                          {!questions ? (
                              <div className="text-center py-8">
                                  <Loader2 className="animate-spin mx-auto text-amber-400" size={32} />
                                  <p className="text-sm text-amber-400/70 mt-2">Loading questions...</p>
                              </div>
                          ) : (
                              <>
                                  {/* Summary Stats */}
                                  <div className="grid grid-cols-2 gap-4 mb-4">
                                      <div className="bg-gradient-to-br from-amber-500/20 to-yellow-500/20 p-4 rounded-2xl border-2 border-amber-500/50 shadow-lg shadow-amber-500/20">
                                          <p className="text-xs text-amber-400/70">Total Questions</p>
                                          <p className="text-3xl font-bold text-amber-400">{questions.total_questions || 0}</p>
                                      </div>
                                      <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-4 rounded-2xl border-2 border-blue-500/50 shadow-lg shadow-blue-500/20">
                                          <p className="text-xs text-blue-400/70">Countries</p>
                                          <p className="text-3xl font-bold text-blue-400">{questions.country_breakdown?.length || 0}</p>
                                      </div>
                                  </div>

                                  {/* Country Breakdown */}
                                  {questions.country_breakdown && questions.country_breakdown.length > 0 && (
                                      <div className="bg-slate-800/50 p-4 rounded-2xl border border-amber-500/30 mb-4">
                                          <p className="text-xs font-bold text-amber-400 mb-3">🌍 Top Countries</p>
                                          <div className="flex flex-wrap gap-2">
                                              {questions.country_breakdown.slice(0, 8).map((c, idx) => (
                                                  <span key={idx} className="text-xs bg-slate-700/50 px-3 py-1.5 rounded-xl border border-slate-600/50 text-slate-300 hover:border-amber-500/50 transition-all duration-300">
                                                      {getCountryFlag(c._id)} {c.country} <span className="font-bold text-amber-400">({c.count})</span>
                                                  </span>
                                              ))}
                                          </div>
                                      </div>
                                  )}

                                  {/* Daily Questions */}
                                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                                      {(!questions.daily_data || questions.daily_data.length === 0) && (
                                          <p className="text-center text-slate-500 py-8">No questions in selected period</p>
                                      )}
                                      {questions.daily_data?.map((day, dayIdx) => (
                                          <div key={dayIdx} className="border-2 border-amber-500/30 rounded-2xl overflow-hidden bg-gradient-to-br from-amber-500/5 to-yellow-500/5 shadow-lg shadow-amber-500/10">
                                              <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 px-4 py-3 flex justify-between items-center border-b border-amber-500/30">
                                                  <span className="font-bold text-amber-400">{day.date}</span>
                                                  <span className="text-xs bg-gradient-to-r from-amber-500 to-yellow-500 text-black px-3 py-1.5 rounded-full font-bold shadow-lg">{day.count} questions</span>
                                              </div>
                                              <div className="divide-y divide-amber-500/10">
                                                  {day.questions.map((q, qIdx) => (
                                                      <div key={qIdx} className="p-4 hover:bg-amber-500/5 transition-all duration-300">
                                                          <div className="flex justify-between items-start mb-2">
                                                              <div className="flex items-center gap-2">
                                                                  <span className="text-lg">{getCountryFlag(q.country_code)}</span>
                                                                  <span className="text-xs text-slate-400">{q.country || 'Unknown'}</span>
                                                                  {q.city && q.city !== 'Unknown' && (
                                                                      <span className="text-[10px] text-slate-500">• {q.city}</span>
                                                                  )}
                                                              </div>
                                                              <span className="text-[10px] text-slate-500">
                                                                  {new Date(q.timestamp).toLocaleTimeString()}
                                                              </span>
                                                          </div>
                                                          <p className="text-sm text-white mb-2 bg-slate-800/50 p-3 rounded-xl border border-amber-500/20" dir="auto">{q.question}</p>
                                                          {q.answer && (
                                                              <p className="text-xs text-slate-400 italic truncate bg-slate-800/30 p-2 rounded-lg" dir="auto">
                                                                  💬 {q.answer.length > 100 ? q.answer.slice(0, 100) + '...' : q.answer}
                                                              </p>
                                                          )}
                                                      </div>
                                                  ))}
                                              </div>
                                          </div>
                                      ))}
                                  </div>

                                  <button 
                                      onClick={() => fetchQuestions(questionsDays)} 
                                      className="w-full py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-black rounded-xl text-sm font-bold hover:from-amber-400 hover:to-yellow-400 transition-all duration-300 shadow-lg shadow-amber-500/30"
                                      data-testid="refresh-questions-btn"
                                  >
                                      Refresh Questions
                                  </button>
                              </>
                          )}
                      </div>
                  )}

                  {/* PAYMENTS TAB */}
                  {activeTab === "payments" && (
                      <div className="space-y-4" data-testid="payments-panel">

                          {/* PAYMENT STATS - Collapsible */}
                          <div className="rounded-2xl border border-slate-600/50 overflow-hidden">
                              <button onClick={() => toggleSection('paymentStats')} className="w-full flex items-center justify-between p-4 bg-slate-700/50 hover:bg-slate-700/70 transition-all duration-200" data-testid="toggle-payment-stats">
                                  <h4 className="font-bold text-white flex items-center gap-2 text-base">Payment Stats & Subscriptions</h4>
                                  {collapsedSections.paymentStats ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronUp size={18} className="text-slate-400" />}
                              </button>
                              {!collapsedSections.paymentStats && (
                                  <div className="p-4 space-y-4">
                                      {paymentStats && (
                                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                              <div className="bg-green-50 p-3 rounded-xl border border-green-200">
                                                  <p className="text-xs text-green-400/80">Total Revenue</p>
                                                  <p className="text-2xl font-bold text-green-400">${paymentStats.total_revenue?.toFixed(2) || 0}</p>
                                              </div>
                                              <div className="bg-blue-500/10 p-4 rounded-xl border border-blue-500/30">
                                                  <p className="text-xs text-blue-400/80">Transactions</p>
                                                  <p className="text-2xl font-bold text-blue-400">{paymentStats.total_transactions || 0}</p>
                                              </div>
                                              <div className="bg-yellow-500/10 p-4 rounded-xl border border-yellow-500/30">
                                                  <p className="text-xs text-yellow-400/80">Pending</p>
                                                  <p className="text-2xl font-bold text-yellow-400">{paymentStats.pending_transactions || 0}</p>
                                              </div>
                                              <div className="bg-red-500/10 p-4 rounded-xl border border-red-500/30">
                                                  <p className="text-xs text-red-400/80">Failed</p>
                                                  <p className="text-2xl font-bold text-red-400">{paymentStats.failed_transactions || 0}</p>
                                              </div>
                                          </div>
                                      )}
                                      {paymentStats?.subscription_distribution && (
                                          <div className="bg-indigo-500/10 p-5 rounded-2xl border border-indigo-500/30">
                                              <h4 className="font-bold text-indigo-400 mb-4">Subscription Distribution</h4>
                                              <div className="grid grid-cols-5 gap-2">
                                                  <div className="bg-purple-500/20 p-3 rounded-xl text-center border border-purple-500/50">
                                                      <p className="text-xs text-purple-300">Gift VIP</p>
                                                      <p className="text-xl font-bold text-purple-300">{paymentStats.subscription_distribution.gift || 0}</p>
                                                  </div>
                                                  <div className="bg-slate-700/50 p-3 rounded-xl text-center border border-amber-500/30">
                                                      <p className="text-xs text-amber-400">Ultimate ($50)</p>
                                                      <p className="text-xl font-bold text-amber-400">{paymentStats.subscription_distribution.ultimate || 0}</p>
                                                  </div>
                                                  <div className="bg-slate-700/50 p-3 rounded-xl text-center border border-purple-500/30">
                                                      <p className="text-xs text-purple-400">Pro ($20)</p>
                                                      <p className="text-xl font-bold text-purple-400">{paymentStats.subscription_distribution.pro || 0}</p>
                                                  </div>
                                                  <div className="bg-slate-700/50 p-3 rounded-xl text-center border border-blue-500/30">
                                                      <p className="text-xs text-blue-400">Basic ($10)</p>
                                                      <p className="text-xl font-bold text-blue-400">{paymentStats.subscription_distribution.basic || 0}</p>
                                                  </div>
                                                  <div className="bg-slate-700/50 p-3 rounded-xl text-center border border-gray-500/30">
                                                      <p className="text-xs text-gray-400">No Plan</p>
                                                      <p className="text-xl font-bold text-gray-400">{paymentStats.subscription_distribution.none || 0}</p>
                                                  </div>
                                              </div>
                                          </div>
                                      )}
                                  </div>
                              )}
                          </div>

                          {/* MONTHLY REVENUE BREAKDOWN */}
                          {paymentStats?.monthly_revenue && paymentStats.monthly_revenue.length > 0 && (
                          <div className="rounded-2xl border border-slate-600/50 overflow-hidden">
                              <button onClick={() => toggleSection('monthlyRevenue')} className="w-full flex items-center justify-between p-4 bg-slate-700/50 hover:bg-slate-700/70 transition-all duration-200" data-testid="toggle-monthly-revenue">
                                  <h4 className="font-bold text-white flex items-center gap-2 text-base">Monthly Revenue & Purchases</h4>
                                  {collapsedSections.monthlyRevenue ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronUp size={18} className="text-slate-400" />}
                              </button>
                              {!collapsedSections.monthlyRevenue && (
                                  <div className="p-4 space-y-4">
                                      {/* Monthly Bar Chart */}
                                      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                                          <p className="text-xs text-emerald-400 font-bold mb-3">Revenue by Month</p>
                                          <div className="flex items-end gap-2 h-40">
                                              {[...paymentStats.monthly_revenue].reverse().slice(-12).map((m, idx) => {
                                                  const maxRev = Math.max(...paymentStats.monthly_revenue.map(x => x.revenue));
                                                  const pct = maxRev > 0 ? (m.revenue / maxRev) * 100 : 0;
                                                  const [yr, mo] = m._id.split('-');
                                                  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                                                  const label = monthNames[parseInt(mo) - 1] || mo;
                                                  return (
                                                      <div key={idx} className="flex-1 flex flex-col items-center group" title={`${m._id}: $${m.revenue.toFixed(0)} (${m.count} txns)`}>
                                                          <span className="text-[9px] text-emerald-400 font-bold opacity-0 group-hover:opacity-100 transition-opacity mb-1">${m.revenue.toFixed(0)}</span>
                                                          <div
                                                              className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t-lg hover:from-emerald-500 hover:to-emerald-300 transition-all cursor-pointer"
                                                              style={{ height: `${Math.max(pct, 4)}%` }}
                                                          ></div>
                                                          <span className="text-[8px] text-slate-500 mt-1">{label}</span>
                                                          <span className="text-[7px] text-slate-600">{yr.slice(2)}</span>
                                                      </div>
                                                  );
                                              })}
                                          </div>
                                      </div>

                                      {/* Monthly Table */}
                                      <div className="overflow-x-auto">
                                          <table className="w-full text-sm">
                                              <thead>
                                                  <tr className="border-b border-slate-600/50">
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Month</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Revenue</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Purchases</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Basic ($10)</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Pro ($20)</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Ultimate ($50)</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Premium ($100)</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Gift</th>
                                                  </tr>
                                              </thead>
                                              <tbody>
                                                  {paymentStats.monthly_revenue.map((m, idx) => {
                                                      const plans = paymentStats.monthly_by_plan?.[m._id] || {};
                                                      const [yr, mo] = m._id.split('-');
                                                      const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                                                      const label = `${monthNames[parseInt(mo) - 1] || mo} ${yr}`;
                                                      return (
                                                          <tr key={idx} className="border-b border-slate-700/30 hover:bg-slate-700/30 transition-colors">
                                                              <td className="py-3 px-3 text-right text-white font-medium text-xs">{label}</td>
                                                              <td className="py-3 px-3 text-right">
                                                                  <span className="text-emerald-400 font-bold">${m.revenue.toFixed(2)}</span>
                                                              </td>
                                                              <td className="py-3 px-3 text-right text-white font-medium">{m.count}</td>
                                                              <td className="py-3 px-3 text-right text-xs">
                                                                  {plans.basic ? <span className="text-slate-300">{plans.basic.count} <span className="text-emerald-400">(${plans.basic.revenue.toFixed(0)})</span></span> : <span className="text-slate-600">—</span>}
                                                              </td>
                                                              <td className="py-3 px-3 text-right text-xs">
                                                                  {plans.pro ? <span className="text-blue-300">{plans.pro.count} <span className="text-emerald-400">(${plans.pro.revenue.toFixed(0)})</span></span> : <span className="text-slate-600">—</span>}
                                                              </td>
                                                              <td className="py-3 px-3 text-right text-xs">
                                                                  {plans.ultimate ? <span className="text-violet-300">{plans.ultimate.count} <span className="text-emerald-400">(${plans.ultimate.revenue.toFixed(0)})</span></span> : <span className="text-slate-600">—</span>}
                                                              </td>
                                                              <td className="py-3 px-3 text-right text-xs">
                                                                  {plans.premium ? <span className="text-amber-300">{plans.premium.count} <span className="text-emerald-400">(${plans.premium.revenue.toFixed(0)})</span></span> : <span className="text-slate-600">—</span>}
                                                              </td>
                                                              <td className="py-3 px-3 text-right text-xs">
                                                                  {plans.gift ? <span className="text-purple-300">{plans.gift.count}</span> : <span className="text-slate-600">—</span>}
                                                              </td>
                                                          </tr>
                                                      );
                                                  })}
                                              </tbody>
                                              <tfoot>
                                                  <tr className="border-t-2 border-emerald-500/30 bg-emerald-500/5">
                                                      <td className="py-3 px-3 text-right text-emerald-400 font-bold text-xs">TOTAL</td>
                                                      <td className="py-3 px-3 text-right text-emerald-400 font-bold">${paymentStats.monthly_revenue.reduce((s, m) => s + m.revenue, 0).toFixed(2)}</td>
                                                      <td className="py-3 px-3 text-right text-emerald-400 font-bold">{paymentStats.monthly_revenue.reduce((s, m) => s + m.count, 0)}</td>
                                                      <td colSpan="5"></td>
                                                  </tr>
                                              </tfoot>
                                          </table>
                                      </div>
                                  </div>
                              )}
                          </div>
                          )}

                          {/* RECENT TRANSACTIONS - Professional with Filters */}
                          <div className="rounded-2xl border border-slate-600/50 overflow-hidden">
                              <button onClick={() => toggleSection('recentTx')} className="w-full flex items-center justify-between p-4 bg-slate-700/50 hover:bg-slate-700/70 transition-all duration-200" data-testid="toggle-recent-tx">
                                  <h4 className="font-bold text-white flex items-center gap-2 text-base">All Transactions ({paymentTransactions.length})</h4>
                                  {collapsedSections.recentTx ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronUp size={18} className="text-slate-400" />}
                              </button>
                              {!collapsedSections.recentTx && (
                                  <div className="p-4 space-y-3">
                                      {/* Filter Bar */}
                                      <div className="flex flex-wrap gap-2 items-center" data-testid="tx-filters">
                                          {['all', 'paid', 'pending', 'complete', 'failed', 'gift'].map(f => (
                                              <button
                                                  key={f}
                                                  onClick={() => setTxFilter(f)}
                                                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 ${
                                                      txFilter === f
                                                          ? f === 'paid' ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                                                          : f === 'pending' ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                                                          : f === 'complete' ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                                                          : f === 'failed' ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                                                          : f === 'gift' ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30'
                                                          : 'bg-slate-500 text-white shadow-lg shadow-slate-500/30'
                                                          : 'bg-slate-700/80 text-slate-400 hover:bg-slate-600/80 hover:text-white border border-slate-600/50'
                                                  }`}
                                                  data-testid={`tx-filter-${f}`}
                                              >
                                                  {f === 'all' ? `All (${paymentTransactions.length})` : `${f.charAt(0).toUpperCase() + f.slice(1)} (${paymentTransactions.filter(t => t.status === f || (f === 'gift' && t.plan_id === 'gift')).length})`}
                                              </button>
                                          ))}
                                      </div>

                                      {/* Transactions Table */}
                                      <div className="overflow-x-auto">
                                          <table className="w-full text-sm">
                                              <thead>
                                                  <tr className="border-b border-slate-600/50">
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">User</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Plan</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Amount</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Status</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Credited</th>
                                                      <th className="text-right text-slate-400 font-medium py-2 px-3 text-xs">Date & Time</th>
                                                  </tr>
                                              </thead>
                                              <tbody>
                                                  {(() => {
                                                      const filtered = txFilter === 'all' 
                                                          ? paymentTransactions
                                                          : txFilter === 'gift'
                                                              ? paymentTransactions.filter(t => t.plan_id === 'gift')
                                                              : paymentTransactions.filter(t => t.status === txFilter);
                                                      if (filtered.length === 0) return (
                                                          <tr><td colSpan="6" className="text-center text-slate-500 py-8">No transactions match this filter</td></tr>
                                                      );
                                                      return filtered.map((tx, idx) => {
                                                          const date = tx.created_at ? new Date(tx.created_at) : null;
                                                          const dateStr = date ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                                                          const timeStr = date ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : '';
                                                          return (
                                                              <tr key={idx} className="border-b border-slate-700/30 hover:bg-slate-700/30 transition-colors">
                                                                  <td className="py-3 px-3 text-right">
                                                                      <p className="font-medium text-white text-sm">{tx.user_info?.name || 'Unknown'}</p>
                                                                      <p className="text-xs text-slate-500">{tx.user_info?.email || tx.user_id?.slice(0, 12) || '—'}</p>
                                                                  </td>
                                                                  <td className="py-3 px-3 text-right">
                                                                      <span className={`text-xs px-2 py-1 rounded-md font-bold ${
                                                                          tx.plan_id === 'premium' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                                                                          tx.plan_id === 'ultimate' ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30' :
                                                                          tx.plan_id === 'pro' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                                                                          tx.plan_id === 'gift' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                                                                          'bg-slate-600/30 text-slate-300 border border-slate-500/30'
                                                                      }`}>{tx.plan_id || '—'}</span>
                                                                  </td>
                                                                  <td className="py-3 px-3 text-right">
                                                                      <span className="font-bold text-emerald-400">${tx.amount || 0}</span>
                                                                      <span className="text-xs text-slate-500 ml-1">{(tx.currency || 'usd').toUpperCase()}</span>
                                                                  </td>
                                                                  <td className="py-3 px-3 text-right">
                                                                      <span className={`text-xs px-2.5 py-1 rounded-full font-bold inline-block ${
                                                                          tx.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' :
                                                                          tx.status === 'complete' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40' :
                                                                          tx.status === 'pending' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40' :
                                                                          tx.status === 'failed' ? 'bg-red-500/20 text-red-400 border border-red-500/40' :
                                                                          'bg-slate-600/30 text-slate-400 border border-slate-500/30'
                                                                      }`}>{tx.status || 'unknown'}</span>
                                                                  </td>
                                                                  <td className="py-3 px-3 text-right">
                                                                      {tx.credited 
                                                                          ? <span className="text-xs text-emerald-400 font-bold flex items-center justify-end gap-1"><CheckCircle size={12} /> Yes</span>
                                                                          : <span className="text-xs text-slate-500">No</span>
                                                                      }
                                                                  </td>
                                                                  <td className="py-3 px-3 text-right">
                                                                      <p className="text-xs text-white font-medium">{dateStr}</p>
                                                                      <p className="text-xs text-slate-500">{timeStr}</p>
                                                                  </td>
                                                              </tr>
                                                          );
                                                      });
                                                  })()}
                                              </tbody>
                                          </table>
                                      </div>
                                  </div>
                              )}
                          </div>

                          {/* PAYMENTS CSV EXPORT / IMPORT */}
                          <div className="rounded-2xl border border-slate-600/50 overflow-hidden">
                              <button onClick={() => toggleSection('paymentsCsv')} className="w-full flex items-center justify-between p-4 bg-slate-700/50 hover:bg-slate-700/70 transition-all duration-200" data-testid="toggle-payments-csv">
                                  <h4 className="font-bold text-white flex items-center gap-2 text-base">Payments CSV (Export / Import)</h4>
                                  {collapsedSections.paymentsCsv ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronUp size={18} className="text-slate-400" />}
                              </button>
                              {!collapsedSections.paymentsCsv && (
                                  <div className="p-4 space-y-3">
                                      <p className="text-xs text-slate-400">Export all payment transactions as CSV or import from a CSV file. Includes: session_id, user, plan, amount, currency, status, provider, credited, dates.</p>
                                      <div className="flex gap-3">
                                          <a
                                              href={`${API_URL}/admin/payments/export-csv?key=${encodeURIComponent(password)}`}
                                              download
                                              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-bold hover:from-blue-500 hover:to-cyan-500 transition-all duration-300 shadow-lg shadow-blue-500/30"
                                              data-testid="export-payments-csv-btn"
                                          >
                                              <Download size={18} />
                                              Export Payments CSV
                                          </a>
                                          <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-bold hover:from-emerald-500 hover:to-green-500 transition-all duration-300 shadow-lg shadow-emerald-500/30 cursor-pointer">
                                              <Upload size={18} />
                                              Import Payments CSV
                                              <input
                                                  type="file"
                                                  accept=".csv"
                                                  className="hidden"
                                                  onChange={async (e) => {
                                                      const file = e.target.files[0];
                                                      if (!file) return;
                                                      if (!confirm(`Import payments from "${file.name}"?\nThis will add new transactions and update existing ones.`)) {
                                                          e.target.value = '';
                                                          return;
                                                      }
                                                      try {
                                                          const formData = new FormData();
                                                          formData.append('file', file);
                                                          const res = await axios.post(`${API_URL}/admin/payments/import-csv`, formData, {
                                                              headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
                                                              timeout: 60000
                                                          });
                                                          alert(`Import complete!\nImported: ${res.data.imported}\nUpdated: ${res.data.updated}\nErrors: ${res.data.errors?.length || 0}`);
                                                          fetchPaymentData();
                                                      } catch (err) {
                                                          alert('Import failed: ' + (err.response?.data?.detail || err.message));
                                                      }
                                                      e.target.value = '';
                                                  }}
                                                  data-testid="import-payments-csv-input"
                                              />
                                          </label>
                                      </div>
                                  </div>
                              )}
                          </div>

                          <button 
                              onClick={fetchPaymentData} 
                              className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl text-sm font-bold hover:from-green-500 hover:to-emerald-500 transition-all duration-300 shadow-lg shadow-green-500/30 hover:shadow-green-500/50"
                          >
                              Refresh Payment Data
                          </button>
                      </div>
                  )}

                  {/* Upgrade Modal */}
                  {upgradeModalOpen && selectedUser && (
                      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
                          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md mx-4 border border-slate-700 shadow-2xl">
                              <h3 className="font-bold text-lg mb-4 text-white">Upgrade User Subscription</h3>
                              <div className="mb-4">
                                  <p className="text-sm text-slate-300">User: <strong className="text-white">{selectedUser.name}</strong></p>
                                  <p className="text-xs text-slate-500">{selectedUser.email}</p>
                                  <p className="text-xs text-slate-500">Current Plan: {selectedUser.subscription_plan || 'none'}</p>
                              </div>
                              <div className="mb-4">
                                  <label className="block text-sm font-medium mb-2 text-slate-300">Select New Plan</label>
                                  <select
                                      value={upgradePlan}
                                      onChange={(e) => setUpgradePlan(e.target.value)}
                                      className="w-full p-3 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
                                  >
                                      <option value="basic">Basic ($10 - 10 questions)</option>
                                      <option value="pro">Pro ($20 - 20 questions)</option>
                                      <option value="ultimate">Ultimate ($50 - 50 questions)</option>
                                      <option value="premium">Premium ($100 - 100 questions)</option>
                                  </select>
                              </div>
                              <div className="mb-4">
                                  <label className="block text-sm font-medium mb-2 text-slate-300">Note (optional)</label>
                                  <input
                                      type="text"
                                      value={upgradeNote}
                                      onChange={(e) => setUpgradeNote(e.target.value)}
                                      placeholder="e.g., Stripe payment verified"
                                      className="w-full p-3 bg-slate-700 border border-slate-600 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
                                  />
                              </div>
                              <div className="flex gap-2">
                                  <button
                                      onClick={() => { setUpgradeModalOpen(false); setSelectedUser(null); }}
                                      className="flex-1 py-3 bg-slate-700 text-slate-300 rounded-xl font-medium hover:bg-slate-600 transition-all duration-300"
                                  >
                                      Cancel
                                  </button>
                                  <button
                                      onClick={handleUpgradeUser}
                                      className="flex-1 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium hover:from-green-400 hover:to-emerald-400 transition-all duration-300 shadow-lg shadow-green-500/30"
                                  >
                                      Confirm Upgrade
                                  </button>
                              </div>
                          </div>
                      </div>
                  )}

                  {activeTab === "backup" && (
                      <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700/50 space-y-6 backdrop-blur-sm">
                          
                          {/* Progress Overlay */}
                          {opProgress.active && (
                              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="progress-overlay">
                                  <div className="bg-slate-800 border border-slate-600 rounded-3xl p-8 max-w-lg w-full shadow-2xl">
                                      {/* Header */}
                                      <div className="flex items-center gap-3 mb-6">
                                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                                              opProgress.stage === 'complete' ? 'bg-green-500/20' :
                                              opProgress.stage === 'error' ? 'bg-red-500/20' :
                                              'bg-blue-500/20'
                                          }`}>
                                              {opProgress.stage === 'complete' ? (
                                                  <Check size={28} className="text-green-400" />
                                              ) : opProgress.stage === 'error' ? (
                                                  <X size={28} className="text-red-400" />
                                              ) : (
                                                  <Loader2 size={28} className="text-blue-400 animate-spin" />
                                              )}
                                          </div>
                                          <div>
                                              <h3 className="text-white font-bold text-lg">
                                                  {opProgress.type.startsWith('export') ? 'Exporting' : 'Importing'} Data
                                              </h3>
                                              <p className="text-slate-400 text-sm">
                                                  {opProgress.type.replace('export-', '').replace('import-', '').replace('full', 'Full Backup').replace('kb', 'Knowledge Base').replace('csv', 'Users CSV')}
                                              </p>
                                          </div>
                                      </div>

                                      {/* Progress Bar */}
                                      <div className="mb-4">
                                          <div className="flex justify-between items-center mb-2">
                                              <span className={`text-xs font-bold uppercase tracking-wider ${
                                                  opProgress.stage === 'complete' ? 'text-green-400' :
                                                  opProgress.stage === 'error' ? 'text-red-400' :
                                                  opProgress.stage === 'processing' ? 'text-amber-400' :
                                                  'text-blue-400'
                                              }`}>
                                                  {opProgress.stage === 'preparing' ? 'Preparing...' :
                                                   opProgress.stage === 'downloading' ? 'Downloading' :
                                                   opProgress.stage === 'uploading' ? 'Uploading' :
                                                   opProgress.stage === 'processing' ? 'Processing on Server' :
                                                   opProgress.stage === 'complete' ? 'Complete' :
                                                   opProgress.stage === 'error' ? 'Failed' : ''}
                                              </span>
                                              <span className="text-white font-bold text-sm">{opProgress.progress}%</span>
                                          </div>
                                          <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
                                              <div
                                                  className={`h-full rounded-full transition-all duration-300 ${
                                                      opProgress.stage === 'complete' ? 'bg-gradient-to-r from-green-500 to-emerald-400' :
                                                      opProgress.stage === 'error' ? 'bg-gradient-to-r from-red-500 to-red-400' :
                                                      opProgress.stage === 'processing' ? 'bg-gradient-to-r from-amber-500 to-yellow-400 animate-pulse' :
                                                      'bg-gradient-to-r from-blue-500 to-cyan-400'
                                                  }`}
                                                  style={{ width: `${opProgress.stage === 'processing' ? 100 : opProgress.progress}%` }}
                                              />
                                          </div>
                                      </div>

                                      {/* Status Message */}
                                      <p className="text-slate-300 text-sm mb-4">{opProgress.message}</p>

                                      {/* Stats Row */}
                                      {(opProgress.stage === 'downloading' || opProgress.stage === 'uploading') && (
                                          <div className="grid grid-cols-3 gap-3 mb-4">
                                              <div className="bg-slate-700/50 p-2.5 rounded-xl text-center">
                                                  <p className="text-slate-500 text-[10px] uppercase">Transferred</p>
                                                  <p className="text-white font-bold text-sm">{formatBytes(opProgress.loaded)}</p>
                                              </div>
                                              <div className="bg-slate-700/50 p-2.5 rounded-xl text-center">
                                                  <p className="text-slate-500 text-[10px] uppercase">Speed</p>
                                                  <p className="text-cyan-400 font-bold text-sm">{formatSpeed(opProgress.speed)}</p>
                                              </div>
                                              <div className="bg-slate-700/50 p-2.5 rounded-xl text-center">
                                                  <p className="text-slate-500 text-[10px] uppercase">Total</p>
                                                  <p className="text-white font-bold text-sm">{opProgress.total ? formatBytes(opProgress.total) : '—'}</p>
                                              </div>
                                          </div>
                                      )}

                                      {/* Processing stage with live progress */}
                                      {opProgress.stage === 'processing' && (
                                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
                                              <div className="flex items-center gap-2 mb-2">
                                                  <Loader2 size={16} className="text-amber-400 animate-spin" />
                                                  <p className="text-amber-400 font-bold text-sm">Server Processing</p>
                                              </div>
                                              <p className="text-slate-300 text-xs">{opProgress.message}</p>
                                          </div>
                                      )}

                                      {/* File name for exports */}
                                      {opProgress.stage === 'complete' && opProgress.result && (
                                          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4">
                                              {opProgress.type.startsWith('export') ? (
                                                  <div className="space-y-1">
                                                      <p className="text-green-400 font-bold text-sm">File saved successfully!</p>
                                                      <p className="text-slate-300 text-xs">File: {opProgress.result.fileName || opProgress.fileName}</p>
                                                      <p className="text-slate-300 text-xs">Size: {formatBytes(opProgress.result.size)}</p>
                                                      <p className="text-slate-400 text-[10px]">Time: {((Date.now() - opProgress.startTime) / 1000).toFixed(1)}s</p>
                                                  </div>
                                              ) : (
                                                  <div className="space-y-1">
                                                      <p className="text-green-400 font-bold text-sm">Import completed successfully!</p>
                                                      {opProgress.result.imported_collections && Object.entries(opProgress.result.imported_collections).map(([col, stats]) => (
                                                          <p key={col} className="text-slate-300 text-xs">
                                                              {col}: <span className="text-green-400">{stats.imported || stats.added || 0} new</span>
                                                              {(stats.updated !== undefined) && <span className="text-amber-400">, {stats.updated} updated</span>}
                                                              {(stats.skipped !== undefined) && <span className="text-slate-500">, {stats.skipped} skipped</span>}
                                                          </p>
                                                      ))}
                                                      {opProgress.result.imported && Object.entries(opProgress.result.imported).map(([col, stats]) => (
                                                          <p key={col} className="text-slate-300 text-xs">
                                                              {col}: <span className="text-green-400">{stats.added || 0} added</span>
                                                              <span className="text-slate-500">, {stats.skipped || 0} skipped</span>
                                                          </p>
                                                      ))}
                                                      {opProgress.result.errors?.length > 0 && (
                                                          <div className="mt-2 border-t border-red-500/30 pt-2">
                                                              <p className="text-red-400 text-xs font-bold">Errors:</p>
                                                              {opProgress.result.errors.map((err, i) => (
                                                                  <p key={i} className="text-red-300 text-[10px]">{err}</p>
                                                              ))}
                                                          </div>
                                                      )}
                                                      <p className="text-slate-400 text-[10px]">Time: {((Date.now() - opProgress.startTime) / 1000).toFixed(1)}s</p>
                                                  </div>
                                              )}
                                          </div>
                                      )}

                                      {/* Error details */}
                                      {opProgress.stage === 'error' && (
                                          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
                                              <p className="text-red-400 font-bold text-sm">Operation Failed</p>
                                              <p className="text-red-300 text-xs mt-1">{opProgress.error}</p>
                                          </div>
                                      )}

                                      {/* Close button */}
                                      {(opProgress.stage === 'complete' || opProgress.stage === 'error') && (
                                          <button
                                              onClick={() => setOpProgress(p => ({ ...p, active: false }))}
                                              className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${
                                                  opProgress.stage === 'complete'
                                                      ? 'bg-green-600 hover:bg-green-500 text-white'
                                                      : 'bg-red-600 hover:bg-red-500 text-white'
                                              }`}
                                              data-testid="progress-close-btn"
                                          >
                                              {opProgress.stage === 'complete' ? 'Done' : 'Close'}
                                          </button>
                                      )}
                                  </div>
                              </div>
                          )}

                          <div className="text-center mb-6">
                              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-pink-500/40">
                                  <Database size={32} className="text-white" />
                              </div>
                              <h3 className="text-2xl font-bold text-white">
                                  Full Database Backup
                              </h3>
                              <p className="text-slate-400 mt-2">Export and restore all your application data</p>
                          </div>

                          {/* Export Section */}
                          <div className="bg-slate-700/50 p-6 rounded-2xl border border-slate-600/50 hover:border-purple-500/50 transition-all duration-300">
                              <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                  <span className="text-2xl">⬇️</span> Export Full Backup (JSON)
                              </h4>
                              <p className="text-sm text-slate-400 mb-4">
                                  Download a complete backup of all your data including:
                              </p>
                              <ul className="text-sm text-slate-300 mb-6 grid grid-cols-2 gap-2">
                                  <li className="flex items-center gap-2">✅ All registered users</li>
                                  <li className="flex items-center gap-2">✅ Subscription plans</li>
                                  <li className="flex items-center gap-2">✅ Payment transactions</li>
                                  <li className="flex items-center gap-2">✅ Chat history</li>
                                  <li className="flex items-center gap-2">✅ User sessions</li>
                                  <li className="flex items-center gap-2">✅ Feedbacks</li>
                                  <li className="flex items-center gap-2">✅ Pending payments</li>
                                  <li className="flex items-center gap-2">✅ Device/IP tracking</li>
                              </ul>
                              <button
                                  onClick={() => handleExport('full')}
                                  disabled={opProgress.active}
                                  className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl text-lg font-bold hover:from-purple-500 hover:to-indigo-500 transition-all duration-300 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                  data-testid="export-full-backup-btn"
                              >
                                  <Database size={24} />
                                  Download Full Backup
                              </button>
                          </div>

                          {/* Import Section */}
                          <div className="bg-slate-700/50 p-6 rounded-2xl border border-slate-600/50 hover:border-green-500/50 transition-all duration-300">
                              <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                  <span className="text-2xl">⬆️</span> Restore Backup
                              </h4>
                              <p className="text-sm text-slate-400 mb-4">
                                  Upload a previously exported JSON backup file to restore all data.
                              </p>
                              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
                                  <p className="text-sm text-amber-400 font-medium">⚠️ Warning:</p>
                                  <p className="text-sm text-amber-300/80">
                                      This will update existing records and add new ones from the backup file. 
                                      Existing data that matches will be overwritten.
                                  </p>
                              </div>
                              <label className={`w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-2xl text-lg font-bold hover:from-green-500 hover:to-emerald-500 transition-all duration-300 shadow-lg shadow-green-500/30 hover:shadow-green-500/50 hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${opProgress.active ? 'opacity-50 pointer-events-none' : ''}`}>
                                  <Upload size={24} />
                                  Select Backup File to Restore
                                  <input
                                      type="file"
                                      accept=".json,.gz"
                                      className="hidden"
                                      disabled={opProgress.active}
                                      onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (!file) return;
                                          if (!confirm('⚠️ This will restore/update ALL data from the backup file.\n\nAre you sure you want to continue?')) {
                                              e.target.value = '';
                                              return;
                                          }
                                          handleImport('full', file);
                                          e.target.value = '';
                                      }}
                                      data-testid="restore-backup-input"
                                  />
                              </label>
                          </div>

                          {/* Quick User CSV Export / Import */}
                          <div className="bg-slate-700/50 p-6 rounded-2xl border border-slate-600/50 hover:border-blue-500/50 transition-all duration-300">
                              <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                  <span className="text-2xl">📋</span> Users CSV (Export / Import)
                              </h4>
                              <p className="text-sm text-slate-400 mb-4">
                                  Export or import user data as a CSV file (for spreadsheets).
                              </p>
                              <div className="flex gap-3">
                                  <button
                                      onClick={() => handleExport('csv')}
                                      disabled={opProgress.active}
                                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-bold hover:from-blue-500 hover:to-cyan-500 transition-all duration-300 shadow-lg shadow-blue-500/30 disabled:opacity-50"
                                      data-testid="export-csv-btn"
                                  >
                                      <Download size={18} />
                                      Export CSV
                                  </button>
                                  <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-bold hover:from-emerald-500 hover:to-green-500 transition-all duration-300 shadow-lg shadow-emerald-500/30 cursor-pointer disabled:opacity-50">
                                      <Upload size={18} />
                                      Import CSV
                                      <input
                                          type="file"
                                          accept=".csv"
                                          className="hidden"
                                          onChange={async (e) => {
                                              const file = e.target.files[0];
                                              if (!file) return;
                                              if (!confirm(`Import users from "${file.name}"?\nThis will add new users and update existing ones.`)) {
                                                  e.target.value = '';
                                                  return;
                                              }
                                              try {
                                                  const formData = new FormData();
                                                  formData.append('file', file);
                                                  const res = await axios.post(`${API_URL}/admin/users/import-csv`, formData, {
                                                      headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
                                                      timeout: 60000
                                                  });
                                                  alert(`Import complete!\nImported: ${res.data.imported}\nUpdated: ${res.data.updated}\nErrors: ${res.data.errors?.length || 0}`);
                                              } catch (err) {
                                                  alert('Import failed: ' + (err.response?.data?.detail || err.message));
                                              }
                                              e.target.value = '';
                                          }}
                                          data-testid="import-csv-input"
                                      />
                                  </label>
                              </div>
                          </div>

                          {/* Chat History JSON Export / Import */}
                          <div className="bg-slate-700/50 p-6 rounded-2xl border border-slate-600/50 hover:border-violet-500/50 transition-all duration-300">
                              <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                  <span className="text-2xl"><MessageCircle size={24} className="text-violet-400 inline" /></span> Chat History JSON (Export / Import)
                              </h4>
                              <p className="text-sm text-slate-400 mb-4">
                                  Full chat history with messages, user info, timestamps, device/IP data, and plan info.
                              </p>
                              <div className="flex gap-3">
                                  <a
                                      href={`${API_URL}/admin/chats/export-json?key=${encodeURIComponent(password)}`}
                                      download
                                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-bold hover:from-violet-500 hover:to-purple-500 transition-all duration-300 shadow-lg shadow-violet-500/30"
                                      data-testid="export-chats-json-btn"
                                  >
                                      <Download size={18} />
                                      Export Chats JSON
                                  </a>
                                  <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white rounded-xl font-bold hover:from-fuchsia-500 hover:to-pink-500 transition-all duration-300 shadow-lg shadow-fuchsia-500/30 cursor-pointer">
                                      <Upload size={18} />
                                      Import Chats JSON
                                      <input
                                          type="file"
                                          accept=".json"
                                          className="hidden"
                                          onChange={async (e) => {
                                              const file = e.target.files[0];
                                              if (!file) return;
                                              if (!confirm(`Import chats from "${file.name}"?\nThis will add new chats and update existing ones.`)) {
                                                  e.target.value = '';
                                                  return;
                                              }
                                              try {
                                                  const formData = new FormData();
                                                  formData.append('file', file);
                                                  const res = await axios.post(`${API_URL}/admin/chats/import-json`, formData, {
                                                      headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
                                                      timeout: 120000
                                                  });
                                                  alert(`Import complete!\nImported: ${res.data.imported}\nUpdated: ${res.data.updated}\nTotal: ${res.data.total_processed}\nErrors: ${res.data.errors?.length || 0}`);
                                              } catch (err) {
                                                  alert('Import failed: ' + (err.response?.data?.detail || err.message));
                                              }
                                              e.target.value = '';
                                          }}
                                          data-testid="import-chats-json-input"
                                      />
                                  </label>
                              </div>
                          </div>

                          {/* Knowledge Base Export/Import */}
                          <div className="bg-slate-700/50 p-6 rounded-2xl border border-amber-500/30 hover:border-amber-500/50 transition-all duration-300">
                              <h4 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                                  <span className="text-2xl">📚</span> Knowledge Base Export / Import
                              </h4>
                              <p className="text-sm text-slate-400 mb-4">
                                  Export or import all AI knowledge base data (sources + source chunks).
                              </p>

                              {/* KB Export */}
                              <button
                                  onClick={() => handleExport('kb')}
                                  disabled={opProgress.active}
                                  className="w-full flex items-center justify-center gap-3 px-6 py-3 mb-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-2xl font-bold hover:from-amber-500 hover:to-orange-500 transition-all duration-300 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-testid="export-kb-btn"
                              >
                                  <Database size={20} />
                                  Download Knowledge Base (.json.gz)
                              </button>

                              {/* KB Import */}
                              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-600/50 space-y-3">
                                  <p className="text-sm text-slate-300 font-medium">Import Knowledge Base</p>
                                  <div className="flex gap-3 items-center">
                                      <label className="text-xs text-slate-400 flex items-center gap-2">
                                          <input
                                              type="radio"
                                              name="kb-import-mode"
                                              value="merge"
                                              defaultChecked
                                              onChange={(e) => { window._kbImportMode = e.target.value; }}
                                              className="accent-amber-500"
                                          />
                                          Merge (keep existing, add new)
                                      </label>
                                      <label className="text-xs text-slate-400 flex items-center gap-2">
                                          <input
                                              type="radio"
                                              name="kb-import-mode"
                                              value="replace"
                                              onChange={(e) => { window._kbImportMode = e.target.value; }}
                                              className="accent-red-500"
                                          />
                                          Replace (wipe & restore)
                                      </label>
                                  </div>
                                  <label className={`w-full flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-bold hover:from-teal-500 hover:to-emerald-500 transition-all duration-300 shadow-lg shadow-teal-500/30 cursor-pointer hover:scale-[1.02] active:scale-[0.98] ${opProgress.active ? 'opacity-50 pointer-events-none' : ''}`}>
                                      <Upload size={20} />
                                      Select Knowledge Base File
                                      <input
                                          type="file"
                                          accept=".json,.gz"
                                          className="hidden"
                                          data-testid="import-kb-input"
                                          disabled={opProgress.active}
                                          onChange={(e) => {
                                              const file = e.target.files[0];
                                              if (!file) return;
                                              const mode = window._kbImportMode || 'merge';
                                              const modeLabel = mode === 'replace' ? 'REPLACE (wipe all existing data)' : 'MERGE (keep existing, add new)';
                                              if (!confirm(`Import knowledge base?\n\nMode: ${modeLabel}\nFile: ${file.name}\n\n${mode === 'replace' ? '⚠️ WARNING: This will DELETE all existing knowledge base data!' : 'Existing data will be preserved.'}\n\nContinue?`)) {
                                                  e.target.value = '';
                                                  return;
                                              }
                                              handleImport('kb', file, mode);
                                              e.target.value = '';
                                          }}
                                      />
                                  </label>
                                  <p className="text-[10px] text-slate-500">Supports .json and .json.gz files. Large files may take a few minutes.</p>
                              </div>
                          </div>
                      </div>
                  )}

                  {activeTab === "status" && stats && (
                      <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700/50 space-y-4 backdrop-blur-sm">
                          <h3 className="font-bold text-sky-400 flex items-center gap-2 mb-4">
                              <div className="p-2 bg-gradient-to-br from-sky-500 to-blue-500 rounded-xl shadow-lg shadow-sky-500/30">
                                  <Activity size={18} className="text-white" />
                              </div>
                              System Status
                          </h3>
                          <div className="grid grid-cols-2 gap-3">
                              <div className="bg-slate-700/50 p-4 rounded-2xl border border-slate-600/50 hover:border-sky-500/50 transition-all duration-300">
                                  <p className="text-xs text-slate-400">Total Users (Chats)</p>
                                  <p className="text-2xl font-bold text-sky-400">{stats.total_chats}</p>
                              </div>
                              <div className="bg-slate-700/50 p-4 rounded-2xl border border-slate-600/50 hover:border-purple-500/50 transition-all duration-300">
                                  <p className="text-xs text-slate-400">Total Messages</p>
                                  <p className="text-2xl font-bold text-purple-400">{stats.total_messages}</p>
                              </div>
                              <div className="bg-slate-700/50 p-4 rounded-2xl border border-slate-600/50 hover:border-emerald-500/50 transition-all duration-300">
                                  <p className="text-xs text-slate-400">Total Sources</p>
                                  <p className="text-2xl font-bold text-emerald-400">{stats.total}</p>
                              </div>
                              <div className="bg-slate-700/50 p-4 rounded-2xl border border-slate-600/50 hover:border-amber-500/50 transition-all duration-300">
                                  <p className="text-xs text-slate-400">Background Import</p>
                                  <p className={`text-lg font-bold ${stats.is_importing ? "text-emerald-400 animate-pulse" : "text-slate-500"}`}>
                                      {stats.is_importing ? "Running..." : "Idle"}
                                  </p>
                              </div>
                              <div className="bg-slate-700/50 p-4 rounded-2xl border border-red-500/30 hover:border-red-500/50 transition-all duration-300">
                                  <p className="text-xs text-red-400/70">YouTube Videos</p>
                                  <p className="text-xl font-bold text-red-400">{stats.youtube}</p>
                              </div>
                              <div className="bg-slate-700/50 p-4 rounded-2xl border border-emerald-500/30 hover:border-emerald-500/50 transition-all duration-300">
                                  <p className="text-xs text-emerald-400/70">Files</p>
                                  <p className="text-xl font-bold text-emerald-400">{stats.files}</p>
                              </div>
                              <div className="bg-slate-700/50 p-4 rounded-2xl border border-orange-500/30 hover:border-orange-500/50 transition-all duration-300">
                                  <p className="text-xs text-orange-400/70">Audio Files</p>
                                  <p className="text-xl font-bold text-orange-400">{stats.audio || 0}</p>
                              </div>
                              <div className="bg-slate-700/50 p-4 rounded-2xl border border-blue-500/30 hover:border-blue-500/50 transition-all duration-300">
                                  <p className="text-xs text-blue-400/70">Google Drive</p>
                                  <p className="text-xl font-bold text-blue-400">{stats.gdrive || 0}</p>
                              </div>
                          </div>

                          <div className="bg-slate-700/50 p-4 rounded-2xl border border-slate-600/50">
                              <p className="text-xs text-slate-400 mb-3 font-bold">Latest Imports</p>
                              <div className="space-y-2">
                                  {stats.recent.map((item, idx) => (
                                      <p key={idx} className="text-xs text-slate-300 truncate border-b border-slate-600/30 pb-2 hover:text-white transition-all duration-300">
                                          📄 {item.filename} <span className="text-slate-500">({new Date(item.date).toLocaleTimeString()})</span>
                                      </p>
                                  ))}
                              </div>
                          </div>
                          
                          <button onClick={fetchStats} className="w-full py-3 bg-gradient-to-r from-sky-600 to-blue-600 text-white rounded-xl text-sm font-bold hover:from-sky-500 hover:to-blue-500 transition-all duration-300 shadow-lg shadow-sky-500/30">
                              Refresh Status
                          </button>
                      </div>
                  )}

                  {activeTab === "file" && (
                  <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700/50 backdrop-blur-sm">
                      <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                          <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl shadow-lg shadow-emerald-500/30">
                              <Upload size={18} className="text-white" />
                          </div>
                          {t('uploadFile')}
                      </h3>
                      
                      <div className="relative">
                          <input 
                              type="file" 
                              ref={fileInputRef}
                              onChange={handleUpload}
                              accept=".pdf,.txt,.md,.docx,.doc,.pptx,.xlsx,.xls,.csv,.json,.xml"
                              disabled={uploading}
                              className="hidden"
                              id="file-upload"
                          />
                          <label 
                              htmlFor="file-upload" 
                              className={`flex items-center justify-center gap-3 w-full py-4 border-2 border-dashed border-emerald-500/50 rounded-2xl cursor-pointer bg-slate-700/30 hover:bg-emerald-500/10 hover:border-emerald-400 transition-all duration-300 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                          >
                              {uploading ? <Loader2 className="animate-spin text-emerald-400" size={24} /> : <Upload size={24} className="text-emerald-400" />}
                              <span className="font-bold text-emerald-400">
                                  {uploading ? "Processing..." : t('uploadFile')}
                              </span>
                          </label>
                      </div>
                  </div>
                  )}

                  {activeTab === "audio" && (
                  <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700/50 backdrop-blur-sm">
                      <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                          <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl shadow-lg shadow-orange-500/30">
                              <Mic size={18} className="text-white" />
                          </div>
                          Upload Audio (All Formats)
                      </h3>
                      <p className="text-xs text-slate-400 mb-4 ml-12">
                          Best formats: MP3, WAV, M4A, MP4, MPEG, WebM (max 25MB)
                      </p>
                      
                      <div className="relative">
                          <input 
                              type="file" 
                              ref={audioInputRef}
                              onChange={handleAudioUpload}
                              accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,.wma,.aiff,.opus,.webm,.amr,.mpeg,.mpga,audio/*"
                              disabled={uploading}
                              className="hidden"
                              id="audio-upload"
                          />
                          <label 
                              htmlFor="audio-upload" 
                              className={`flex items-center justify-center gap-3 w-full py-4 border-2 border-dashed border-orange-500/50 rounded-2xl cursor-pointer bg-slate-700/30 hover:bg-orange-500/10 hover:border-orange-400 transition-all duration-300 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                          >
                              {uploading ? <Loader2 className="animate-spin text-orange-400" size={24} /> : <Mic size={24} className="text-orange-400" />}
                              <span className="font-bold text-orange-400">
                                  {uploading ? "Transcribing Audio..." : "Upload Audio File"}
                              </span>
                          </label>
                      </div>
                      
                      {transcriptionPreview && (
                          <div className="mt-4 p-4 bg-slate-700/50 rounded-xl border border-orange-500/30">
                              <p className="text-xs font-bold text-orange-400 mb-2">Transcription Preview:</p>
                              <p className="text-sm text-slate-300 whitespace-pre-wrap">{transcriptionPreview}</p>
                          </div>
                      )}
                  </div>
                  )}

                  {activeTab === "gdrive" && (
                  <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700/50 backdrop-blur-sm">
                      <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                          <div className="p-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl shadow-lg shadow-blue-500/30">
                              <HardDrive size={18} className="text-white" />
                          </div>
                          Import from Google Drive
                      </h3>
                      <p className="text-xs text-slate-400 mb-4 ml-12">
                          Paste any Google Drive share link (Documents, Sheets, PDFs, Audio files)
                      </p>
                      
                      <form onSubmit={handleGdriveImport} className="flex flex-col gap-3">
                          <div className="relative">
                              <LinkIcon size={18} className="absolute right-3 top-3.5 text-slate-500" />
                              <input 
                                  type="text"
                                  value={gdriveUrl}
                                  onChange={(e) => setGdriveUrl(e.target.value)}
                                  placeholder="https://drive.google.com/file/d/... or https://docs.google.com/..."
                                  className="w-full p-3 pr-10 bg-slate-700/50 border border-slate-600 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                                  dir="ltr"
                              />
                          </div>
                          <button 
                              type="submit"
                              disabled={uploading || !gdriveUrl}
                              className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-bold hover:from-blue-500 hover:to-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.02] active:scale-[0.98]"
                          >
                              {uploading ? <Loader2 className="animate-spin" size={18} /> : <HardDrive size={18} />}
                              <span>{uploading ? "Importing..." : "Import from Google Drive"}</span>
                          </button>
                      </form>
                      
                      <div className="mt-4 p-4 bg-slate-700/50 rounded-xl border border-slate-600/50">
                          <p className="text-xs font-bold text-blue-400 mb-2">Supported Links:</p>
                          <ul className="text-xs text-slate-400 space-y-1">
                              <li>• Google Docs (exported as text)</li>
                              <li>• Google Sheets (exported as CSV)</li>
                              <li>• Google Slides (exported as text)</li>
                              <li>• PDF files</li>
                              <li>• Audio files (auto-transcribed)</li>
                              <li>• Text files (.txt, .md)</li>
                          </ul>
                          <div className="mt-3 p-3 bg-amber-500/10 rounded-xl border border-amber-500/30">
                              <p className="text-[10px] text-amber-400 font-bold">⚠️ Important:</p>
                              <p className="text-[10px] text-amber-300/80">File sharing must be set to "Anyone with the link can view"</p>
                              <p className="text-[10px] text-slate-500 mt-1">Right-click file → Share → General access → "Anyone with the link"</p>
                          </div>
                      </div>
                  </div>
                  )}

                  {activeTab === "youtube" && (
                  <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700/50 backdrop-blur-sm">
                      <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                          <div className="p-2 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl shadow-lg shadow-red-500/30">
                              <Youtube size={18} className="text-white" />
                          </div>
                          {t('importYoutube')}
                      </h3>
                      
                      <form onSubmit={handleYoutubeUpload} className="flex flex-col gap-3">
                          <div className="relative">
                              <LinkIcon size={18} className="absolute right-3 top-3.5 text-slate-500" />
                              <input 
                                  type="text"
                                  value={youtubeUrl}
                                  onChange={(e) => setYoutubeUrl(e.target.value)}
                                  placeholder="https://www.youtube.com/watch?v=..."
                                  className="w-full p-3 pr-10 bg-slate-700/50 border border-slate-600 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500 transition-all duration-300"
                                  dir="ltr"
                              />
                          </div>
                          <button 
                              type="submit"
                              disabled={uploading || !youtubeUrl}
                              className="w-full py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-xl font-bold hover:from-red-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-red-500/30 hover:shadow-red-500/50 hover:scale-[1.02] active:scale-[0.98]"
                          >
                              {uploading ? <Loader2 className="animate-spin" size={18} /> : <Youtube size={18} />}
                              <span>
                                  {importProgress.total > 0 
                                      ? `Importing ${importProgress.current}/${importProgress.total}...` 
                                      : t('importYoutube')}
                              </span>
                          </button>
                      </form>
                  </div>
                  )}

                  {activeTab !== "feedback" && activeTab !== "status" && (
                  <div className="bg-slate-800/30 p-5 rounded-2xl border border-slate-700/50">
                      <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                          <FileText size={18} className="text-slate-400" />
                          Sources ({sources.length})
                      </h3>
                      <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                          {sources.map(src => (
                              <div key={src.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-xl border border-slate-600/50 hover:border-slate-500/50 hover:bg-slate-700/70 transition-all duration-300 group">
                                  <div className="flex items-center gap-3 overflow-hidden">
                                      <div className="p-2.5 bg-slate-600/50 rounded-xl border border-slate-500/50 group-hover:border-slate-400/50 transition-all duration-300">
                                          {src.type === 'youtube' ? <Youtube size={16} className="text-red-400" /> : <FileText size={16} className="text-emerald-400" />}
                                      </div>
                                      <div className="min-w-0">
                                          <p className="font-medium text-white truncate text-sm">{src.filename}</p>
                                          <p className="text-[10px] text-slate-500">
                                              {new Date(src.upload_date).toLocaleDateString()} • {src.chunk_count} chunks
                                          </p>
                                      </div>
                                  </div>
                                  <button 
                                      onClick={() => handleDelete(src.id)}
                                      className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all duration-300"
                                  >
                                      <Trash2 size={16} />
                                  </button>
                              </div>
                          ))}
                      </div>
                  </div>
                  )}
              </div>
          )}
      </div>
        </div>
      </div>
    </div>
  );

  if (isModal) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
            {Content}
        </div>
      );
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e1b4b] to-[#0f172a] p-6 flex items-center justify-center" dir={dir}>
          {Content}
      </div>
  );
};

// --- Bilingual Legal Page Layout Component ---
const BilingualLegalLayout = ({ titleEn, titleAr, contentEn, contentAr }) => {
    const navigate = useNavigate();
    
    return (
        <div className="min-h-screen bg-gradient-to-b from-[#0f172a] to-[#1e293b]" dir="ltr">
            {/* Header */}
            <header className="bg-[#0f172a]/80 backdrop-blur-sm border-b border-[#334155] py-4 px-6">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <button 
                        onClick={() => navigate('/')}
                        className="flex items-center gap-2 text-[#d4a574] hover:text-[#e2b687] transition-colors"
                    >
                        <ArrowLeft size={20} />
                        <span className="font-medium">Back to Home</span>
                    </button>
                    <div className="text-xl font-bold text-[#d4a574]">
                        Dr. Nawwaf | د. نواف
                    </div>
                    <button 
                        onClick={() => navigate('/')}
                        className="flex items-center gap-2 text-[#d4a574] hover:text-[#e2b687] transition-colors"
                    >
                        <span className="font-medium">العودة للرئيسية</span>
                        <ArrowLeft size={20} className="rotate-180" />
                    </button>
                </div>
            </header>
            
            {/* Bilingual Title */}
            <div className="max-w-7xl mx-auto px-6 pt-10 pb-6">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl md:text-3xl font-bold text-white">{titleEn}</h1>
                    <h1 className="text-2xl md:text-3xl font-bold text-white font-arabic">{titleAr}</h1>
                </div>
                <div className="h-px bg-gradient-to-r from-[#d4a574] via-[#334155] to-[#d4a574] mt-4"></div>
            </div>
            
            {/* Side by Side Content */}
            <main className="max-w-7xl mx-auto px-6 pb-12">
                <div className="grid md:grid-cols-2 gap-8">
                    {/* English - Left Side (LTR alignment) */}
                    <div className="bg-[#1e293b]/50 backdrop-blur-sm rounded-2xl border border-[#334155] p-6 md:p-8">
                        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-[#334155]">
                            <span className="text-lg">🇺🇸</span>
                            <span className="text-[#d4a574] font-semibold">English</span>
                        </div>
                        <div className="text-left" dir="ltr">
                            {contentEn}
                        </div>
                    </div>
                    
                    {/* Arabic - Right Side (RTL alignment) */}
                    <div className="bg-[#1e293b]/50 backdrop-blur-sm rounded-2xl border border-[#334155] p-6 md:p-8">
                        <div className="flex items-center justify-end gap-2 mb-6 pb-4 border-b border-[#334155]">
                            <span className="text-[#d4a574] font-semibold font-arabic">العربية</span>
                            <span className="text-lg">🇸🇦</span>
                        </div>
                        <div className="text-right" dir="rtl">
                            {contentAr}
                        </div>
                    </div>
                </div>
            </main>
            
            {/* Footer */}
            <footer className="bg-[#0f172a] border-t border-[#334155] py-6">
                <div className="max-w-7xl mx-auto px-6 text-center" dir="rtl">
                    <p className="text-[#94a3b8] text-sm">مدير قسم البرمجة والذكاء الاصطناعي — <span className="text-[#d4a574]">كنان واكد</span></p>
                </div>
            </footer>
        </div>
    );
};

// --- Terms of Service Page ---
const TermsPage = () => {
    const { lang, t } = useLanguage();
    
    const contentAr = (
        <div className="space-y-6 text-[#cbd5e1]" dir="rtl">
            <p className="text-sm text-[#94a3b8]">آخر تحديث: يناير 2026</p>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">1. مقدمة</h2>
                <p>مرحباً بك في منصة "اسأل د. نواف الشبلي". باستخدامك لهذه المنصة، فإنك توافق على الالتزام بهذه الشروط والأحكام. يرجى قراءتها بعناية قبل استخدام خدماتنا.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">2. طبيعة الخدمة</h2>
                <p className="mb-3">تقدم هذه المنصة خدمة حوارية تعتمد على الذكاء الاصطناعي، مستوحاة من تعاليم وأفكار الدكتور نواف الشبلي في المجالات الفكرية والروحانية. هذه الخدمة:</p>
                <p>• تهدف للإرشاد الفكري والروحي العام فقط</p>
                <p>• لا تُعد بديلاً عن الاستشارة الطبية أو النفسية المتخصصة</p>
                <p>• تعمل بنظام الذكاء الاصطناعي وقد لا تعكس دائماً رأي الدكتور نواف الشبلي الشخصي</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">3. الاشتراكات والدفع</h2>
                <p>نوفر عدة خطط اشتراك بأسعار مختلفة. جميع الأسعار معروضة بالدولار الأمريكي (USD). الدفع يتم عبر منصة Stripe الآمنة.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">4. سياسة عدم الاسترداد</h2>
                <div className="bg-[#0f172a]/50 border border-[#d4a574]/30 rounded-xl p-4">
                    <p className="font-bold text-[#d4a574]">هام: جميع المبالغ المدفوعة غير قابلة للاسترداد.</p>
                    <p className="mt-2">نظراً لطبيعة المنتجات الرقمية والوصول الفوري للخدمة، لا نقدم استرداد للمبالغ المدفوعة بعد إتمام عملية الشراء. يرجى التأكد من اختيارك قبل الاشتراك.</p>
                </div>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">5. سلوك المستخدم</h2>
                <p className="mb-3">يُتوقع من المستخدمين:</p>
                <p>• استخدام المنصة بشكل محترم وأخلاقي</p>
                <p>• عدم محاولة إساءة استخدام النظام أو التحايل عليه</p>
                <p>• عدم مشاركة معلومات الدخول مع الآخرين</p>
                <p>• الالتزام بالقوانين المحلية والدولية</p>
                    <li>الالتزام بالقوانين المحلية والدولية</li>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">6. المحتوى</h2>
                <p>جميع المحتويات على هذه المنصة محمية ولا يجوز نسخها أو توزيعها دون إذن كتابي مسبق.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">7. تعديل الشروط</h2>
                <p>نحتفظ بالحق في تعديل هذه الشروط في أي وقت. سيتم إخطار المستخدمين بالتغييرات الجوهرية عبر البريد الإلكتروني أو من خلال إشعار على المنصة.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">8. القانون الحاكم</h2>
                <p>تخضع هذه الشروط للقوانين السارية، وأي نزاعات تنشأ عن استخدام هذه الخدمة ستُحل وفقاً لتلك القوانين.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">9. التواصل</h2>
                <p>للاستفسارات حول هذه الشروط، يرجى التواصل معنا عبر:</p>
                <div className="bg-[#0f172a]/50 rounded-xl p-4 mt-3 border border-[#334155]">
                    <p className="text-[#94a3b8] text-sm mb-1">مدير قسم البرمجة والذكاء الاصطناعي</p>
                    <p className="text-[#d4a574] font-bold">كنان واكد</p>
                    <p className="mt-3">واتساب: <span className="text-[#d4a574]">+963988242654</span></p>
                </div>
            </section>
        </div>
    );
    
    const contentEn = (
        <div className="space-y-6 text-[#cbd5e1]">
            <p className="text-sm text-[#94a3b8]">Last updated: January 2026</p>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">1. Introduction</h2>
                <p>Welcome to the "Ask Dr. Nawwaf Al-Shibli" platform. By using this platform, you agree to be bound by these Terms of Service. Please read them carefully before using our services.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">2. Nature of Service</h2>
                <p className="mb-3">This platform provides an AI-powered conversational service inspired by the teachings and ideas of Dr. Nawwaf Al-Shibli in intellectual and spiritual matters. This service:</p>
                <p>• Is intended for general intellectual and spiritual guidance only</p>
                <p>• Is not a substitute for professional medical or psychological advice</p>
                <p>• Operates using artificial intelligence and may not always reflect Dr. Nawwaf Al-Shibli's personal opinions</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">3. Subscriptions and Payment</h2>
                <p>We offer multiple subscription plans at various price points. All prices are displayed in US Dollars (USD). Payments are processed securely through Stripe.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">4. No Refund Policy</h2>
                <div className="bg-[#0f172a]/50 border border-[#d4a574]/30 rounded-xl p-4">
                    <p className="font-bold text-[#d4a574]">Important: All payments are non-refundable.</p>
                    <p className="mt-2">Due to the nature of digital products and instant access to services, we do not offer refunds after purchase completion. Please ensure your selection before subscribing.</p>
                </div>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">5. User Conduct</h2>
                <p className="mb-3">Users are expected to:</p>
                <p>• Use the platform respectfully and ethically</p>
                <p>• Not attempt to abuse or circumvent the system</p>
                <p>• Not share login credentials with others</p>
                <p>• Comply with local and international laws</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">6. Intellectual Property</h2>
                <p>All content on this platform, including text, design, and trademarks, is protected by intellectual property rights and may not be copied or distributed without prior written permission.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">7. Modifications to Terms</h2>
                <p>We reserve the right to modify these terms at any time. Users will be notified of material changes via email or through a notice on the platform.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">8. Governing Law</h2>
                <p>These terms are governed by the laws of the United Arab Emirates, and any disputes arising from the use of this service will be resolved in accordance with UAE law.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">9. Contact</h2>
                <p>For questions about these terms, please contact us at:</p>
                <div className="bg-[#0f172a]/50 rounded-xl p-4 mt-3 border border-[#334155]">
                    <p className="text-[#94a3b8] text-sm mb-1">AI & Programming Manager</p>
                    <p className="text-[#d4a574] font-bold text-lg">Kinan Waked</p>
                    <p className="mt-3">WhatsApp: <span className="text-[#d4a574]">+963988242654</span></p>
                </div>
            </section>
        </div>
    );
    
    return (
        <BilingualLegalLayout 
            titleEn="Terms of Service"
            titleAr="شروط الخدمة"
            contentEn={contentEn}
            contentAr={contentAr}
        />
    );
};

// --- Privacy Policy Page ---
const PrivacyPage = () => {
    const { lang, t } = useLanguage();
    
    const contentAr = (
        <div className="space-y-6 text-[#cbd5e1]" dir="rtl">
            <p className="text-sm text-[#94a3b8]">آخر تحديث: يناير 2026</p>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">1. المعلومات التي نجمعها</h2>
                <p className="mb-3">عند استخدامك لمنصتنا، قد نجمع المعلومات التالية:</p>
                <p>• <strong>معلومات الحساب:</strong> الاسم، البريد الإلكتروني عند التسجيل عبر Google</p>
                <p>• <strong>محتوى المحادثات:</strong> الأسئلة التي تطرحها والردود المقدمة</p>
                <p>• <strong>معلومات الدفع:</strong> تتم معالجتها بشكل آمن عبر Stripe ولا نحتفظ بتفاصيل بطاقتك</p>
                <p>• <strong>بيانات الاستخدام:</strong> معلومات تقنية عن كيفية استخدامك للمنصة</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">2. كيف نستخدم معلوماتك</h2>
                <p className="mb-3">نستخدم المعلومات المجمعة من أجل:</p>
                <p>• تقديم وتحسين خدماتنا</p>
                <p>• معالجة المدفوعات وإدارة الاشتراكات</p>
                <p>• التواصل معك بخصوص حسابك</p>
                <p>• تحسين تجربة المستخدم والذكاء الاصطناعي</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">3. حماية البيانات</h2>
                <p className="mb-3">نلتزم بحماية بياناتك من خلال:</p>
                <p>• تشفير جميع البيانات أثناء النقل والتخزين</p>
                <p>• تقييد الوصول إلى البيانات للموظفين المخولين فقط</p>
                <p>• استخدام منصات دفع آمنة ومعتمدة (Stripe)</p>
                <p>• مراجعة دورية لإجراءات الأمان</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">4. مشاركة البيانات</h2>
                <p className="mb-3">لا نبيع أو نؤجر بياناتك الشخصية لأطراف ثالثة. قد نشارك المعلومات فقط مع:</p>
                <p>• مزودي الخدمات الضروريين لتشغيل المنصة</p>
                <p>• السلطات القانونية عند الطلب وفقاً للقانون</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">5. حقوقك</h2>
                <p className="mb-3">لديك الحق في:</p>
                <p>• الوصول إلى بياناتك الشخصية</p>
                <p>• طلب تصحيح أو حذف بياناتك</p>
                <p>• الاعتراض على معالجة بياناتك</p>
                <p>• سحب موافقتك في أي وقت</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">6. ملفات تعريف الارتباط</h2>
                <p>نستخدم ملفات تعريف الارتباط لتحسين تجربتك وتذكر تفضيلاتك. يمكنك إدارة إعدادات ملفات تعريف الارتباط من خلال متصفحك.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">7. التواصل</h2>
                <p>لأي استفسارات تتعلق بالخصوصية، يرجى التواصل معنا:</p>
                <div className="bg-[#0f172a]/50 rounded-xl p-4 mt-3 border border-[#334155]">
                    <p className="text-[#94a3b8] text-sm mb-1">مدير قسم البرمجة والذكاء الاصطناعي</p>
                    <p className="text-[#d4a574] font-bold">كنان واكد</p>
                    <p className="mt-3">واتساب: <span className="text-[#d4a574]">+963988242654</span></p>
                </div>
            </section>
        </div>
    );
    
    const contentEn = (
        <div className="space-y-6 text-[#cbd5e1]">
            <p className="text-sm text-[#94a3b8]">Last updated: January 2026</p>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">1. Information We Collect</h2>
                <p className="mb-3">When using our platform, we may collect the following information:</p>
                <p>• <strong>Account Information:</strong> Name, email address when signing up via Google</p>
                <p>• <strong>Conversation Content:</strong> Questions you ask and responses provided</p>
                <p>• <strong>Payment Information:</strong> Processed securely via Stripe; we do not store your card details</p>
                <p>• <strong>Usage Data:</strong> Technical information about how you use the platform</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">2. How We Use Your Information</h2>
                <p className="mb-3">We use collected information to:</p>
                <p>• Provide and improve our services</p>
                <p>• Process payments and manage subscriptions</p>
                <p>• Communicate with you about your account</p>
                <p>• Enhance user experience and AI performance</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">3. Data Protection</h2>
                <p className="mb-3">We are committed to protecting your data through:</p>
                <p>• Encryption of all data in transit and at rest</p>
                <p>• Restricting data access to authorized personnel only</p>
                <p>• Using secure, certified payment platforms (Stripe)</p>
                <p>• Regular security audits and reviews</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">4. Data Sharing</h2>
                <p className="mb-3">We do not sell or rent your personal data to third parties. We may share information only with:</p>
                <p>• Service providers necessary for platform operation</p>
                <p>• Legal authorities when required by law</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">5. Your Rights</h2>
                <p className="mb-3">You have the right to:</p>
                <p>• Access your personal data</p>
                <p>• Request correction or deletion of your data</p>
                <p>• Object to processing of your data</p>
                <p>• Withdraw your consent at any time</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">6. Cookies</h2>
                <p>We use cookies to improve your experience and remember your preferences. You can manage cookie settings through your browser.</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">7. Contact</h2>
                <p>For any privacy-related inquiries, please contact us at:</p>
                <div className="bg-[#0f172a]/50 rounded-xl p-4 mt-3 border border-[#334155]">
                    <p className="text-[#94a3b8] text-sm mb-1">AI & Programming Manager</p>
                    <p className="text-[#d4a574] font-bold text-lg">Kinan Waked</p>
                    <p className="mt-3">WhatsApp: <span className="text-[#d4a574]">+963988242654</span></p>
                </div>
            </section>
        </div>
    );
    
    return (
        <BilingualLegalLayout 
            titleEn="Privacy Policy"
            titleAr="سياسة الخصوصية"
            contentEn={contentEn}
            contentAr={contentAr}
        />
    );
};

// --- Disclaimer Page ---
const DisclaimerPage = () => {
    const { lang, t } = useLanguage();
    
    const contentAr = (
        <div className="space-y-6 text-[#cbd5e1]" dir="rtl">
            <div className="bg-[#0f172a]/50 border-2 border-[#d4a574]/50 rounded-xl p-6 mb-8">
                <h2 className="text-xl font-bold text-[#d4a574] mb-4 flex items-center justify-center gap-2">
                    <span>⚠️</span> إخلاء مسؤولية هام
                </h2>
                <p className="text-lg">يرجى قراءة هذا الإخلاء بعناية قبل استخدام خدماتنا.</p>
            </div>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">1. إخلاء المسؤولية الطبية</h2>
                <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
                    <p className="font-bold text-red-400 mb-2">تحذير طبي</p>
                    <p>المحتوى المقدم على هذه المنصة <strong>ليس بديلاً</strong> عن الاستشارة الطبية المهنية أو التشخيص أو العلاج. لا تتجاهل أبداً نصيحة طبية متخصصة أو تتأخر في طلبها بسبب شيء قرأته على هذه المنصة.</p>
                    <p className="mt-3">إذا كنت تعاني من حالة طبية طارئة، اتصل بخدمات الطوارئ المحلية فوراً.</p>
                </div>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">2. إخلاء المسؤولية الروحية والنفسية</h2>
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4">
                    <p className="mb-3">الإرشادات الروحية والفكرية المقدمة هنا:</p>
                    <p>• مخصصة للتأمل والإلهام العام فقط</p>
                    <p>• لا تُعد بديلاً عن العلاج النفسي المتخصص</p>
                    <p>• لا تُعد فتاوى دينية رسمية</p>
                    <p>• قد لا تناسب جميع المعتقدات أو الظروف الشخصية</p>
                </div>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">3. طبيعة الذكاء الاصطناعي</h2>
                <p className="mb-3">هذه المنصة تستخدم تقنية الذكاء الاصطناعي. يرجى العلم أن:</p>
                <p>• الردود يتم إنشاؤها بواسطة نظام آلي</p>
                <p>• المحتوى مستوحى من تعاليم الدكتور نواف لكنه لا يمثل رأيه الشخصي المباشر في كل حالة</p>
                <p>• قد تحتوي الردود على أخطاء أو معلومات غير دقيقة</p>
                <p>• النظام قد لا يفهم السياق الكامل لسؤالك</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">4. حدود المسؤولية</h2>
                <p className="mb-3">لا نتحمل أي مسؤولية عن:</p>
                <p>• أي قرارات تتخذها بناءً على المحتوى المقدم</p>
                <p>• أي أضرار مباشرة أو غير مباشرة ناتجة عن استخدام المنصة</p>
                <p>• دقة أو اكتمال المعلومات المقدمة</p>
                <p>• أي انقطاع في الخدمة أو فقدان للبيانات</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">5. الاستخدام المسؤول</h2>
                <p className="mb-3">نشجعك على:</p>
                <p>• استشارة المختصين للقضايا الطبية والنفسية والقانونية</p>
                <p>• استخدام هذه المنصة كمصدر إلهام وتأمل فقط</p>
                <p>• التحقق من أي معلومات مهمة من مصادر موثوقة أخرى</p>
            </section>
            
            <section className="mt-8 p-4 bg-[#1e293b] rounded-xl border border-[#334155]">
                <p className="text-[#94a3b8]">
                    باستخدامك لهذه المنصة، فإنك تُقر بأنك قرأت وفهمت هذا الإخلاء من المسؤولية.
                </p>
            </section>
        </div>
    );
    
    const contentEn = (
        <div className="space-y-6 text-[#cbd5e1]">
            <div className="bg-[#0f172a]/50 border-2 border-[#d4a574]/50 rounded-xl p-6 mb-8">
                <h2 className="text-xl font-bold text-[#d4a574] mb-4 flex items-center justify-center gap-2">
                    <span>⚠️</span> Important Disclaimer
                </h2>
                <p className="text-lg">Please read this disclaimer carefully before using our services.</p>
            </div>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">1. Medical Disclaimer</h2>
                <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
                    <p className="font-bold text-red-400 mb-2">Medical Warning</p>
                    <p>The content provided on this platform is <strong>not a substitute</strong> for professional medical advice, diagnosis, or treatment. Never disregard professional medical advice or delay seeking it because of something you read on this platform.</p>
                    <p className="mt-3">If you are experiencing a medical emergency, contact your local emergency services immediately.</p>
                </div>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">2. Spiritual and Psychological Disclaimer</h2>
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4">
                    <p className="mb-3">The spiritual and intellectual guidance provided here:</p>
                    <p>• Is intended for general contemplation and inspiration only</p>
                    <p>• Is not a substitute for professional psychological therapy</p>
                    <p>• Does not constitute official religious rulings</p>
                    <p>• May not be suitable for all beliefs or personal circumstances</p>
                </div>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">3. Nature of Artificial Intelligence</h2>
                <p className="mb-3">This platform uses artificial intelligence technology. Please note that:</p>
                <p>• Responses are generated by an automated system</p>
                <p>• Content is inspired by Dr. Nawwaf's teachings but does not represent his direct personal opinion in every case</p>
                <p>• Responses may contain errors or inaccurate information</p>
                <p>• The system may not understand the full context of your question</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">4. Limitation of Liability</h2>
                <p className="mb-3">We are not responsible for:</p>
                <p>• Any decisions you make based on the content provided</p>
                <p>• Any direct or indirect damages resulting from platform use</p>
                <p>• The accuracy or completeness of information provided</p>
                <p>• Any service interruption or data loss</p>
            </section>
            
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">5. Responsible Use</h2>
                <p className="mb-3">We encourage you to:</p>
                <p>• Consult specialists for medical, psychological, and legal issues</p>
                <p>• Use this platform as a source of inspiration and contemplation only</p>
                <p>• Verify any important information from other reliable sources</p>
            </section>
            
            <section className="mt-8 p-4 bg-[#1e293b] rounded-xl border border-[#334155]">
                <p className="text-[#94a3b8]">
                    By using this platform, you acknowledge that you have read and understood this disclaimer.
                </p>
            </section>
        </div>
    );
    
    return (
        <BilingualLegalLayout 
            titleEn="Disclaimer"
            titleAr="إخلاء المسؤولية"
            contentEn={contentEn}
            contentAr={contentAr}
        />
    );
};

// --- About Us / Our Mission Page ---
const AboutPage = () => {
    const { lang, t } = useLanguage();
    
    const contentAr = (
        <div className="space-y-8 text-[#cbd5e1]" dir="rtl">
            {/* Mission Statement */}
            <section>
                <h2 className="text-2xl font-bold text-[#d4a574] mb-6">رسالتنا</h2>
                <p className="text-xl leading-relaxed">
                    نسعى لتقديم الحكمة الروحية والفكرية المستمدة من تعاليم الدكتور نواف الشبلي للعالم أجمع، من خلال منصة رقمية متاحة للجميع، في أي وقت ومن أي مكان.
                </p>
            </section>
            
            {/* About Dr. Nawwaf */}
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">عن الدكتور نواف الشبلي</h2>
                <p className="leading-relaxed">
                    الدكتور نواف الشبلي هو مفكر ومرشد روحي، كرّس حياته لدراسة علوم الوعي والحكمة الإلهية. من خلال سنوات من البحث والتأمل والتعلم من كبار الحكماء، طوّر منهجاً فريداً يجمع بين الفكر العميق والإرشاد الروحاني.
                </p>
                <p className="leading-relaxed mt-4">
                    تعاليمه تركز على رحلة الإنسان نحو معرفة الذات ومعرفة الخالق، من خلال التخلص من قيود الأنا والانفتاح على الحقيقة الإلهية.
                </p>
            </section>
            
            {/* Our Vision */}
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">رؤيتنا</h2>
                <p className="leading-relaxed">
                    نؤمن بأن الحكمة يجب أن تكون متاحة للجميع. من خلال دمج تقنية الذكاء الاصطناعي مع تعاليم الدكتور نواف الشبلي، نوفر مساحة آمنة للباحثين عن المعرفة والسكينة الروحية.
                </p>
            </section>
            
            {/* What We Offer */}
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">ما نقدمه</h2>
                <p>✦ <strong>حوار فكري وروحي:</strong> أجوبة مستوحاة من حكمة الدكتور نواف</p>
                <p>✦ <strong>خصوصية تامة:</strong> محادثاتك محمية وسرية بالكامل</p>
                <p>✦ <strong>توفر دائم:</strong> متاح على مدار الساعة، في أي مكان</p>
                <p>✦ <strong>دعم متعدد اللغات:</strong> العربية، الإنجليزية، والفرنسية</p>
            </section>
            
            {/* Contact Section */}
            <section className="bg-[#0f172a]/50 rounded-xl p-6 border border-[#334155]">
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">تواصل معنا</h2>
                <div className="mb-4">
                    <p className="text-[#94a3b8] text-sm mb-1">مدير قسم البرمجة والذكاء الاصطناعي</p>
                    <p className="text-[#d4a574] font-bold text-lg">كنان واكد</p>
                </div>
                <p><strong>واتساب:</strong> <span className="text-[#d4a574]">+963988242654</span></p>
            </section>
        </div>
    );
    
    const contentEn = (
        <div className="space-y-8 text-[#cbd5e1]">
            {/* Mission Statement */}
            <section>
                <h2 className="text-2xl font-bold text-[#d4a574] mb-6">Our Mission</h2>
                <p className="text-xl leading-relaxed">
                    We strive to bring the spiritual and intellectual wisdom derived from the teachings of Dr. Nawwaf Al-Shibli to the world, through a digital platform accessible to everyone, anytime, anywhere.
                </p>
            </section>
            
            {/* About Dr. Nawwaf */}
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">About Dr. Nawwaf Al-Shibli</h2>
                <p className="leading-relaxed">
                    Dr. Nawwaf Al-Shibli is a thinker and spiritual guide who has dedicated his life to studying consciousness sciences and divine wisdom. Through years of research, contemplation, and learning from great sages, he has developed a unique methodology that combines deep thought with spiritual guidance.
                </p>
                <p className="leading-relaxed mt-4">
                    His teachings focus on the human journey toward self-knowledge and knowledge of the Creator, through liberation from the constraints of ego and openness to divine truth.
                </p>
            </section>
            
            {/* Our Vision */}
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">Our Vision</h2>
                <p className="leading-relaxed">
                    We believe that wisdom should be accessible to everyone. By combining artificial intelligence technology with the teachings of Dr. Nawwaf Al-Shibli, we provide a safe space for seekers of knowledge and spiritual serenity.
                </p>
            </section>
            
            {/* What We Offer */}
            <section>
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">What We Offer</h2>
                <p>✦ <strong>Intellectual & Spiritual Dialogue:</strong> Answers inspired by Dr. Nawwaf's wisdom</p>
                <p>✦ <strong>Complete Privacy:</strong> Your conversations are fully protected and confidential</p>
                <p>✦ <strong>Always Available:</strong> Accessible 24/7, from anywhere</p>
                <p>✦ <strong>Multi-language Support:</strong> Arabic, English, and French</p>
            </section>
            
            {/* Contact Section */}
            <section className="bg-[#0f172a]/50 rounded-xl p-6 border border-[#334155]">
                <h2 className="text-xl font-bold text-[#d4a574] mb-4">Contact Us</h2>
                <div className="mb-4">
                    <p className="text-[#94a3b8] text-sm mb-1">AI & Programming Manager</p>
                    <p className="text-[#d4a574] font-bold text-lg">Kinan Waked</p>
                </div>
                <p><strong>WhatsApp:</strong> <span className="text-[#d4a574]">+963988242654</span></p>
            </section>
        </div>
    );
    
    return (
        <BilingualLegalLayout 
            titleEn="About Us"
            titleAr="من نحن"
            contentEn={contentEn}
            contentAr={contentAr}
        />
    );
};

// ==================== REVIEWS SECTION ====================
const StarRating = ({ rating, onRate, size = 20, interactive = false }) => (
    <div className="flex gap-0.5" dir="ltr">
        {[1,2,3,4,5].map(i => (
            <button key={i} onClick={() => interactive && onRate?.(i)} className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}>
                <Star size={size} className={i <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-600'} />
            </button>
        ))}
    </div>
);

const ReviewsSection = ({ user }) => {
    const [reviews, setReviews] = useState([]);
    const [total, setTotal] = useState(0);
    const [showAll, setShowAll] = useState(false);
    const [allReviews, setAllReviews] = useState([]);
    const [allLoading, setAllLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [newRating, setNewRating] = useState(5);
    const [newText, setNewText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const { lang } = useLanguage();

    useEffect(() => {
        axios.get(`${API_URL}/reviews?limit=6`).then(res => {
            setReviews(res.data.reviews || []);
            setTotal(res.data.total || 0);
        }).catch(() => {});
    }, []);

    const loadAll = async () => {
        setShowAll(true);
        setAllLoading(true);
        try {
            const res = await axios.get(`${API_URL}/reviews?limit=200`);
            setAllReviews(res.data.reviews || []);
        } catch (e) {}
        setAllLoading(false);
    };

    const handleSubmit = async () => {
        if (!newText.trim()) return;
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/reviews`, { rating: newRating, text: newText.trim() }, { withCredentials: true });
            setShowForm(false);
            setNewText('');
            setNewRating(5);
            const res = await axios.get(`${API_URL}/reviews?limit=6`);
            setReviews(res.data.reviews || []);
            setTotal(res.data.total || 0);
        } catch (e) {
            alert(e.response?.data?.detail || 'Failed to submit review');
        }
        setSubmitting(false);
    };

    const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : '5.0';

    return (
        <section className="py-16 px-6" dir="rtl">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="text-center mb-10">
                    <h2 className="text-2xl font-bold text-[#d4a574] mb-2">
                        {lang === 'ar' ? 'تقييم المستخدمين' : 'User Reviews'}
                    </h2>
                    <div className="flex items-center justify-center gap-3 mb-3">
                        <StarRating rating={Math.round(parseFloat(avgRating))} size={22} />
                        <span className="text-white font-bold text-lg">{avgRating}</span>
                    </div>
                    {user && (
                        <button
                            onClick={() => setShowForm(true)}
                            className="mt-2 px-5 py-2 bg-gradient-to-r from-[#d4a574] to-[#b8956a] text-[#0f172a] rounded-xl font-bold text-sm hover:from-[#e0b584] hover:to-[#c9a47a] transition-all"
                            data-testid="write-review-btn"
                        >
                            {lang === 'ar' ? 'اكتب تقييمك' : 'Write a Review'}
                        </button>
                    )}
                </div>

                {/* Latest 6 Reviews Grid */}
                {reviews.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                        {reviews.slice(0, 6).map((r, idx) => (
                            <div key={idx} className="bg-[#1e293b]/70 backdrop-blur-sm rounded-2xl p-5 border border-[#334155] hover:border-[#d4a574]/30 transition-all">
                                <div className="flex items-center gap-3 mb-3">
                                    {r.user_picture ? (
                                        <img src={r.user_picture} alt="" className="w-9 h-9 rounded-full border border-[#d4a574]/40" />
                                    ) : (
                                        <div className="w-9 h-9 rounded-full bg-[#d4a574]/20 flex items-center justify-center">
                                            <UserCircle size={20} className="text-[#d4a574]" />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-white font-medium text-sm truncate">{r.user_name || (lang === 'ar' ? 'مستخدم' : 'User')}</p>
                                        <StarRating rating={r.rating} size={14} />
                                    </div>
                                </div>
                                <p className="text-[#cbd5e1] text-sm leading-relaxed line-clamp-4">{r.text}</p>
                                <p className="text-[#64748b] text-xs mt-3">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* See All Reviews Button */}
                {total > 6 && (
                    <div className="text-center">
                        <button
                            onClick={loadAll}
                            className="px-6 py-3 bg-[#1e293b] border border-[#d4a574]/40 text-[#d4a574] rounded-xl font-bold text-sm hover:bg-[#d4a574]/10 transition-all"
                            data-testid="see-all-reviews-btn"
                        >
                            {lang === 'ar' ? `عرض جميع التقييمات (${total})` : `See All Reviews (${total})`}
                        </button>
                    </div>
                )}
            </div>

            {/* Write Review Modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
                    <div className="bg-gradient-to-b from-[#1e293b] to-[#0f172a] rounded-2xl w-full max-w-md p-6 shadow-xl border border-[#334155]">
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="text-lg font-bold text-white">{lang === 'ar' ? 'اكتب تقييمك' : 'Write Your Review'}</h3>
                            <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X size={20} /></button>
                        </div>
                        <div className="text-center mb-5">
                            <p className="text-slate-400 text-sm mb-2">{lang === 'ar' ? 'تقييمك' : 'Your Rating'}</p>
                            <StarRating rating={newRating} onRate={setNewRating} size={32} interactive />
                        </div>
                        <textarea
                            value={newText}
                            onChange={(e) => setNewText(e.target.value)}
                            placeholder={lang === 'ar' ? 'شاركنا تجربتك مع التطبيق...' : 'Share your experience...'}
                            className="w-full h-28 px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#d4a574]/50 resize-none"
                            dir="rtl"
                            data-testid="review-text-input"
                        />
                        <button
                            onClick={handleSubmit}
                            disabled={submitting || !newText.trim()}
                            className="w-full mt-4 py-3 bg-gradient-to-r from-[#d4a574] to-[#b8956a] text-[#0f172a] rounded-xl font-bold hover:from-[#e0b584] hover:to-[#c9a47a] transition-all disabled:opacity-50"
                            data-testid="submit-review-btn"
                        >
                            {submitting ? <Loader2 className="animate-spin mx-auto" size={20} /> : (lang === 'ar' ? 'نشر التقييم' : 'Submit Review')}
                        </button>
                    </div>
                </div>
            )}

            {/* All Reviews Floating Modal */}
            {showAll && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setShowAll(false)}>
                    <div className="bg-gradient-to-b from-[#1e293b] to-[#0f172a] rounded-2xl w-full max-w-2xl max-h-[80vh] shadow-xl border border-[#334155] flex flex-col">
                        <div className="flex justify-between items-center p-5 border-b border-[#334155]">
                            <h3 className="text-lg font-bold text-white">{lang === 'ar' ? `جميع التقييمات (${allReviews.length})` : `All Reviews (${allReviews.length})`}</h3>
                            <button onClick={() => setShowAll(false)} className="text-slate-400 hover:text-white"><X size={20} /></button>
                        </div>
                        <div className="overflow-y-auto flex-1 p-5 space-y-3">
                            {allLoading ? (
                                <div className="text-center py-10"><Loader2 className="animate-spin mx-auto text-[#d4a574]" size={32} /></div>
                            ) : allReviews.map((r, idx) => (
                                <div key={idx} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                                    <div className="flex items-center gap-3 mb-2">
                                        {r.user_picture ? (
                                            <img src={r.user_picture} alt="" className="w-8 h-8 rounded-full border border-[#d4a574]/40" />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-[#d4a574]/20 flex items-center justify-center"><UserCircle size={16} className="text-[#d4a574]" /></div>
                                        )}
                                        <div className="flex-1">
                                            <p className="text-white font-medium text-sm">{r.user_name || 'User'}</p>
                                            <div className="flex items-center gap-2">
                                                <StarRating rating={r.rating} size={12} />
                                                <span className="text-[#64748b] text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-[#cbd5e1] text-sm leading-relaxed">{r.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

const WelcomePage = () => {
    const navigate = useNavigate();
    const { t, changeLang, lang } = useLanguage();
    const { user, loading, signInWithGoogle, signOut, sessionExpired, setSessionExpired } = useAuth();
    const [searchParams] = useSearchParams();
    
    // Only show pending message if user has a pending upgrade AND has no plan
    const pendingData = safeStorage.getItem('pendingUpgrade');
    let hasPendingUpgrade = false;
    
    if (pendingData && user && user.subscription_plan === 'none') {
        try {
            const parsed = JSON.parse(pendingData);
            // Only show if pending was set within last 24 hours
            const pendingTime = new Date(parsed.timestamp);
            const now = new Date();
            const hoursDiff = (now - pendingTime) / (1000 * 60 * 60);
            hasPendingUpgrade = hoursDiff < 24;
            
            // Clear if expired
            if (hoursDiff >= 24) {
                safeStorage.removeItem('pendingUpgrade');
            }
        } catch (e) {
            safeStorage.removeItem('pendingUpgrade');
        }
    }
    
    // Clear pending status if user now has a plan (subscription was activated)
    useEffect(() => {
        if (pendingData && user && user.subscription_plan !== 'none') {
            safeStorage.removeItem('pendingUpgrade');
        }
    }, [user, pendingData]);
    
    // Auto-redirect logged-in users with active plans to chat
    useEffect(() => {
        if (!loading && user && user.subscription_plan && user.subscription_plan !== 'none') {
            navigate('/chat');
        }
    }, [user, loading, navigate]);
    
    const isPendingUpgrade = (searchParams.get('pending') === 'true' && user?.subscription_plan === 'none') || hasPendingUpgrade;

    const languages = [
        { code: 'fr', label: 'Français', flag: '🇫🇷' },
        { code: 'ar', label: 'العربية', flag: '🇸🇦' },
        { code: 'en', label: 'English', flag: '🇺🇸' }
    ];

    const features = [
        {
            icon: <MessageCircle size={32} />,
            title: '',
            desc: 'كل سؤال هو مفتاح يُفتح به بابٌ إلى الذات'
        },
        {
            icon: <Users size={32} />,
            title: '',
            desc: 'هنا، نغوص في عمق المعنى لنستخرج لآلئ البصيرة'
        },
        {
            icon: <Sparkles size={32} />,
            title: '',
            desc: 'السكينة الحقيقية لا تكمن في الإجابات، بل في تناغم الروح مع الحقيقة'
        }
    ];

    const wisdomQuotes = [
        {
            text: 'طريق معرفة الإله بنفي الأنا و الذات و خلع الألقاب و الصفات و العيش وفقاً لإرادة الإله الواحدة لا وفقاً لرغبات الفرد المتعددة',
            source: 'الحقائق التوحيدية الكبرى'
        },
        {
            text: 'كل من تَلَطّفت أفكاره و مشاعره و اعتقاداته و أصبحت إلهية فقد خلص من روابط الأنا و بدأ رحلة الصعود إلى قمة معرفة باريه',
            source: 'نظرة تجاوزية في أعماق الوعي'
        },
        {
            text: 'ما أطرحه ليس فلسفة فكرية إنما علوم وعي نابعة من تجربتي العميقة مع كبار الحكماء',
            source: 'د نواف الشبلي'
        }
    ];

    const handleStartChat = () => {
        if (user) {
            // Check if user has an active subscription
            if (user.subscription_plan && user.subscription_plan !== 'none') {
                navigate('/chat');
            } else {
                // Redirect to pricing page if no subscription
                navigate('/pricing');
            }
        } else {
            signInWithGoogle();
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#0f172a] to-[#1e293b] flex flex-col relative">
            {/* Header Bar */}
            <header className="bg-[#0f172a]/90 backdrop-blur-sm" dir="rtl">
                {/* Main Navbar - Login info, buttons, questions */}
                <div className="px-3 py-2 border-b border-[#334155]">
                    <div className="max-w-6xl mx-auto flex justify-between items-center">
                        {/* Logo/Brand */}
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-[#d4a574]/60">
                                <img src={DR_NAWWAF_IMG} alt="د. نواف" className="w-full h-full object-cover" />
                            </div>
                            <span className="text-[#d4a574] font-bold text-[11px] sm:text-xs">اسأل د. نواف</span>
                        </div>

                        {/* User Section */}
                        {user ? (
                            <div className="flex items-center gap-1.5 sm:gap-2">
                                {/* Questions Count */}
                                <div className="flex items-center gap-1 bg-[#1e293b] rounded-full px-2 py-1">
                                    <span className="text-[8px] sm:text-[10px] text-[#94a3b8]">الأسئلة:</span>
                                    <span className="text-[8px] sm:text-[10px] text-[#d4a574] font-bold">
                                        {user.questions_limit === -1 ? '∞' : (user.questions_limit || 0) - (user.questions_used || 0)}
                                    </span>
                                </div>
                                
                                {/* User Profile */}
                                <div className="flex items-center gap-1 bg-[#1e293b] rounded-full px-1.5 sm:px-2 py-1">
                                    {user.picture && (
                                        <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                                    )}
                                    <span className="text-[8px] sm:text-[10px] text-[#94a3b8] hidden sm:inline">{user.name?.split(' ')[0]}</span>
                                </div>
                                
                                {/* Sign Out */}
                                <button 
                                    onClick={signOut}
                                    className="flex items-center gap-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 px-2 py-1 rounded-full text-[8px] sm:text-[10px] font-medium transition-colors"
                                    data-testid="welcome-signout-btn"
                                >
                                    <LogOut size={12} />
                                    <span>{lang === 'ar' ? 'تسجيل خروج' : 'Sign Out'}</span>
                                </button>
                            </div>
                        ) : (
                            <button 
                                onClick={signInWithGoogle}
                                className="flex items-center gap-1.5 bg-gradient-to-r from-[#d4a574] to-[#b8956a] text-[#0f172a] px-3 py-1.5 rounded-full text-[10px] font-bold hover:from-[#e2b687] hover:to-[#c9a67b] transition-all"
                            >
                                <span>تسجيل الدخول</span>
                            </button>
                        )}
                    </div>
                </div>
            </header>
            
            {/* Session Expired Banner — show when user's session expired */}
            {sessionExpired && !user && (
                <div className="flex justify-center z-30 mt-2 px-3" data-testid="session-expired-banner">
                    <div className="bg-amber-500/15 border border-amber-500/40 rounded-xl px-4 py-3 max-w-md w-full text-center backdrop-blur-sm">
                        <p className="text-amber-300 text-sm font-medium mb-2">
                            {lang === 'ar' ? 'انتهت جلستك. يرجى تسجيل الدخول مرة أخرى للمتابعة.' : 'Your session has expired. Please sign in again to continue.'}
                        </p>
                        <button 
                            onClick={() => { setSessionExpired(false); signInWithGoogle(); }}
                            className="bg-gradient-to-r from-[#d4a574] to-[#b8956a] text-[#0f172a] px-4 py-1.5 rounded-full text-xs font-bold hover:from-[#e2b687] hover:to-[#c9a67b] transition-all"
                            data-testid="session-expired-signin-btn"
                        >
                            {lang === 'ar' ? 'تسجيل الدخول مرة أخرى' : 'Sign In Again'}
                        </button>
                    </div>
                </div>
            )}

            {/* Company Info Banner - Floating framed text */}
            <div className="flex justify-center z-20 mt-1 px-2 sm:px-0" dir="rtl">
                <div className="relative inline-block">
                    {/* Animated gold border frame */}
                    <div className="absolute -inset-[2px] rounded-xl overflow-hidden">
                        <div className="absolute inset-0 animate-[spin_6s_linear_infinite] bg-[conic-gradient(from_0deg,#d4a574,#f0c987,#fff8e7,#e8b878,#c9956a,#8b6914,#d4a574)]"></div>
                        <div className="absolute inset-[2px] rounded-xl bg-[#0f172a]"></div>
                    </div>
                    {/* Shimmer sweep */}
                    <div className="absolute -inset-[2px] rounded-xl overflow-hidden pointer-events-none">
                        <div className="absolute inset-0 -translate-x-full animate-[shimmer_2.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                    </div>
                    {/* Sparkle dots */}
                    <div className="absolute -inset-1 pointer-events-none">
                        <div className="absolute -top-0.5 left-1/4 w-1 h-1 bg-[#f0c987] rounded-full animate-[sparkle_2s_ease-in-out_infinite]"></div>
                        <div className="absolute -top-0.5 right-1/4 w-1 h-1 bg-[#d4a574] rounded-full animate-[sparkle_2s_ease-in-out_infinite_0.7s]"></div>
                        <div className="absolute -bottom-0.5 left-1/3 w-1 h-1 bg-[#f0c987] rounded-full animate-[sparkle_2s_ease-in-out_infinite_1.4s]"></div>
                        <div className="absolute -bottom-0.5 right-1/3 w-1 h-1 bg-[#d4a574] rounded-full animate-[sparkle_2s_ease-in-out_infinite_0.3s]"></div>
                        <div className="absolute top-1/2 -left-1 w-1 h-1 bg-[#fff8e7] rounded-full animate-[sparkle_2s_ease-in-out_infinite_1s]"></div>
                        <div className="absolute top-1/2 -right-1 w-1 h-1 bg-[#fff8e7] rounded-full animate-[sparkle_2s_ease-in-out_infinite_1.8s]"></div>
                    </div>
                    {/* Content */}
                    <div className="relative z-10 px-3 sm:px-8 py-1.5 sm:py-2.5">
                        <div className="flex justify-center items-center whitespace-nowrap gap-x-1 sm:gap-x-3 text-[6px] sm:text-xs">
                            <span className="text-[#d4a574] font-light" style={{fontFamily: 'serif'}}>أنا لستُ الطريق، بل مساحةٌ آمنة... لتتذكر فيها أن الطريق هو أنت</span>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Custom animations */}
            <style>{`
                @keyframes shimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                @keyframes sparkle {
                    0%, 100% { opacity: 0.3; transform: scale(0.6); }
                    50% { opacity: 1; transform: scale(1.4); }
                }
            `}</style>

            {/* Pending Upgrade Message */}
            {isPendingUpgrade && (
                <div className="bg-gradient-to-r from-[#1e3a5f] to-[#1e40af] py-8 px-6 relative">
                    <button 
                        onClick={() => {
                            safeStorage.removeItem('pendingUpgrade');
                            window.location.href = '/';
                        }}
                        className="absolute top-4 right-4 text-[#d4a574]/70 hover:text-[#d4a574]"
                        title="Dismiss"
                    >
                        <X size={24} />
                    </button>
                    <div className="max-w-2xl mx-auto text-center" dir="rtl">
                        <div className="bg-[#0f172a]/40 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#d4a574]/30">
                            <div className="w-20 h-20 bg-gradient-to-br from-[#d4a574] to-[#b8956a] rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-3xl text-[#0f172a] font-bold">ن</span>
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-[#d4a574] mb-4">
                                أهلاً بك
                            </h2>
                            <p className="text-lg md:text-xl text-white/90 font-medium mb-6">
                                يُرجى إرسال وصل الدفع إلى رقم الوتس-آب لتفعيل باقة الإشتراك
                            </p>
                            
                            <p className="text-[#d4a574] text-lg font-bold mb-3" dir="ltr">
                                WhatsApp: +963988242654
                            </p>
                            
                            <a 
                                href="https://wa.me/963988242654" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-bold rounded-full hover:from-[#2EE077] hover:to-[#25D366] transition-all shadow-lg shadow-[#25D366]/30"
                            >
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                WhatsApp
                            </a>
                            
                            <p className="text-[#64748b] text-sm mt-6">
                                في انتظار التفعيل ⏳
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-[#1e3a5f] via-[#1e40af] to-[#1e3a5f] py-16 px-6 overflow-hidden">
                {/* Decorative Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-0 left-1/4 w-[1px] h-full bg-gradient-to-b from-transparent via-[#d4a574]/20 to-transparent"></div>
                    <div className="absolute top-0 right-1/4 w-[1px] h-full bg-gradient-to-b from-transparent via-[#d4a574]/20 to-transparent"></div>
                    <div className="absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#d4a574]/10 to-transparent"></div>
                </div>

                <div className="max-w-4xl mx-auto flex flex-col items-center gap-8 relative z-10 text-center">
                    {/* Profile Image */}
                    <div className="relative flex-shrink-0">
                        <div className="absolute inset-0 bg-[#d4a574]/20 rounded-full blur-2xl scale-150"></div>
                        <div className="relative">
                            <div className="w-52 h-52 md:w-64 md:h-64 lg:w-72 lg:h-72 rounded-full overflow-hidden border-4 border-[#d4a574]/60 shadow-2xl shadow-[#d4a574]/20">
                                <img 
                                    src={DR_NAWWAF_IMG} 
                                    alt="Dr. Nawwaf Al-Shibli" 
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            
                            {/* AI Badge */}
                            <div className="absolute -bottom-2 -right-2 w-14 h-14 bg-gradient-to-br from-[#d4a574] to-[#b8956a] rounded-full flex items-center justify-center shadow-lg border-2 border-[#1e3a5f]">
                                <span className="text-[#0f172a] text-sm font-bold">AI</span>
                            </div>
                        </div>
                    </div>

                    {/* Title */}
                    <div className="text-center">
                        <h1 className="text-3xl md:text-5xl font-bold text-white mb-2 tracking-wide">
                            {lang === 'ar' ? 'اسأل د. نواف الشبلي' : lang === 'fr' ? 'Demandez au Dr. Nawwaf' : 'Ask Dr. Nawwaf Al-Shibli'}
                        </h1>
                        <p className="text-[#d4a574] text-sm tracking-widest uppercase">
                            Powered by ChatGPT
                        </p>
                    </div>

                    {/* Subtitle */}
                    <div className="text-center max-w-2xl">
                        <p className="text-[#cbd5e1] text-lg leading-relaxed">
                            {lang === 'ar' ? 'مساحتك الآمنة للحوار الروحي' : lang === 'fr' ? 'Votre espace sûr pour le dialogue intellectuel et spirituel' : 'Your sanctuary for intellectual discourse, spiritual guidance, and divine wisdom from the teachings of Dr. Nawwaf Al-Shibli'}
                        </p>
                    </div>
                    
                    {/* CTA Button */}
                    {/* User Info or Sign In */}
                    {loading ? (
                        <div className="flex items-center gap-2 text-[#94a3b8]">
                            <Loader2 className="animate-spin" size={20} />
                        </div>
                    ) : user ? (
                        <div className="flex flex-col items-center gap-3 mb-4">
                            <button 
                                onClick={() => navigate('/pricing')}
                                className="flex items-center gap-3 bg-gradient-to-r from-[#7c3aed] to-[#5b21b6] rounded-full px-6 py-3 border border-[#8b5cf6] hover:from-[#8b5cf6] hover:to-[#6d28d9] transition-all shadow-lg shadow-[#7c3aed]/30"
                            >
                                <span className="text-white font-bold">{lang === 'ar' ? 'عرض خطط الاشتراك' : 'View Subscription Plans'}</span>
                                <span className="text-[#d4a574]">✨</span>
                            </button>
                        </div>
                    ) : null}

                    <button 
                        onClick={handleStartChat}
                        disabled={loading}
                        className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#d4a574] to-[#b8956a] text-[#0f172a] rounded-full text-lg font-bold hover:from-[#e2b687] hover:to-[#c9a67b] transition-all shadow-xl shadow-[#d4a574]/30 disabled:opacity-50"
                        style={{animation: 'heartbeat 1.5s ease-in-out infinite'}}
                        data-testid="start-chat-btn"
                    >
                        {user ? (
                            user.subscription_plan && user.subscription_plan !== 'none' ? (
                                <>
                                    <MessageCircle size={24} />
                                    <span>{lang === 'ar' ? 'ابدأ المحادثة الآن' : lang === 'fr' ? 'Commencer la conversation' : 'Start Conversation'}</span>
                                </>
                            ) : (
                                <>
                                    <span>✨</span>
                                    <span>{lang === 'ar' ? 'اشترك للبدء' : lang === 'fr' ? 'Abonnez-vous pour commencer' : 'Subscribe to Start'}</span>
                                </>
                            )
                        ) : (
                            <>
                                <svg className="w-6 h-6" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                <span>{lang === 'ar' ? 'سجّل دخولك للبدء' : lang === 'fr' ? 'Connectez-vous pour commencer' : 'Sign in to Start'}</span>
                            </>
                        )}
                    </button>

                    {/* Language Selector */}
                    <div className="flex justify-center items-center gap-3 mt-2 flex-wrap">
                        {languages.map((l) => (
                            <button
                                key={l.code}
                                onClick={() => changeLang(l.code)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2
                                    ${lang === l.code 
                                        ? 'bg-[#d4a574] text-[#0f172a] shadow-md' 
                                        : 'bg-[#1e293b] text-[#94a3b8] hover:bg-[#334155] border border-[#334155]'
                                    }`}
                            >
                                <span>{l.flag}</span>
                                <span>{l.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* ElevenLabs Voice Agent */}
            <div className="fixed bottom-6 right-6 z-50">
                <elevenlabs-convai agent-id="agent_4201kgh3k8whfnas7dtj65wwf6bc"></elevenlabs-convai>
            </div>

            {/* Features Section */}
            <section className="bg-[#0f172a] py-16 px-6">
                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
                    {features.map((feature, idx) => (
                        <div key={idx} className="bg-gradient-to-b from-[#1e293b] to-[#0f172a] rounded-2xl p-8 text-center border border-[#334155] hover:border-[#d4a574]/40 transition-all group">
                            <div className="w-16 h-16 bg-gradient-to-br from-[#1e40af] to-[#1e3a5f] rounded-2xl flex items-center justify-center mb-6 mx-auto text-[#d4a574] group-hover:from-[#d4a574]/20 group-hover:to-[#d4a574]/10 transition-all">
                                {feature.icon}
                            </div>
                            <p className="text-[#e2e8f0] text-base leading-relaxed text-center font-arabic" dir="rtl">{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Wisdom Quotes Section */}
            <section className="bg-gradient-to-b from-[#0f172a] to-[#1e293b] py-16 px-6">
                <h2 className="text-2xl font-bold text-center text-[#d4a574] mb-10 font-arabic tracking-wide">د. نواف الشبلي</h2>
                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
                    {wisdomQuotes.map((quote, idx) => (
                        <div key={idx} className="bg-[#1e293b]/50 backdrop-blur-sm rounded-2xl p-8 text-center border border-[#334155] hover:border-[#d4a574]/30 transition-all">
                            <div className="text-5xl mb-4 text-[#d4a574]/40 font-serif">"</div>
                            <p className="text-[#e2e8f0] text-sm mb-6 leading-relaxed text-center font-arabic" dir="rtl">{quote.text}</p>
                            <div className="flex items-center justify-center">
                                <span className="text-[#d4a574] font-semibold text-xs font-arabic">{quote.source}</span>
                            </div>
                        </div>
                    ))}
                </div>
                
                {/* Dots indicator */}
                <div className="flex justify-center gap-2 mt-8">
                    <div className="w-2 h-2 rounded-full bg-[#d4a574]"></div>
                    <div className="w-2 h-2 rounded-full bg-[#334155]"></div>
                    <div className="w-2 h-2 rounded-full bg-[#334155]"></div>
                </div>
            </section>

            {/* Reviews Section */}
            <ReviewsSection user={user} />

            {/* Footer */}
            <footer className="bg-[#0f172a] border-t border-[#334155] py-10 px-6">
                <div className="max-w-5xl mx-auto">
                    {/* Contact WhatsApp Button */}
                    <div className="text-center mb-8" dir="rtl">
                        <p className="text-white/90 text-base font-medium mb-1">للتواصل والاستفسار</p>
                        <p className="text-[#94a3b8] text-sm mb-1">مدير قسم البرمجة والذكاء الاصطناعي</p>
                        <p className="text-[#d4a574] text-sm mb-4">كنان واكد</p>
                        <a 
                            href="https://wa.me/963988242654" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white px-6 py-3.5 rounded-2xl hover:from-[#2EE077] hover:to-[#25D366] transition-all shadow-lg shadow-[#25D366]/25 group"
                            data-testid="footer-whatsapp-btn"
                        >
                            <svg className="w-6 h-6 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            <span className="font-bold text-lg" dir="ltr">+963988242654</span>
                        </a>
                    </div>

                    {/* Company Info */}
                    <div className="text-center mb-8">
                        <div className="bg-gradient-to-br from-[#1e3a5f]/50 to-[#0f172a] rounded-2xl p-6 border border-[#d4a574]/20 max-w-lg mx-auto">
                            <p className="text-[#d4a574] text-lg font-light" style={{fontFamily: 'serif'}}>أنا لستُ الطريق، بل مساحةٌ آمنة... لتتذكر فيها أن الطريق هو أنت</p>
                        </div>
                    </div>
                    
                    {/* Legal Links */}
                    <div className="flex flex-wrap justify-center gap-4 mb-6 text-sm">
                        <button 
                            onClick={() => navigate('/about')}
                            className="text-[#94a3b8] hover:text-[#d4a574] transition-colors"
                            data-testid="footer-about-link"
                        >
                            {lang === 'ar' ? 'من نحن' : 'About Us'}
                        </button>
                        <span className="text-[#334155]">•</span>
                        <button 
                            onClick={() => navigate('/terms')}
                            className="text-[#94a3b8] hover:text-[#d4a574] transition-colors"
                            data-testid="footer-terms-link"
                        >
                            {lang === 'ar' ? 'شروط الخدمة' : 'Terms of Service'}
                        </button>
                        <span className="text-[#334155]">•</span>
                        <button 
                            onClick={() => navigate('/privacy')}
                            className="text-[#94a3b8] hover:text-[#d4a574] transition-colors"
                            data-testid="footer-privacy-link"
                        >
                            {lang === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
                        </button>
                        <span className="text-[#334155]">•</span>
                        <button 
                            onClick={() => navigate('/disclaimer')}
                            className="text-[#94a3b8] hover:text-[#d4a574] transition-colors"
                            data-testid="footer-disclaimer-link"
                        >
                            {lang === 'ar' ? 'إخلاء المسؤولية' : 'Disclaimer'}
                        </button>
                    </div>
                    
                    {/* Secure Payment Badge */}
                    <div className="flex justify-center items-center gap-2 mb-6">
                        <Lock size={14} className="text-[#22c55e]" />
                        <span className="text-[#64748b] text-xs">
                            {lang === 'ar' ? 'دفع آمن ومشفر عبر Stripe' : 'Secure & Encrypted Payment via Stripe'}
                        </span>
                    </div>
                    
                    {/* Social Links */}
                    <div className="flex justify-center gap-4">
                        <a href="https://www.facebook.com/share/17oiHWwSeB/" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-[#1e293b] rounded-full flex items-center justify-center text-[#94a3b8] hover:bg-[#334155] hover:text-[#d4a574] transition-all border border-[#334155]">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        </a>
                        <a href="https://www.instagram.com/nawwafalshibli?igsh=anJqdDhuejRlbnJl" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-[#1e293b] rounded-full flex items-center justify-center text-[#94a3b8] hover:bg-[#334155] hover:text-[#d4a574] transition-all border border-[#334155]">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

const MainChat = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(safeStorage.getItem("chat_session_id"));
    const [sessions, setSessions] = useState([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isAdminOpen, setIsAdminOpen] = useState(false);
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const [pendingFeedback, setPendingFeedback] = useState(null); // { type, aiContent, userQuery }
    const [limitModal, setLimitModal] = useState({ open: false, message: '' });
    const [isRecording, setIsRecording] = useState(false);
    const [recordingStatus, setRecordingStatus] = useState(''); // '', 'recording', 'processing'
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    
    const messagesEndRef = useRef(null);
    const navigate = useNavigate();
    const { t, lang } = useLanguage();
    const { user, loading: authLoading, signInWithGoogle, signOut, refreshUser } = useAuth();
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [exportingChats, setExportingChats] = useState(false);

    // Redirect to home if not authenticated or no subscription
    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/');
        } else if (!authLoading && user && (!user.subscription_plan || user.subscription_plan === 'none')) {
            navigate('/pricing');
        }
    }, [user, authLoading, navigate]);

    // Show onboarding for users who haven't set up their profile yet
    useEffect(() => {
        if (user && !user.has_profile && !safeStorage.getItem('profile_onboarding_dismissed')) {
            setShowOnboarding(true);
        }
    }, [user]);

    // Refresh user data on chat mount to get fresh question counts
    useEffect(() => {
        if (user) refreshUser();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Generate or get unique device ID for privacy
    const getDeviceId = () => {
        let deviceId = safeStorage.getItem("device_id");
        if (!deviceId) {
            deviceId = 'dev_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
            safeStorage.setItem("device_id", deviceId);
        }
        return deviceId;
    };
    
    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };
  
    useEffect(() => {
      scrollToBottom();
    }, [messages]);
  
    // Load Sessions List - only for this device
    const fetchSessions = async () => {
        try {
            const deviceId = getDeviceId();
            // Pass device_id to claim orphaned chats, but backend returns ALL user's chats
            const res = await axios.get(`${API_URL}/sessions?device_id=${deviceId}`, { withCredentials: true });
            setSessions(res.data);
        } catch (e) {
            console.error("Fetch sessions error", e);
        }
    };
  
    useEffect(() => {
        fetchSessions();
    }, [sessionId]); 
  
    // Load Chat History
    useEffect(() => {
      if (sessionId) {
        loadHistory(sessionId);
      } else {
          setMessages([
              {
                  role: 'assistant',
                  content: lang === 'ar' 
                    ? 'أهلاً بك يا بني. أنا الدكتور نواف الشبلي، هنا لأستمع إليك وأشاركك رحلة التفكّر والسكينة. ما الذي يشغل بالك اليوم؟'
                    : (lang === 'fr' 
                        ? "Bienvenue. Je suis le Dr Nawwaf Al-Shibli, ici pour vous écouter et partager un voyage de réflexion et de sérénité. Qu'est-ce qui vous préoccupe aujourd'hui ?"
                        : "Welcome. I am Dr. Nawwaf Al-Shibli, here to listen to you and share a journey of reflection and serenity. What is on your mind today?"
                      )
              }
          ]);
      }
    }, [sessionId]);

    const loadHistory = async (id) => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/chat/${id}`);
        if (res.data && res.data.length > 0) {
          setMessages(res.data);
        } else {
           setMessages([
              { role: 'assistant', content: t('welcomeDesc') } // Fallback
          ]);
        }
      } catch (err) {
        console.error("Failed to load history", err);
        // Fallback
        safeStorage.removeItem("chat_session_id");
        setSessionId(null);
      } finally {
          setLoading(false);
      }
    };
  
    const handleSend = async (e) => {
      e.preventDefault();
      if (!input.trim() || loading) return;
      if (!user) {
          signInWithGoogle();
          return;
      }
  
      const userMsg = input.trim();
      setInput("");
      setMessages([{ role: "user", content: userMsg }]);
      setLoading(true);
  
      // Helper: try to fetch the latest answer from chat history
      const tryFetchFromHistory = async () => {
          try {
              const sessionsRes = await axios.get(`${API_URL}/chat/sessions`, { withCredentials: true, timeout: 10000 });
              const latest = (sessionsRes.data || [])[0];
              if (latest) {
                  const chatRes = await axios.get(`${API_URL}/chat/${latest.id}`, { withCredentials: true, timeout: 10000 });
                  const msgs = chatRes.data || [];
                  if (msgs.length >= 2 && msgs[msgs.length - 1].role === 'assistant') {
                      setSessionId(latest.id);
                      safeStorage.setItem("chat_session_id", latest.id);
                      setMessages(msgs);
                      fetchSessions();
                      await refreshUser();
                      return true;
                  }
              }
          } catch (e) { console.warn("History fallback failed", e); }
          return false;
      };

      try {
        const deviceId = getDeviceId();
        // Step 1: Start async chat job
        const res = await axios.post(`${API_URL}/chat`, {
          message: userMsg,
          session_id: null,
          device_id: deviceId
        }, { withCredentials: true, timeout: 15000 });
  
        const { job_id, session_id: newSessionId } = res.data;
        setSessionId(newSessionId);
        safeStorage.setItem("chat_session_id", newSessionId);

        // Step 2: Poll for result — 2s intervals, up to 4 minutes
        let attempts = 0;
        let consecutiveErrors = 0;
        while (attempts < 120) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          try {
            const statusRes = await axios.get(`${API_URL}/chat/job/${job_id}`, {
              withCredentials: true, timeout: 10000
            });
            consecutiveErrors = 0; // reset on success

            if (statusRes.data.status === 'completed') {
              setMessages([
                { role: "user", content: userMsg },
                { role: "assistant", content: statusRes.data.response }
              ]);
              fetchSessions();
              await refreshUser();
              return;
            } else if (statusRes.data.status === 'failed') {
              // Job failed — try history fallback before showing error
              if (await tryFetchFromHistory()) return;
              throw new Error(statusRes.data.error || 'Generation failed');
            }
          } catch (pollErr) {
            if (pollErr.response?.status === 404) {
              // Job not found — maybe TTL expired, try history
              if (await tryFetchFromHistory()) return;
              consecutiveErrors++;
            } else {
              consecutiveErrors++;
            }
            // After 5 consecutive poll errors, try history fallback
            if (consecutiveErrors >= 5) {
              await new Promise(r => setTimeout(r, 3000));
              if (await tryFetchFromHistory()) return;
            }
            // After 10 consecutive errors, give up
            if (consecutiveErrors >= 10) break;
          }
          attempts++;
        }
        // Polling exhausted — final history fallback
        await new Promise(r => setTimeout(r, 5000));
        if (await tryFetchFromHistory()) return;
        throw new Error('Timeout');
      } catch (error) {
        console.error("Chat error:", error);
        
        if (error.response?.status === 401) {
            const detail = error.response.data?.detail;
            if (detail?.error === 'authentication_required') {
                setLimitModal({
                    open: true,
                    type: 'auth',
                    message: lang === 'ar' ? detail.message_ar : detail.message_en
                });
                setMessages(messages);
            }
        } else if (error.response?.status === 403) {
            const detail = error.response.data?.detail;
            if (detail?.error === 'limit_reached' || detail?.error === 'device_ip_limit_reached') {
                setLimitModal({
                    open: true,
                    type: detail?.error === 'device_ip_limit_reached' ? 'abuse' : 'limit',
                    message: detail.message,
                    questions_used: detail.questions_used,
                    questions_limit: detail.questions_limit,
                    subscription_plan: detail.subscription_plan
                });
                setMessages(messages);
            }
        } else {
            // Last resort — try history one more time
            if (!(await tryFetchFromHistory())) {
                setMessages(prev => [...prev, { 
                  role: "assistant", 
                  content: t('error') 
                }]);
            }
        }
      } finally {
        setLoading(false);
      }
    };

    // Voice Recording Functions
    const startRecording = async () => {
        if (!user) {
            signInWithGoogle();
            return;
        }
        
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];
            
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            
            mediaRecorder.onstop = async () => {
                setRecordingStatus('processing');
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                
                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
                
                // Send to backend for transcription
                try {
                    const formData = new FormData();
                    formData.append('audio', audioBlob, 'recording.webm');
                    
                    const response = await axios.post(`${API_URL}/transcribe`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        withCredentials: true
                    });
                    
                    if (response.data.text) {
                        setInput(response.data.text);
                    }
                } catch (error) {
                    console.error('Transcription error:', error);
                    alert(lang === 'ar' ? 'فشل تحويل الصوت إلى نص' : 'Failed to transcribe audio');
                } finally {
                    setRecordingStatus('');
                    setIsRecording(false);
                }
            };
            
            mediaRecorder.start();
            setIsRecording(true);
            setRecordingStatus('recording');
        } catch (error) {
            console.error('Microphone access error:', error);
            alert(lang === 'ar' ? 'لا يمكن الوصول إلى الميكروفون' : 'Cannot access microphone');
        }
    };
    
    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
    };
    
    const startNewChat = () => {
        safeStorage.removeItem("chat_session_id");
        setSessionId(null);
        setMessages([]);
    };

    const handleExportChats = async () => {
        if (exportingChats) return;
        if (!sessions || sessions.length === 0) {
            alert(lang === 'ar' ? 'لا توجد محادثات لتصديرها' : 'No conversations to export');
            return;
        }
        setExportingChats(true);
        try {
            const response = await axios.get(`${API_URL}/chat/export-all-docx`, {
                responseType: 'blob',
                withCredentials: true,
                timeout: 120000
            });
            // Check if response is actually an error (JSON instead of DOCX)
            if (response.data.type && response.data.type.includes('json')) {
                const text = await response.data.text();
                const err = JSON.parse(text);
                alert(err.detail || (lang === 'ar' ? 'فشل تصدير المحادثات' : 'Failed to export chats'));
                return;
            }
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const disposition = response.headers['content-disposition'];
            let filename = 'Dr_Nawwaf_Chats.docx';
            if (disposition) {
                const match = disposition.match(/filename\*?=(?:UTF-8'')?([^;\s]+)/);
                if (match) filename = decodeURIComponent(match[1]);
            }
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            if (error.response && error.response.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const err = JSON.parse(text);
                    if (err.detail === 'No conversations found') {
                        alert(lang === 'ar' ? 'لا توجد محادثات لتصديرها' : 'No conversations to export');
                    } else {
                        alert(err.detail || (lang === 'ar' ? 'فشل تصدير المحادثات' : 'Failed to export chats'));
                    }
                } catch {
                    alert(lang === 'ar' ? 'فشل تصدير المحادثات' : 'Failed to export chats');
                }
            } else {
                alert(lang === 'ar' ? 'فشل تصدير المحادثات' : 'Failed to export chats');
            }
        } finally {
            setExportingChats(false);
        }
    };
  
    // Chat deletion is permanently disabled - users' history is sacred
    // deleteSession function removed to prevent any accidental data loss

    const handleRateMessage = (type, aiContent, userQuery) => {
        setPendingFeedback({ type, aiContent, userQuery });
        // Only open modal if negative feedback, or just submit if positive?
        // Let's open modal for both to get optional text
        setFeedbackModalOpen(true);
    };

    const submitFeedback = async (comment) => {
        try {
            await axios.post(`${API_URL}/feedback`, {
                session_id: sessionId,
                user_message: pendingFeedback.userQuery || "Previous context",
                ai_response: pendingFeedback.aiContent,
                rating: pendingFeedback.type === 'up' ? 1 : -1,
                comment: comment
            });
        } catch (e) {
            console.error("Feedback error", e);
        } finally {
            setFeedbackModalOpen(false);
            setPendingFeedback(null);
        }
    };

    return (
        <div className="flex h-screen bg-[#0f172a] text-white overflow-hidden">
          
          {/* Sidebar Overlay for Mobile */}
          {isSidebarOpen && (
            <div 
                className="fixed inset-0 bg-black/60 z-30 md:hidden backdrop-blur-sm"
                onClick={() => setIsSidebarOpen(false)}
            />
          )}
    
          {/* Sidebar */}
          <Sidebar 
            isOpen={isSidebarOpen} 
            onClose={() => setIsSidebarOpen(false)}
            sessions={sessions}
            currentSessionId={sessionId}
            onSelectSession={(id) => {
                setSessionId(id);
                safeStorage.setItem("chat_session_id", id);
            }}
            onExportChats={handleExportChats}
            exportingChats={exportingChats}
          />
    
          {/* Main Content */}
          <div className="flex-1 flex flex-col h-full min-w-0">
            
            {/* Header */}
            <header className="flex-none bg-gradient-to-r from-[#1e3a5f] via-[#1e40af] to-[#1e3a5f] border-b border-[#334155] px-3 py-2 z-20">
                <div className="flex items-center justify-between gap-2">
                    {/* Left: Menu + Avatar + Title */}
                    <div className="flex items-center gap-2 min-w-0">
                        <button 
                            onClick={() => setIsSidebarOpen(true)}
                            className="md:hidden p-1.5 text-[#94a3b8] hover:bg-white/10 rounded-lg shrink-0"
                        >
                            <Menu size={20} />
                        </button>
                        <button onClick={() => navigate('/')} className="w-9 h-9 rounded-full overflow-hidden border-2 border-[#d4a574]/60 shrink-0 cursor-pointer hover:opacity-80 transition-opacity">
                            <img src={DR_NAWWAF_IMG} alt="Dr. Nawwaf" className="w-full h-full object-cover" />
                        </button>
                        <div className="min-w-0 hidden sm:block">
                            <h1 className="text-sm font-bold text-white leading-tight truncate">{t('welcomeTitle')}</h1>
                        </div>
                    </div>
                    
                    {/* Right: Questions + Profile + Logout + Back */}
                    <div className="flex items-center gap-1.5 shrink-0">
                        {/* Questions Counter */}
                        {user && user.questions_limit > 0 && (
                            <div className="flex items-center gap-1 bg-[#0f172a]/70 rounded-full px-2.5 py-1 border border-[#d4a574]/30">
                                <span className="text-[#d4a574] text-xs">💬</span>
                                <span className="text-xs text-white font-bold">
                                    {Math.max(0, user.questions_limit - user.questions_used)}
                                </span>
                                <span className="text-[10px] text-[#94a3b8] hidden md:inline">
                                    {lang === 'ar' ? 'متبقي' : 'left'}
                                </span>
                            </div>
                        )}
                        
                        {/* Profile Button */}
                        {user && (
                            <button 
                                onClick={() => setShowProfileModal(true)}
                                className="flex items-center gap-1 bg-[#0f172a]/50 rounded-full px-2.5 py-1.5 text-[#d4a574] hover:text-[#e2b687] hover:bg-[#0f172a]/70 transition-all"
                                title={lang === 'ar' ? 'ملفك الشخصي' : 'Your Profile'}
                                data-testid="profile-btn"
                            >
                                <UserCircle size={16} />
                                <span className="text-xs hidden md:inline">{user.profile?.display_name || (lang === 'ar' ? 'ملفي' : 'Profile')}</span>
                            </button>
                        )}
                        
                        {/* Logout Button */}
                        {user && (
                            <button 
                                onClick={signOut}
                                className="flex items-center gap-1 bg-[#0f172a]/50 rounded-full px-2.5 py-1.5 text-[#f87171] hover:text-red-400 hover:bg-[#0f172a]/70 transition-all"
                                title={t('signOut') || 'Sign Out'}
                                data-testid="signout-btn"
                            >
                                <LogOut size={14} />
                            </button>
                        )}
                        
                        {/* Back Button */}
                        <button 
                            onClick={() => navigate('/')}
                            className="p-1.5 text-[#94a3b8] hover:text-white transition-colors bg-[#0f172a]/50 rounded-full hover:bg-[#0f172a]"
                            title="Back"
                        >
                            <ArrowLeft size={18} />
                        </button>
                    </div>
                </div>
            </header>
    
            {/* Chat Area */}
            <main className="flex-1 overflow-y-auto p-4 md:p-6 scroll-smooth bg-gradient-to-b from-[#f8fafc] to-[#f1f5f9]">
                <div className="max-w-3xl mx-auto space-y-6">
                {messages.map((msg, idx) => (
                    <ChatMessage 
                        key={idx} 
                        message={msg} 
                        onRate={handleRateMessage}
                        // Pass the previous user message if this is an AI response
                        userQuery={idx > 0 ? messages[idx-1].content : ""}
                    />
                ))}
                {loading && (
                    <div className="flex w-full mb-4 justify-center">
                        <div className="flex flex-col items-center gap-4 p-8">
                            {/* Flower of Life Sacred Geometry */}
                            <div className="relative w-32 h-32">
                                <svg viewBox="0 0 200 200" className="w-full h-full animate-[spin_20s_linear_infinite]">
                                    <circle cx="100" cy="100" r="30" fill="none" stroke="#d4a574" strokeWidth="0.8" opacity="0.9"/>
                                    {[0,60,120,180,240,300].map((angle, i) => (
                                        <circle key={i} cx={100 + 30 * Math.cos(angle * Math.PI / 180)} cy={100 + 30 * Math.sin(angle * Math.PI / 180)} r="30" fill="none" stroke="#d4a574" strokeWidth="0.8" opacity="0.8"/>
                                    ))}
                                    {[0,30,60,90,120,150,180,210,240,270,300,330].map((angle, i) => (
                                        <circle key={`o${i}`} cx={100 + 52 * Math.cos(angle * Math.PI / 180)} cy={100 + 52 * Math.sin(angle * Math.PI / 180)} r="30" fill="none" stroke="#d4a574" strokeWidth="0.5" opacity="0.5"/>
                                    ))}
                                    <circle cx="100" cy="100" r="82" fill="none" stroke="#d4a574" strokeWidth="0.5" opacity="0.3"/>
                                    <circle cx="100" cy="100" r="85" fill="none" stroke="#d4a574" strokeWidth="0.3" opacity="0.2"/>
                                </svg>
                                <div className="absolute inset-0 rounded-full bg-[#d4a574]/5 blur-xl animate-pulse"></div>
                            </div>
                            <p className="text-black text-xl font-light tracking-wide animate-[fadeInOut_2.5s_ease-in-out_infinite]" style={{fontFamily: 'serif'}}>
                                استحضر لك الجواب من روحي
                            </p>
                        </div>
                    </div>
                )}
                <style>{`
                    @keyframes fadeInOut {
                        0%, 100% { opacity: 0.15; }
                        50% { opacity: 1; }
                    }
                `}</style>
                <div ref={messagesEndRef} />
                </div>
            </main>
    
            {/* Input Area */}
            <footer className="flex-none bg-gradient-to-r from-[#1e3a5f] via-[#1e40af] to-[#1e3a5f] border-t border-[#334155] p-4 z-20">
                <div className="max-w-3xl mx-auto">
                <form onSubmit={handleSend} className="relative flex items-end gap-2 bg-white p-2 rounded-2xl shadow-lg">
                    <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend(e);
                        }
                    }}
                    placeholder={t('chatInputPlaceholder')}
                    className="flex-1 bg-transparent border-none focus:ring-0 resize-none max-h-32 min-h-[44px] py-3 px-2 text-base placeholder:text-[#94a3b8] text-[#1e293b]"
                    rows={1}
                    disabled={recordingStatus === 'processing'}
                    />
                    
                    {/* Voice Input Button */}
                    <button
                        type="button"
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={loading || recordingStatus === 'processing'}
                        className={`p-3 rounded-xl transition-all mb-[2px] ${
                            isRecording 
                                ? 'bg-red-500 text-white animate-pulse' 
                                : recordingStatus === 'processing'
                                    ? 'bg-[#94a3b8] text-white'
                                    : 'bg-[#1e293b] text-white hover:bg-[#334155]'
                        }`}
                        title={isRecording ? (lang === 'ar' ? 'إيقاف التسجيل' : 'Stop recording') : (lang === 'ar' ? 'تسجيل صوتي' : 'Voice input')}
                        data-testid="voice-input-btn"
                    >
                        {recordingStatus === 'processing' ? (
                            <Loader2 size={20} className="animate-spin" />
                        ) : (
                            <Mic size={20} />
                        )}
                    </button>
                    
                    <button
                    type="submit"
                    disabled={loading || !input.trim() || recordingStatus === 'processing'}
                    className="p-3 bg-gradient-to-r from-[#d4a574] to-[#b8956a] text-[#0f172a] rounded-xl hover:from-[#e2b687] hover:to-[#c9a67b] disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-[2px] shadow-md font-bold"
                    >
                    <Send size={20} className={loading ? "opacity-0" : "opacity-100"} />
                    {loading && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="animate-spin" size={20} /></div>}
                    </button>
                </form>
                
                {/* Recording indicator */}
                {isRecording && (
                    <div className="text-center text-xs text-red-400 mt-1 animate-pulse">
                        🔴 {lang === 'ar' ? 'جاري التسجيل... اضغط لإيقاف' : 'Recording... Click to stop'}
                    </div>
                )}
                <p className="text-center text-[10px] text-[#94a3b8] mt-2">
                    AI Dr. Nawwaf Al-Shibli — {lang === 'ar' ? 'كنان واكد' : 'Kinan Waked'}
                </p>
                </div>
            </footer>
    
            {/* Admin Modal */}
            {isAdminOpen && (
                <AdminPanel isModal={true} onClose={() => setIsAdminOpen(false)} />
            )}

            {/* Feedback Modal */}
            <FeedbackModal 
                isOpen={feedbackModalOpen} 
                onClose={() => setFeedbackModalOpen(false)} 
                onSubmit={submitFeedback} 
            />

            {/* Limit Reached Modal */}
            {limitModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl text-center">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                            limitModal.type === 'abuse' 
                                ? 'bg-gradient-to-br from-[#ef4444] to-[#dc2626]'
                                : 'bg-gradient-to-br from-[#fbbf24] to-[#f59e0b]'
                        }`}>
                            <Lock className="text-white" size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-stone-800 mb-2">
                            {limitModal.type === 'abuse'
                                ? (lang === 'ar' ? 'تم حظر الجهاز/العنوان' : 'Device/IP Blocked')
                                : limitModal.type === 'no_subscription'
                                    ? (lang === 'ar' ? 'يجب شراء اشتراك' : 'Subscription Required')
                                    : (lang === 'ar' ? 'انتهت أسئلتك' : 'Question Limit Reached')
                            }
                        </h3>
                        <p className="text-stone-500 mb-4" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                            {limitModal.message}
                        </p>
                        
                        {limitModal.type === 'limit' && (
                            <div className="bg-stone-100 rounded-xl p-4 mb-6">
                                <p className="text-sm text-stone-600">
                                    {lang === 'ar' ? 'الخطة الحالية:' : 'Current Plan:'} <span className="font-bold text-[#1e40af]">{limitModal.subscription_plan?.toUpperCase() || 'NONE'}</span>
                                </p>
                            </div>
                        )}

                        {limitModal.type === 'abuse' && (
                            <div className="bg-red-50 rounded-xl p-4 mb-6 border border-red-200">
                                <p className="text-sm text-red-600">
                                    {lang === 'ar' 
                                        ? 'تم اكتشاف استخدام متعدد من هذا الجهاز/العنوان. يرجى الترقية لمتابعة الاستخدام.'
                                        : 'Multiple account usage detected from this device/IP. Please upgrade to continue.'
                                    }
                                </p>
                            </div>
                        )}

                        <div className="space-y-3">
                            <button 
                                onClick={() => { setLimitModal({open: false}); navigate('/pricing'); }}
                                className="w-full py-3 bg-gradient-to-r from-[#d4a574] to-[#b8956a] text-[#0f172a] rounded-xl font-bold hover:from-[#e2b687] hover:to-[#c9a67b] transition-all"
                                data-testid="upgrade-btn"
                            >
                                {lang === 'ar' ? 'ترقية الاشتراك' : 'Upgrade Now'}
                            </button>
                            <button 
                                onClick={() => setLimitModal({open: false})}
                                className="w-full py-3 text-stone-500 hover:text-stone-700 text-sm"
                            >
                                {lang === 'ar' ? 'لاحقاً' : 'Maybe Later'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Profile Modal (edit) */}
            <ProfileModal 
                isOpen={showProfileModal} 
                onClose={() => setShowProfileModal(false)} 
                user={user} 
                onSave={() => refreshUser()}
            />

            {/* Profile Onboarding (first time) */}
            <ProfileModal 
                isOpen={showOnboarding} 
                onClose={() => { 
                    setShowOnboarding(false); 
                    safeStorage.setItem('profile_onboarding_dismissed', 'true'); 
                }} 
                user={user} 
                onSave={() => { 
                    refreshUser(); 
                    safeStorage.setItem('profile_onboarding_dismissed', 'true'); 
                }}
                isOnboarding={true}
            />
            
          </div>
        </div>
      );
}

function App() {
  return (
    <LanguageProvider>
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/adm-o1bmcpu6dcme3o9y-x9k" element={<AdminPanel isModal={false} />} />
                    <Route path="/chat" element={<MainChat />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/disclaimer" element={<DisclaimerPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/" element={<WelcomePage />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
